import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TableSortLabel,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import {
  mortalitiesUrl,
  mortEventsUrl,
  cullEventsUrl,
  updateMortalitiesUrl,
  removeMortalitiesUrl,
  mortDescriptionsUrl,
  cullDescriptionsUrl,
  placementWeightsUrl,
  updateWeightUrl,
  removePlacementWeightUrl,
  broilerWeightsUrl,
  updateBroilerWeightsUrl,
  removeBroilerWeightUrl,
  catchDataUrl,
  updateCatchDataUrl,
  removeCatchUrl,
  waterFeedDataUrl,
  waterDataUrl,
  feedScheduleUrl,
  broilerDataByFarmUrl,
  getFeedBalanceUrl,
  getFeedBinsUrl,
  utilitiesUrl,
  updateUtilitiesUrl,
  updateFeedWaterUrl,
  updateWaterDataUrl,
  removeFeedWaterUrl,
  removeFeedSpendUrl,
  removeWaterDataUrl,
  deviceDetailsUrl,
  deviceDetailsForDeviceUrl,
  actNotificationsUrl,
  updateActNotificationsUrl,
  kpiNotificationsUrl,
  updateKpiNotificationsUrl,
  updateDeviceUrl,
  environmentalAlertsTriggerUrl,
  kpiAlertsTriggerUrl,
  activityAlertsTriggerUrl,
} from "api/endpoints";
import {
  readOfflineRecords,
  upsertOfflineRecord,
  removeOfflineRecord,
  clearOfflineRecords,
  replaceOfflineRecords,
} from "utils/offlineRecords";
import SetupCycle from "./setupcycle";

function CaptureRecords({ facilityId, cycleId, farmId }) {
  const IMAGE_BLOB_SAS_TEMPLATE =
    "https://omniofiles.blob.core.windows.net/omniofarm/<image.png>?sv=2025-07-05&spr=https&st=2026-03-02T10%3A51%3A19Z&se=2026-06-03T20%3A00%3A00Z&sr=c&sp=racw&sig=1StoeOlMDcm7320jtp6zL1FswuoUff7WAqH3Mzw3%2B88%3D";
  const IMAGE_BLOB_READ_BASE_URL = "https://omniofiles.blob.core.windows.net/omniofarm";
  const IMAGE_BLOB_READ_SAS_TOKEN =
    "?sv=2025-07-05&spr=https&st=2026-03-02T10%3A56%3A29Z&se=2026-06-03T20%3A00%3A00Z&sr=c&sp=rlf&sig=hFR93REbOm%2BM0uV5g0DtuSgPPbo1vSIZcrkCVLjtrT8%3D";
  const MORTALITY_IMAGE_MAP_STORAGE_KEY = "mortalityImageBlobByRecordId";
  const ALLOWED_IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg"];
  const [mortalityImageBlobMap, setMortalityImageBlobMap] = useState(() => {
    try {
      const raw = localStorage.getItem(MORTALITY_IMAGE_MAP_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  });
  const [openDialog, setOpenDialog] = useState(null);
  const [mortRows, setMortRows] = useState([]);
  const [orderBy, setOrderBy] = useState("date"); // default sort by date
  const [order, setOrder] = useState("desc"); // newest first by default
  const pageSize = 20;
  const [page, setPage] = useState(1); // 1-based index
  const [mortActiveView, setMortActiveView] = useState("mortalities");
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    id: null,
    fatalities: "",
    culls: "",
    dateTime: "",
    eventMortalitiesID: "",
    eventCullsID: "",
  });
  const [mortalityImageFile, setMortalityImageFile] = useState(null);
  const mortalityUploadInputRef = useRef(null);
  const mortalityCameraInputRef = useRef(null);
  const [mortOptions, setMortOptions] = useState([]); // [{id,eventName}]
  const [cullOptions, setCullOptions] = useState([]); // [{id,eventName}]
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("Saved successfully");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [duplicateAddOpen, setDuplicateAddOpen] = useState(false);
  const [duplicateAddView, setDuplicateAddView] = useState(null);
  const [weightTab, setWeightTab] = useState("placement"); // placement | cycle | catch
  const [notificationTab, setNotificationTab] = useState("environmental");
  const [placementRows, setPlacementRows] = useState([]);
  const [cycleRows, setCycleRows] = useState([]);
  const [cycleDateOrder, setCycleDateOrder] = useState("desc");
  const [catchRows, setCatchRows] = useState([]);
  const [catchEditOpen, setCatchEditOpen] = useState(false);
  const [catchForm, setCatchForm] = useState({
    id: null,
    totalweight: "",
    noofbirds: "",
    date: "",
  });
  const [catchDeleteOpen, setCatchDeleteOpen] = useState(false);
  const [catchDeleteTarget, setCatchDeleteTarget] = useState(null);
  const [cycleEditOpen, setCycleEditOpen] = useState(false);
  const [cycleDeleteOpen, setCycleDeleteOpen] = useState(false);
  const [cycleDeleteTarget, setCycleDeleteTarget] = useState(null);
  const [cycleForm, setCycleForm] = useState({
    id: null,
    weightBack: "",
    weightBackR: "",
    weightCenter: "",
    weightCenterR: "",
    weightFront: "",
    weightFrontR: "",
    sampleSize: "",
    weightAverage: "",
    dateTime: "",
  });
  const [placementEditOpen, setPlacementEditOpen] = useState(false);
  const [placementDeleteOpen, setPlacementDeleteOpen] = useState(false);
  const [placementDeleteTarget, setPlacementDeleteTarget] = useState(null);
  const [placementForm, setPlacementForm] = useState({
    id: null,
    totalBirdWeight: "",
    averageBox: "",
    noOfBirdsPerBox: "",
    date: "",
  });

  // Consumption state (Feed/Water)
  const [consRows, setConsRows] = useState([]);
  const [consOrderBy, setConsOrderBy] = useState("date");
  const [consOrder, setConsOrder] = useState("desc");
  const consPageSize = 20;
  const [consPage, setConsPage] = useState(1);
  const [consEditOpen, setConsEditOpen] = useState(false);
  const [consDeleteOpen, setConsDeleteOpen] = useState(false);
  const [consDeleteTarget, setConsDeleteTarget] = useState(null);
  const [consForm, setConsForm] = useState({
    id: null,
    feedKg: "",
    waterReading: "",
    waterSource: "",
    utilityId: null,
    waterL: "",
    dateTime: "",
    bin: "bin1",
    stage: "",
    feedName: "",
    feedSpendId: null,
  });
  const [consFormMode, setConsFormMode] = useState("feed");
  const [consActiveView, setConsActiveView] = useState("readings");
  const [feedBins, setFeedBins] = useState({ bin1: null, bin2: null, feedpaper: null });
  const [feedBalance, setFeedBalance] = useState({
    bin1: null,
    bin2: null,
    bin1Consumption: null,
    bin2Consumption: null,
    bin1Total: null,
    bin2Total: null,
  });
  const [setupFeedRows, setSetupFeedRows] = useState([]);
  const [feedStageOptions, setFeedStageOptions] = useState([]);
  const [feedStageMap, setFeedStageMap] = useState({});
  const [feedNameOptions, setFeedNameOptions] = useState([]);
  const [feedNameMap, setFeedNameMap] = useState({});

  // Helper to pick first defined, non-empty value
  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && `${v}`.length > 0);
  const buildFeedOptions = (items, { valueKey = "id", labelKey = "description" } = {}) => {
    const list = [];
    const map = {};
    items.forEach((item) => {
      const rawValue = valueKey === "feedname" ? item.feedname ?? item.id : item.id;
      if (rawValue == null) return;
      const value = String(rawValue);
      if (map[value]) return;
      let rawLabel;
      switch (labelKey) {
        case "value":
          rawLabel = item.value ?? item.description ?? item.feedname ?? item.id;
          break;
        case "feedname":
          rawLabel = item.feedname ?? item.value ?? item.description ?? item.id;
          break;
        default:
          rawLabel = item.description ?? item.value ?? item.feedname ?? item.id;
          break;
      }
      if (rawLabel == null || rawLabel === "") return;
      const label = String(rawLabel);
      list.push({ value, label });
      map[value] = label;
    });
    list.sort((a, b) => a.label.localeCompare(b.label));
    return { list, map };
  };
  const normalizeFeedDictionary = (dictData) => {
    const dict = Array.isArray(dictData) ? dictData : [];
    const normalizedDict = dict
      .map((item) => {
        const id = pick(item?.id, item?.ID);
        const type = pick(item?.type, item?.Type);
        if (id == null || type == null) return null;
        const value = pick(item?.value, item?.Value);
        const description = pick(item?.description, item?.Description, item?.name, item?.Name);
        const feedname = pick(item?.feedname, item?.Feedname, item?.feedName, item?.FeedName);
        return {
          id: String(id),
          type: String(type),
          value: value != null ? String(value) : null,
          description: description != null ? String(description) : null,
          feedname: feedname != null ? String(feedname) : null,
        };
      })
      .filter(Boolean);

    const stageItems = normalizedDict.filter((item) => item.type === "10");
    const feedItems = normalizedDict.filter((item) => item.type === "5");

    return {
      stage: buildFeedOptions(stageItems, { labelKey: "value" }),
      feedName: buildFeedOptions(feedItems, { valueKey: "id", labelKey: "value" }),
    };
  };
  const getOptionLabelById = (options, selectedId) => {
    if (selectedId === undefined || selectedId === null || `${selectedId}` === "") return "";
    const match = options.find((entry) => `${entry?.id ?? entry?.ID}` === `${selectedId}`);
    return match?.eventName ?? match?.name ?? "";
  };
  const buildMortalityImageMapKey = (recordId, broilerId = facilityId, cycId = cycleId) =>
    `${broilerId ?? ""}|${cycId ?? ""}|${recordId ?? ""}`;
  const persistMortalityImageBlobMap = (nextMap) => {
    try {
      localStorage.setItem(MORTALITY_IMAGE_MAP_STORAGE_KEY, JSON.stringify(nextMap));
    } catch {}
  };
  const rememberMortalityImageBlobName = (recordId, blobName) => {
    if (recordId == null || !blobName) return;
    const key = buildMortalityImageMapKey(recordId);
    setMortalityImageBlobMap((prev) => {
      const next = { ...(prev || {}), [key]: blobName };
      persistMortalityImageBlobMap(next);
      return next;
    });
  };
  const resolveMortalityImageBlobName = (row) => {
    const fromRow =
      row?.blobName ||
      row?.BlobName ||
      row?.imageBlobName ||
      row?.image_name ||
      row?.imageName ||
      "";
    if (fromRow) return String(fromRow);
    const rowId = row?.id ?? row?.ID ?? row?.Id;
    if (rowId == null) return "";
    const key = buildMortalityImageMapKey(rowId);
    return mortalityImageBlobMap?.[key] || "";
  };

  const mapCatchRows = (raw) => {
    const data = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.value)
      ? raw.value
      : [];

    return data
      .map((row) => {
        const broilerRef = pick(
          row?.broilerID,
          row?.BroilerID,
          row?.broilerId,
          row?.facilityId,
          row?.FacilityID
        );
        const cycleRef = pick(row?.cycleId, row?.CycleId, row?.cycleID, row?.CycleID);
        return {
          id: pick(row?.id, row?.ID),
          broilerId: broilerRef ?? null,
          cycleId: cycleRef ?? null,
          totalweight: pick(row?.totalweight, row?.totalWeight, row?.TotalWeight, row?.Totalweight),
          noofbirds: pick(
            row?.noofbirds,
            row?.noOfBirds,
            row?.NoOfBirds,
            row?.birds,
            row?.total_birds
          ),
          date: pick(row?.date, row?.Date, row?.dateTime, row?.DateTime),
        };
      })
      .filter((entry) => {
        const matchesFacility = entry.broilerId == null || `${entry.broilerId}` === `${facilityId}`;
        const matchesCycle =
          cycleId == null || entry.cycleId == null || `${entry.cycleId}` === `${cycleId}`;
        return matchesFacility && matchesCycle;
      });
  };

  const applyCatchRows = (raw) => {
    const mappedRows = mapCatchRows(raw);
    const merged = combineServerRowsWithOffline(OFFLINE_NS_CATCH, mappedRows, matchCatchRow);
    setCatchRows(merged);
  };

  const openCatchAdd = () => {
    setCatchForm({
      id: null,
      totalweight: "",
      noofbirds: "",
      date: nowLocalDateTimeInput(),
      broilerId: facilityId != null ? String(facilityId) : null,
      cycleId: cycleId != null ? String(cycleId) : null,
    });
    setCatchEditOpen(true);
  };

  const openCatchEdit = async (row) => {
    if (!row) return;
    try {
      const res = await axios.get(catchDataUrl(facilityId), {
        omnioOffline: { skipAuthHeader: true },
      });
      const records = mapCatchRows(res.data);
      const target = row?.id != null ? records.find((entry) => entry.id === row.id) : null;
      const source = target || row;
      setCatchForm({
        id: source?.id ?? null,
        totalweight: source?.totalweight ?? "",
        noofbirds: source?.noofbirds ?? "",
        date: toLocalDateTimeInput(source?.date) || "",
      });
    } catch (error) {
      setCatchForm({
        id: row?.id ?? null,
        totalweight: row?.totalweight ?? "",
        noofbirds: row?.noofbirds ?? "",
        date: toLocalDateTimeInput(row?.date) || "",
      });
    }
    setCatchEditOpen(true);
  };

  const closeCatchEdit = () => {
    setCatchEditOpen(false);
    setCatchForm({
      id: null,
      totalweight: "",
      noofbirds: "",
      date: nowLocalDateTimeInput(),
      broilerId: facilityId != null ? String(facilityId) : null,
      cycleId: cycleId != null ? String(cycleId) : null,
    });
  };
  // Format a date-time string to YYYY-MM-DD (date only)
  const toDateOnly = (val) => {
    if (!val) return "";
    const s = String(val).trim();
    // If already in YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // If ISO or contains time, take the date portion
    const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    // Fallback: try Date
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Format to DD-MM-YYYY HH:mm for display
  const formatDisplayDateTime = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
  };

  const UTC_PLUS_TWO_OFFSET_MINUTES = 120;
  const hasTimezoneSuffix = (value) => /(?:z|[+-]\d{2}:?\d{2})$/i.test(String(value).trim());

  const formatUtcPlusTwoDateParts = (value) => {
    if (!value) return null;
    if (typeof value === "string" && !hasTimezoneSuffix(value)) {
      const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
      if (match) {
        return {
          yyyy: Number(match[1]),
          mm: match[2],
          dd: match[3],
          hh: match[4] ?? "00",
          mi: match[5] ?? "00",
        };
      }
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    const shifted = new Date(parsed.getTime() + UTC_PLUS_TWO_OFFSET_MINUTES * 60 * 1000);
    return {
      yyyy: shifted.getUTCFullYear(),
      mm: String(shifted.getUTCMonth() + 1).padStart(2, "0"),
      dd: String(shifted.getUTCDate()).padStart(2, "0"),
      hh: String(shifted.getUTCHours()).padStart(2, "0"),
      mi: String(shifted.getUTCMinutes()).padStart(2, "0"),
    };
  };

  const formatDisplayDateTimeUtcPlusTwo = (val) => {
    const parts = formatUtcPlusTwoDateParts(val);
    if (!parts) return val ? String(val) : "";
    return `${parts.dd}-${parts.mm}-${parts.yyyy} ${parts.hh}:${parts.mi}`;
  };

  const toUtcPlusTwoDateTimeInput = (val) => {
    const parts = formatUtcPlusTwoDateParts(val);
    if (!parts) return "";
    return `${parts.yyyy}-${parts.mm}-${parts.dd}T${parts.hh}:${parts.mi}`;
  };

  const nowUtcPlusTwoDateTimeInput = () => toUtcPlusTwoDateTimeInput(new Date());

  const toLocalDateTimeInput = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  const nowLocalDateTimeInput = () => toLocalDateTimeInput(new Date());

  const getDateTimestamp = (value) => {
    if (!value) return null;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const notificationTabs = [
    { key: "environmental", label: "Environmental" },
    { key: "performance", label: "Performance" },
    { key: "activities", label: "Activities" },
  ];

  const getNotificationCacheKey = (targetFacilityId) =>
    `OmnioNotificationTables:v1:${targetFacilityId ?? "unknown"}`;

  const readNotificationTableCache = (targetFacilityId) => {
    if (typeof window === "undefined" || targetFacilityId == null) return null;
    try {
      const raw = window.localStorage.getItem(getNotificationCacheKey(targetFacilityId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const rows = parsed?.rows;
      if (
        !rows ||
        !Array.isArray(rows.environmental) ||
        !Array.isArray(rows.performance) ||
        !Array.isArray(rows.activities)
      ) {
        return null;
      }
      return rows;
    } catch (error) {
      console.warn("Unable to read notification table cache", error);
      return null;
    }
  };

  const writeNotificationTableCache = (targetFacilityId, rows) => {
    if (typeof window === "undefined" || targetFacilityId == null || !rows) return;
    try {
      window.localStorage.setItem(
        getNotificationCacheKey(targetFacilityId),
        JSON.stringify({ timestamp: Date.now(), rows })
      );
    } catch (error) {
      console.warn("Unable to write notification table cache", error);
    }
  };

  const [notificationRows, setNotificationRows] = useState({
    environmental: [],
    performance: [],
    activities: [],
  });
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationError, setNotificationError] = useState(null);
  const [notificationReload, setNotificationReload] = useState(0);
  const createNotificationForm = (overrides = {}) => ({
    id: null,
    backendId: null,
    displayId: "",
    kind: "environmental",
    broilerId: "",
    deviceId: "",
    deviceType: "",
    parameter: "",
    kpi: "",
    activity: "",
    strictUpper: "",
    strictLower: "",
    strictEnabled: false,
    fuzzyUpper: "",
    fuzzyLower: "",
    fuzzyEnabled: false,
    completed: false,
    conditionsNotMet: false,
    notCompleted: false,
    deviationP: "",
    enabled: false,
    primaryContact: "",
    secondaryContact: "",
    delay: "",
    ...overrides,
  });
  const [notificationEditOpen, setNotificationEditOpen] = useState(false);
  const [notificationForm, setNotificationForm] = useState(() => createNotificationForm());
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [offlineSyncTick, setOfflineSyncTick] = useState(0);

  const formatNotificationValue = (value) => {
    if (value === true) return "Yes";
    if (value === false) return "No";
    if (value == null) return "";
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
    return `${value}`;
  };

  const formatFuzzyLimitValue = (value) => {
    if (value == null || value === "") return "";
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(Number(value.toFixed(2)));
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length === 0) return "";
      const numeric = Number(trimmed);
      if (Number.isFinite(numeric)) {
        return String(Number(numeric.toFixed(2)));
      }
      return trimmed;
    }
    return formatNotificationValue(value);
  };

  const toNullIfEmpty = (value) => {
    if (value == null) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed.length === 0 ? null : trimmed;
    }
    return value;
  };

  const toNumberOrNull = (value) => {
    if (value == null) return null;
    const str = typeof value === "string" ? value.trim() : `${value}`.trim();
    if (str.length === 0) return null;
    const num = Number(str);
    return Number.isFinite(num) ? num : null;
  };

  const pickNumber = (...values) => {
    for (const value of values) {
      const num = toNumberOrNull(value);
      if (num != null) return num;
    }
    return null;
  };

  const normalizeBinSelection = (value) => {
    if (!value) return "bin1";
    const raw = String(value).trim().toLowerCase();
    if (raw === "feedpaper" || raw === "feed_paper" || raw === "feed-paper" || raw === "paper") {
      return "feedpaper";
    }
    if (raw === "bin2" || raw === "2" || raw === "bin_2" || raw === "bin-2") return "bin2";
    return "bin1";
  };

  const isTrueFlag = (value) =>
    value === true || value === 1 || String(value).trim().toLowerCase() === "true";

  const getBinSelectionFromFlags = (source) => {
    if (!source) return null;
    if (isTrueFlag(pick(source?.bin_2, source?.Bin_2, source?.bin2, source?.Bin2))) {
      return "bin2";
    }
    if (isTrueFlag(pick(source?.bin_1, source?.Bin_1, source?.bin1, source?.Bin1))) {
      return "bin1";
    }
    return null;
  };

  const formatIntValue = (value) => {
    const num = toNumberOrNull(value);
    if (!Number.isFinite(num)) return "";
    return String(Math.trunc(num));
  };

  const normalizeFeedBins = (payload) => {
    const candidate = Array.isArray(payload)
      ? payload[0]
      : Array.isArray(payload?.data)
      ? payload.data[0]
      : payload?.data ?? payload?.value ?? payload;
    if (!candidate) return { bin1: null, bin2: null, feedpaper: null };
    const bin1 = pickNumber(
      candidate?.totalBin_1,
      candidate?.totalBin1,
      candidate?.total_bin_1,
      candidate?.total_bin1,
      candidate?.totalBinOne,
      candidate?.TotalBin_1,
      candidate?.TotalBin1,
      candidate?.Total_Bin_1,
      candidate?.bin1,
      candidate?.bin_1,
      candidate?.binOne,
      candidate?.Bin1,
      candidate?.Bin_1,
      candidate?.BinOne,
      candidate?.["bin 1"],
      candidate?.["Bin 1"],
      candidate?.["Total Bin 1"]
    );
    const bin2 = pickNumber(
      candidate?.totalBin_2,
      candidate?.totalBin2,
      candidate?.total_bin_2,
      candidate?.total_bin2,
      candidate?.totalBinTwo,
      candidate?.TotalBin_2,
      candidate?.TotalBin2,
      candidate?.Total_Bin_2,
      candidate?.bin2,
      candidate?.bin_2,
      candidate?.binTwo,
      candidate?.Bin2,
      candidate?.Bin_2,
      candidate?.BinTwo,
      candidate?.["bin 2"],
      candidate?.["Bin 2"],
      candidate?.["Total Bin 2"]
    );
    const feedpaper = pickNumber(
      candidate?.totalFeedpaper,
      candidate?.totalFeedPaper,
      candidate?.total_feedpaper,
      candidate?.total_feed_paper,
      candidate?.TotalFeedpaper,
      candidate?.TotalFeedPaper,
      candidate?.Total_Feedpaper,
      candidate?.Total_Feed_Paper,
      candidate?.feedpaper,
      candidate?.feedPaper,
      candidate?.feed_paper,
      candidate?.Feedpaper,
      candidate?.FeedPaper,
      candidate?.Feed_Paper,
      candidate?.["feed paper"],
      candidate?.["Feed Paper"],
      candidate?.["Total Feed Paper"]
    );
    return { bin1, bin2, feedpaper };
  };

  const normalizeFeedBalance = (payload) => {
    const candidate = Array.isArray(payload)
      ? payload[0]
      : Array.isArray(payload?.data)
      ? payload.data[0]
      : payload?.data ?? payload?.value ?? payload;
    if (!candidate) {
      return {
        bin1: null,
        bin2: null,
        bin1Consumption: null,
        bin2Consumption: null,
        bin1Total: null,
        bin2Total: null,
      };
    }

    return {
      bin1: pickNumber(candidate?.bin1_balance, candidate?.bin1Balance, candidate?.Bin1Balance),
      bin2: pickNumber(candidate?.bin2_balance, candidate?.bin2Balance, candidate?.Bin2Balance),
      bin1Consumption: pickNumber(
        candidate?.bin1_consumption,
        candidate?.bin1Consumption,
        candidate?.Bin1Consumption
      ),
      bin2Consumption: pickNumber(
        candidate?.bin2_consumption,
        candidate?.bin2Consumption,
        candidate?.Bin2Consumption
      ),
      bin1Total: pickNumber(candidate?.bin1_total, candidate?.bin1Total, candidate?.Bin1Total),
      bin2Total: pickNumber(candidate?.bin2_total, candidate?.bin2Total, candidate?.Bin2Total),
    };
  };

  const mapSetupFeedRows = (data, targetFacilityId, targetCycleId) => {
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.value)
      ? data.value
      : [];

    return list
      .map((row, index) => {
        const startDate = pick(row?.startDate, row?.startdate, row?.StartDate);
        const endDate = pick(row?.endDate, row?.enddate, row?.EndDate);
        const effectiveDate = startDate ?? endDate ?? null;
        const stage = pick(
          row?.stage,
          row?.Stage,
          row?.stageName,
          row?.StageName,
          row?.stageId,
          row?.StageId,
          row?.StageID
        );
        const supplier = pick(
          row?.supplier,
          row?.Supplier,
          row?.supplierId,
          row?.SupplierId,
          row?.SupplierID
        );
        const feedName = pick(
          row?.feedName,
          row?.FeedName,
          row?.feedname,
          row?.Feedname,
          row?.feedId,
          row?.FeedId,
          row?.feed_id
        );
        const feedSpendId = pick(
          row?.feedSpendId,
          row?.FeedSpendId,
          row?.feed_spend_id,
          row?.FeedSpendID
        );
        return {
          id: pick(row?.id, row?.ID, row?.feedId, row?.FeedId, row?.feed_id) ?? `setup-${index}`,
          broilerId: pick(row?.broilerID, row?.BroilerID, row?.facilityId, row?.FacilityID),
          cycleId: pick(row?.cycleId, row?.cycleID, row?.CycleId, row?.CycleID),
          stage: stage != null ? `${stage}` : "",
          supplier: supplier != null ? `${supplier}` : "",
          feedName: feedName != null ? `${feedName}` : "",
          bin1: pickNumber(
            row?.bin_1,
            row?.bin1,
            row?.binOne,
            row?.Bin_1,
            row?.Bin1,
            row?.BinOne,
            row?.totalBin_1,
            row?.totalBin1,
            row?.TotalBin_1,
            row?.TotalBin1,
            row?.["bin 1"],
            row?.["Bin 1"]
          ),
          bin2: pickNumber(
            row?.bin_2,
            row?.bin2,
            row?.binTwo,
            row?.Bin_2,
            row?.Bin2,
            row?.BinTwo,
            row?.totalBin_2,
            row?.totalBin2,
            row?.TotalBin_2,
            row?.TotalBin2,
            row?.["bin 2"],
            row?.["Bin 2"]
          ),
          feedpaper: pickNumber(
            row?.feedpaper,
            row?.feedPaper,
            row?.feed_paper,
            row?.Feedpaper,
            row?.FeedPaper,
            row?.Feed_Paper,
            row?.totalFeedpaper,
            row?.totalFeedPaper,
            row?.TotalFeedpaper,
            row?.TotalFeedPaper,
            row?.["feed paper"],
            row?.["Feed Paper"]
          ),
          startDate,
          endDate,
          startTimestamp: getDateTimestamp(startDate),
          endTimestamp: getDateTimestamp(endDate),
          dateTime: effectiveDate,
          timestamp: getDateTimestamp(effectiveDate),
        };
      })
      .filter((entry) => {
        if (entry.broilerId != null && `${entry.broilerId}` !== `${targetFacilityId}`) return false;
        if (targetCycleId == null || entry.cycleId == null) return true;
        return `${entry.cycleId}` === `${targetCycleId}`;
      });
  };

  const sortRowsByDateAscending = (rows, getDateValue) =>
    [...rows].sort((a, b) => {
      const aTime = getDateTimestamp(getDateValue(a));
      const bTime = getDateTimestamp(getDateValue(b));
      if (aTime == null && bTime == null) return 0;
      if (aTime == null) return -1;
      if (bTime == null) return 1;
      if (aTime < bTime) return -1;
      if (aTime > bTime) return 1;
      const aId = `${a?.id ?? ""}`;
      const bId = `${b?.id ?? ""}`;
      return aId.localeCompare(bId);
    });

  const addToBalance = (current, amount) => {
    const num = toNumberOrNull(amount);
    if (num == null) return current;
    return (toNumberOrNull(current) ?? 0) + num;
  };

  const getSetupBinAmount = (row, binKey) => {
    if (binKey === "bin2") return toNumberOrNull(row?.bin2);
    if (binKey === "feedpaper") return toNumberOrNull(row?.feedpaper);
    return toNumberOrNull(row?.bin1);
  };

  const getConsumptionBinAmount = (row, binKey) => {
    if (normalizeBinSelection(row?.bin ?? row?.binSelection) !== binKey) return 0;
    return toNumberOrNull(row?.feedKg) ?? 0;
  };

  const computeAvailableBinBalanceAtDate = (
    binKey,
    setupRows,
    consumptionRows,
    targetDateTime,
    options = {}
  ) => {
    const targetTime = getDateTimestamp(targetDateTime);
    const excludedIds = new Set(
      (options.excludeIds ?? []).filter((value) => value != null).map((value) => `${value}`)
    );
    const sortedSetupRows = sortRowsByDateAscending(
      Array.isArray(setupRows) ? setupRows : [],
      (row) => row?.dateTime
    );
    const sortedConsumptionRows = sortRowsByDateAscending(
      (Array.isArray(consumptionRows) ? consumptionRows : []).filter(
        (row) => row && getConsumptionType(row) !== "water" && !excludedIds.has(`${row?.id ?? ""}`)
      ),
      (row) => row?.dateTime
    );

    let balance = null;
    let hasSetupContribution = false;

    sortedSetupRows.forEach((row) => {
      if (targetTime != null && row?.timestamp != null && row.timestamp > targetTime) return;
      const amount = getSetupBinAmount(row, binKey);
      if (amount == null) return;
      hasSetupContribution = true;
      balance = addToBalance(balance, amount);
    });

    if (!hasSetupContribution) {
      balance = toNumberOrNull(options.fallbackBalance);
    }

    sortedConsumptionRows.forEach((row) => {
      const rowTime = getDateTimestamp(row?.dateTime);
      if (targetTime != null && rowTime != null && rowTime >= targetTime) return;
      const amount = getConsumptionBinAmount(row, binKey);
      if (balance != null) balance -= amount;
    });

    return balance;
  };

  const computeAvailableFeedBinsAtDate = (
    setupRows,
    consumptionRows,
    targetDateTime,
    options = {}
  ) => {
    return {
      bin1: computeAvailableBinBalanceAtDate("bin1", setupRows, consumptionRows, targetDateTime, {
        excludeIds: options.excludeIds,
        fallbackBalance: options.fallbackBins?.bin1,
      }),
      bin2: computeAvailableBinBalanceAtDate("bin2", setupRows, consumptionRows, targetDateTime, {
        excludeIds: options.excludeIds,
        fallbackBalance: options.fallbackBins?.bin2,
      }),
      feedpaper: computeAvailableBinBalanceAtDate(
        "feedpaper",
        setupRows,
        consumptionRows,
        targetDateTime,
        {
          excludeIds: options.excludeIds,
          fallbackBalance: options.fallbackBins?.feedpaper,
        }
      ),
    };
  };

  const computeFeedBalances = (bins, selection, feedValue) => {
    const bin1 = toNumberOrNull(bins?.bin1);
    const bin2 = toNumberOrNull(bins?.bin2);
    const feedpaper = toNumberOrNull(bins?.feedpaper);
    const amount = toNumberOrNull(feedValue) ?? 0;
    if (selection === "bin2") {
      return {
        bin1Bal: bin1,
        bin2Bal: bin2 != null ? bin2 - amount : null,
        feedpaperBal: feedpaper,
      };
    }
    if (selection === "feedpaper") {
      return {
        bin1Bal: bin1,
        bin2Bal: bin2,
        feedpaperBal: feedpaper != null ? feedpaper - amount : null,
      };
    }
    return {
      bin1Bal: bin1 != null ? bin1 - amount : null,
      bin2Bal: bin2,
      feedpaperBal: feedpaper,
    };
  };

  const normalizeContactForForm = (value) => {
    if (value == null) return "";
    if (typeof value === "string") return value.trim();
    return `${value}`;
  };

  const normalizeContactForPayload = (value) => {
    if (value == null) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed.length === 0 ? null : trimmed;
    }
    if (typeof value === "number") {
      if (Number.isFinite(value)) return `${value}`;
      return null;
    }
    const str = `${value}`.trim();
    return str.length === 0 ? null : str;
  };

  const toNotificationBoolean = (value) => {
    if (value === true || value === false) return value;
    if (value == null) return false;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["true", "1", "yes", "y"].includes(normalized)) return true;
      if (["false", "0", "no", "n"].includes(normalized)) return false;
    }
    if (typeof value === "number") return value !== 0;
    return Boolean(value);
  };

  const notificationTimeToSeconds = (value) => {
    if (typeof value !== "string" || !/^\d{2}:\d{2}$/.test(value)) return null;
    const [hours, minutes] = value.split(":").map(Number);
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
    return hours * 60 * 60 + minutes * 60;
  };

  const notificationSecondsToTime = (value) => {
    const seconds = toNumberOrNull(value);
    if (!Number.isFinite(seconds) || seconds < 0 || seconds >= 24 * 60 * 60) return "";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const renderBooleanCheckbox = (value) => {
    const checked = toNotificationBoolean(value);
    return (
      <Checkbox
        size="small"
        checked={checked}
        disabled
        disableRipple
        sx={{
          p: 0,
          "& .MuiSvgIcon-root": { fontSize: 18 },
        }}
        inputProps={{ "aria-label": checked ? "true" : "false" }}
      />
    );
  };

  const renderNotificationTableBody = (rows, emptyLabel, tabKey) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    const isPerformance = tabKey === "performance";
    const isActivities = tabKey === "activities";
    const noDataColSpan = isPerformance ? 5 : isActivities ? 6 : 10;

    return (
      <TableContainer
        component={Box}
        sx={{
          boxShadow: "none",
          mx: isPerformance || isActivities ? 0 : "auto",
          width: "fit-content",
        }}
      >
        <Table
          key={tabKey}
          size="small"
          sx={{
            tableLayout: "fixed",
            width: isPerformance ? "12cm" : isActivities ? "17cm" : "21cm",
            minWidth: isPerformance ? "12cm" : isActivities ? "17cm" : "21cm",
            borderCollapse: "collapse",
            "& th, & td": {
              border: "1px solid",
              borderColor: "grey.300",
              fontSize: "0.9rem",
              lineHeight: 1.2,
              p: 0.5,
              textAlign: "center",
              color: "text.primary",
            },
          }}
        >
          <colgroup>
            {isPerformance ? (
              <>
                <col style={{ width: "3.4cm" }} />
                <col style={{ width: "2.56cm" }} />
                <col style={{ width: "2.2cm" }} />
                <col style={{ width: "2.0cm" }} />
                <col style={{ width: "1.2cm" }} />
              </>
            ) : isActivities ? (
              <>
                <col style={{ width: "5.2cm" }} />
                <col style={{ width: "2.6cm" }} />
                <col style={{ width: "3.6cm" }} />
                <col style={{ width: "2.8cm" }} />
                <col style={{ width: "2.0cm" }} />
                <col style={{ width: "1.2cm" }} />
              </>
            ) : (
              <>
                <col style={{ width: "2.1cm" }} />
                <col style={{ width: "2.6cm" }} />
                <col style={{ width: "1.9cm" }} />
                <col style={{ width: "1.9cm" }} />
                <col style={{ width: "1.9cm" }} />
                <col style={{ width: "1.9cm" }} />
                <col style={{ width: "1.9cm" }} />
                <col style={{ width: "1.9cm" }} />
                <col style={{ width: "1.4cm" }} />
                <col style={{ width: "1.3cm" }} />
              </>
            )}
          </colgroup>
          <TableHead sx={{ display: "table-header-group", p: 0 }}>
            {isPerformance ? (
              <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                <TableCell sx={{ fontWeight: 700 }}>KPI</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Deviation %</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Enabled</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  Time
                  <br />
                  (UTC+2)
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} />
              </TableRow>
            ) : isActivities ? (
              <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                <TableCell sx={{ fontWeight: 700 }}>Activity</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Completed</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Completed: Conditions not met</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  Not
                  <br />
                  completed
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  Time
                  <br />
                  (UTC+2)
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} />
              </TableRow>
            ) : (
              <>
                <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 700 }} rowSpan={2}>
                    Device
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} rowSpan={2}>
                    Parametre
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} colSpan={3}>
                    Strict Limit
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} colSpan={3}>
                    Fuzzy Limit
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} rowSpan={2}>
                    Freq
                    <br />
                    (min)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} rowSpan={2} />
                </TableRow>
                <TableRow sx={{ height: "0.64cm", bgcolor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Upper</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Lower</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Enabled</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Upper</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Lower</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Enabled</TableCell>
                </TableRow>
              </>
            )}
          </TableHead>
          <TableBody>
            {safeRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={noDataColSpan}
                  align="center"
                  sx={{ py: 2, color: "text.secondary" }}
                >
                  {emptyLabel}
                </TableCell>
              </TableRow>
            ) : (
              safeRows.map((row, idx) => {
                const rowKey = row?.id ?? idx;

                if (isPerformance) {
                  const enabledValue = pick(row?.enabled, row?.strictEnabled, row?.fuzzyEnabled);
                  const deviationValue = pick(
                    row?.deviation,
                    row?.deviationp,
                    row?.strictUpper,
                    row?.strictLower,
                    row?.fuzzyUpper,
                    row?.fuzzyLower
                  );

                  return (
                    <TableRow key={rowKey} sx={{ height: "0.64cm" }}>
                      <TableCell>
                        {formatNotificationValue(pick(row?.parameter, row?.device, row?.kpi))}
                      </TableCell>
                      <TableCell>{formatNotificationValue(deviationValue)}</TableCell>
                      <TableCell>{renderBooleanCheckbox(enabledValue)}</TableCell>
                      <TableCell>
                        {formatNotificationValue(notificationSecondsToTime(row?.delay))}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <EditIcon
                            sx={{ fontSize: 22, color: "text.secondary", cursor: "pointer" }}
                            onClick={() => openNotificationEdit(row)}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                }

                if (isActivities) {
                  const activityName = pick(
                    row?.activity,
                    row?.Activity,
                    row?.activityName,
                    row?.ActivityName,
                    row?.task,
                    row?.Task,
                    row?.name,
                    row?.Name,
                    row?.parameter,
                    row?.device
                  );
                  const completedValue = pick(
                    row?.completed,
                    row?.Completed,
                    row?.completedCount,
                    row?.CompletedCount,
                    row?.complete,
                    row?.Complete
                  );
                  const completedConditionsValue = pick(
                    row?.completedConditionsNotMet,
                    row?.CompletedConditionsNotMet,
                    row?.completedConditions,
                    row?.conditionsNotMet,
                    row?.ConditionsNotMet,
                    row?.completedButConditionsNotMet,
                    row?.CompletedButConditionsNotMet
                  );
                  const notCompletedValue = pick(
                    row?.notCompleted,
                    row?.NotCompleted,
                    row?.incomplete,
                    row?.Incomplete,
                    row?.pending,
                    row?.Pending
                  );

                  return (
                    <TableRow key={rowKey} sx={{ height: "0.64cm" }}>
                      <TableCell>{formatNotificationValue(activityName)}</TableCell>
                      <TableCell>{renderBooleanCheckbox(completedValue)}</TableCell>
                      <TableCell>{renderBooleanCheckbox(completedConditionsValue)}</TableCell>
                      <TableCell>{renderBooleanCheckbox(notCompletedValue)}</TableCell>
                      <TableCell>
                        {formatNotificationValue(notificationSecondsToTime(row?.delay))}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <EditIcon
                            sx={{ fontSize: 22, color: "text.secondary", cursor: "pointer" }}
                            onClick={() => openNotificationEdit(row)}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                }

                return (
                  <TableRow key={rowKey} sx={{ height: "0.64cm" }}>
                    <TableCell>{formatNotificationValue(row?.device)}</TableCell>
                    <TableCell>{formatNotificationValue(row?.parameter)}</TableCell>
                    <TableCell>{formatNotificationValue(row?.strictUpper)}</TableCell>
                    <TableCell>{formatNotificationValue(row?.strictLower)}</TableCell>
                    <TableCell>{renderBooleanCheckbox(row?.strictEnabled)}</TableCell>
                    <TableCell>{formatFuzzyLimitValue(row?.fuzzyUpper)}</TableCell>
                    <TableCell>{formatFuzzyLimitValue(row?.fuzzyLower)}</TableCell>
                    <TableCell>{renderBooleanCheckbox(row?.fuzzyEnabled)}</TableCell>
                    <TableCell>{formatNotificationValue(row?.delay)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <EditIcon
                          sx={{ fontSize: 22, color: "text.secondary", cursor: "pointer" }}
                          onClick={() => openNotificationEdit(row)}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderNotificationsTable = (rows, tabMeta) => {
    const { key: tabKey = "notifications", label: tabLabel = "Notifications" } = tabMeta || {};
    const rowsArray = Array.isArray(rows) ? rows : [];
    const emptyLabel = `No ${tabLabel.toLowerCase()} notifications configured`;
    return renderNotificationTableBody(rowsArray, emptyLabel, tabKey);
  };

  useEffect(() => {
    if (openDialog !== "notifications" || facilityId == null) return;

    let cancelled = false;
    const cachedRows = readNotificationTableCache(facilityId);
    const hasCachedRows = cachedRows != null;

    const normalizeList = (raw) => {
      if (Array.isArray(raw)) return raw;
      if (Array.isArray(raw?.data)) return raw.data;
      if (Array.isArray(raw?.value)) return raw.value;
      if (raw && typeof raw === "object") return [raw];
      return [];
    };

    const fetchDetailsForDevice = async (device) => {
      const idRaw = pick(device?.deviceID, device?.deviceId, device?.DeviceID);
      const deviceId = idRaw == null ? "" : String(idRaw).trim();
      if (!deviceId) return [];
      try {
        const detailRes = await axios.get(deviceDetailsForDeviceUrl(facilityId, deviceId), {
          omnioOffline: { skipAuthHeader: true },
        });
        const normalized = normalizeList(detailRes.data);
        if (normalized.length > 0) return normalized;
      } catch (error) {
        // Ignore failures and fall back to list payload
      }
      return [device];
    };

    const loadNotifications = async () => {
      setNotificationLoading(!hasCachedRows);
      setNotificationError(null);
      if (hasCachedRows) {
        setNotificationRows(cachedRows);
      } else {
        setNotificationRows((prev) => ({
          ...prev,
          environmental: [],
          performance: [],
          activities: [],
        }));
      }

      let performanceRows = cachedRows?.performance ?? [];
      let activityRows = cachedRows?.activities ?? [];
      try {
        const perfRes = await axios.get(kpiNotificationsUrl(facilityId), {
          omnioOffline: { skipAuthHeader: true },
        });
        const rawPerformance = normalizeList(perfRes.data);
        performanceRows = rawPerformance
          .map((entry, index) => {
            const backendIdRaw = pick(
              entry?.id,
              entry?.Id,
              entry?.ID,
              entry?.notificationId,
              entry?.notificationID,
              entry?.broilerNotificationId,
              entry?.BroilerNotificationId,
              entry?.broilerNotificationID,
              entry?.BroilerNotificationID,
              entry?.kpiId,
              entry?.kpiID,
              entry?.deviceDataID,
              entry?.DeviceDataID
            );
            const backendIdStr = (() => {
              if (backendIdRaw == null) return "";
              const str = String(backendIdRaw).trim();
              return str.length === 0 ? "" : str;
            })();
            const displayIdRaw = pick(
              entry?.id,
              entry?.ID,
              entry?.Id,
              entry?.displayId,
              entry?.displayID
            );
            const displayId = (() => {
              const fallback = `performance-${index}`;
              if (displayIdRaw == null) return backendIdStr || fallback;
              const str = String(displayIdRaw).trim();
              if (str.length === 0) return backendIdStr || fallback;
              return str;
            })();
            const backendIdValue = (() => {
              if (backendIdStr.length === 0) return null;
              const numeric = toNumberOrNull(backendIdStr);
              return numeric != null ? numeric : backendIdStr;
            })();
            const submissionId = backendIdValue ?? displayId;
            const kpiName = pick(
              entry?.kpi,
              entry?.KPI,
              entry?.metric,
              entry?.Metric,
              entry?.parameter,
              entry?.Parameter,
              entry?.name,
              entry?.Name,
              entry?.description
            );
            const deviationRaw = pick(
              entry?.deviation,
              entry?.deviationp,
              entry?.deviationP,
              entry?.deviationPct,
              entry?.deviationPercentage,
              entry?.deviation_percent
            );
            const deviationValue = (() => {
              const parsed = toNumberOrNull(deviationRaw);
              if (parsed != null) return parsed;
              if (deviationRaw == null) return null;
              const str = String(deviationRaw).trim();
              return str.length === 0 ? null : str;
            })();
            const delayRaw = pick(
              entry?.delay,
              entry?.notificationDelay,
              entry?.delayMinutes,
              entry?.delayMins,
              entry?.delayInMinutes
            );
            const delayValue = (() => {
              const parsed = toNumberOrNull(delayRaw);
              if (parsed != null) return parsed;
              if (delayRaw == null) return null;
              const str = String(delayRaw).trim();
              return str.length === 0 ? null : str;
            })();
            const enabledValue = toNotificationBoolean(
              pick(
                entry?.enabled,
                entry?.Enabled,
                entry?.isEnabled,
                entry?.IsEnabled,
                entry?.active,
                entry?.Active
              )
            );
            const primaryContactValue = normalizeContactForForm(
              pick(
                entry?.primaryContact,
                entry?.PrimaryContact,
                entry?.primary_contact,
                entry?.primary
              )
            );
            const secondaryContactValue = normalizeContactForForm(
              pick(
                entry?.secondaryContact,
                entry?.SecondaryContact,
                entry?.secondary_contact,
                entry?.secondary
              )
            );
            const broilerIdValue = pick(
              entry?.broilerId,
              entry?.BroilerId,
              entry?.broilerID,
              entry?.BroilerID,
              facilityId
            );
            return {
              id: submissionId,
              backendId: backendIdValue,
              displayId,
              kind: "performance",
              broilerId: pick(
                entry?.broilerId,
                entry?.BroilerId,
                entry?.broilerID,
                entry?.BroilerID
              ),
              deviceDataID: pick(entry?.deviceDataID, entry?.DeviceDataID),
              device: pick(
                entry?.device,
                entry?.deviceId,
                entry?.deviceID,
                entry?.DeviceId,
                entry?.DeviceID
              ),
              kpi: kpiName,
              parameter: kpiName,
              deviation: deviationValue,
              deviationp: deviationValue,
              deviationP: deviationValue,
              enabled: enabledValue,
              strictEnabled: enabledValue,
              primaryContact: primaryContactValue,
              secondaryContact: secondaryContactValue,
              delay: delayValue,
              raw: entry,
            };
          })
          .filter((row) => row.kpi || row.parameter);

        performanceRows.sort((a, b) => String(a.kpi ?? "").localeCompare(String(b.kpi ?? "")));
      } catch {
        // Ignore KPI fetch failures so other notification tabs can still render.
      }

      try {
        const actRes = await axios.get(actNotificationsUrl(facilityId), {
          omnioOffline: { skipAuthHeader: true },
        });
        const rawActivities = normalizeList(actRes.data);
        activityRows = rawActivities
          .map((entry, index) => {
            const backendIdRaw = pick(
              entry?.id,
              entry?.Id,
              entry?.ID,
              entry?.activityId,
              entry?.ActivityId
            );
            const backendIdStr = (() => {
              if (backendIdRaw == null) return "";
              const str = String(backendIdRaw).trim();
              return str.length === 0 ? "" : str;
            })();
            const backendIdValue = (() => {
              if (backendIdStr.length === 0) return null;
              const numeric = toNumberOrNull(backendIdStr);
              return numeric != null ? numeric : backendIdStr;
            })();
            const displayId = backendIdStr.length === 0 ? `activity-${index}` : backendIdStr;
            const submissionId = backendIdValue ?? displayId;
            const activityName = pick(
              entry?.act,
              entry?.activity,
              entry?.Activity,
              entry?.name,
              entry?.Name,
              entry?.task,
              entry?.Task
            );
            const completedValue = toNotificationBoolean(
              pick(entry?.completed, entry?.Completed, entry?.isCompleted, entry?.IsCompleted)
            );
            const conditionsValue = toNotificationBoolean(
              pick(
                entry?.conditionsNotMet,
                entry?.ConditionsNotMet,
                entry?.completedConditionsNotMet,
                entry?.CompletedConditionsNotMet
              )
            );
            const notCompletedValue = toNotificationBoolean(
              pick(
                entry?.notCompleted,
                entry?.NotCompleted,
                entry?.incomplete,
                entry?.Incomplete,
                entry?.pending,
                entry?.Pending
              )
            );
            const delayRaw = pick(
              entry?.delay,
              entry?.Delay,
              entry?.delayMinutes,
              entry?.delayMins,
              entry?.delayInMinutes
            );
            const delayValue = (() => {
              const parsed = toNumberOrNull(delayRaw);
              if (parsed != null) return parsed;
              if (delayRaw == null) return null;
              const str = String(delayRaw).trim();
              return str.length === 0 ? null : str;
            })();
            const primaryContactValue = normalizeContactForForm(
              pick(
                entry?.primaryContact,
                entry?.PrimaryContact,
                entry?.primary_contact,
                entry?.primary
              )
            );
            const secondaryContactValue = normalizeContactForForm(
              pick(
                entry?.secondaryContact,
                entry?.SecondaryContact,
                entry?.secondary_contact,
                entry?.secondary
              )
            );
            const broilerIdValue = pick(
              entry?.broilerId,
              entry?.BroilerId,
              entry?.broilerID,
              entry?.BroilerID,
              facilityId
            );
            return {
              id: submissionId,
              backendId: backendIdValue,
              displayId,
              kind: "activities",
              activity: activityName,
              act: activityName,
              completed: completedValue,
              conditionsNotMet: conditionsValue,
              completedConditionsNotMet: conditionsValue,
              notCompleted: notCompletedValue,
              primaryContact: primaryContactValue,
              secondaryContact: secondaryContactValue,
              delay: delayValue,
              broilerId: broilerIdValue,
              raw: entry,
            };
          })
          .filter((row) => row.activity);
        activityRows.sort((a, b) =>
          String(a.activity ?? "").localeCompare(String(b.activity ?? ""))
        );
      } catch (error) {
        if (!cancelled) {
          activityRows = [];
        }
      }

      try {
        const listRes = await axios.get(deviceDetailsUrl(facilityId), {
          omnioOffline: { skipAuthHeader: true },
        });
        const rawList = normalizeList(listRes.data);
        const seen = new Set();
        const uniqueDevices = [];

        rawList.forEach((device) => {
          const idRaw = pick(device?.deviceID, device?.deviceId, device?.DeviceID);
          const deviceId = idRaw == null ? "" : String(idRaw).trim();
          if (!deviceId || seen.has(deviceId)) return;
          seen.add(deviceId);
          uniqueDevices.push(device);
        });

        if (uniqueDevices.length === 0) {
          if (!cancelled) {
            const refreshedRows = {
              environmental: [],
              performance: performanceRows,
              activities: activityRows,
            };
            setNotificationRows(refreshedRows);
            writeNotificationTableCache(facilityId, refreshedRows);
            setNotificationLoading(false);
          }
          return;
        }

        const detailResults = await Promise.allSettled(
          uniqueDevices.map((device) => fetchDetailsForDevice(device))
        );

        const combined = detailResults.flatMap((result, index) => {
          if (
            result.status === "fulfilled" &&
            Array.isArray(result.value) &&
            result.value.length > 0
          ) {
            return result.value;
          }
          return uniqueDevices[index] ? [uniqueDevices[index]] : [];
        });

        const rowsByType = new Map();

        combined.forEach((entry) => {
          const deviceIdRaw = pick(entry?.deviceID, entry?.deviceId, entry?.DeviceID);
          const deviceId = deviceIdRaw == null ? "" : String(deviceIdRaw).trim();
          if (!deviceId) return;

          const deviceTypeRaw = pick(entry?.deviceType, entry?.DeviceType, entry?.type);
          const deviceTypeValue = deviceTypeRaw == null ? "" : String(deviceTypeRaw).trim();
          const deviceType = deviceTypeValue.length === 0 ? "Device" : deviceTypeValue;

          const limits = normalizeList(
            pick(entry?.device_limits, entry?.deviceLimits, entry?.DeviceLimits)
          );
          if (limits.length === 0) return;

          limits.forEach((limit) => {
            const deviceDataID = pick(
              limit?.deviceDataID,
              limit?.DeviceDataID,
              limit?.deviceDataId,
              limit?.id,
              limit?.Id,
              limit?.ID
            );
            const row = {
              id:
                deviceDataID ?? `${deviceId}-${pick(limit?.valueType, limit?.ValueType, "value")}`,
              deviceDataID,
              device: deviceId,
              deviceType,
              parameter: pick(limit?.valueType, limit?.ValueType),
              strictUpper: pick(limit?.highLimit, limit?.HighLimit),
              strictLower: pick(limit?.lowLimit, limit?.LowLimit),
              strictEnabled: pick(limit?.thresholdEnabled, limit?.ThresholdEnabled),
              fuzzyUpper: pick(
                limit?.fuzzy_High,
                limit?.Fuzzy_High,
                limit?.fuzzyHigh,
                limit?.FuzzyHigh
              ),
              fuzzyLower: pick(
                limit?.fuzzy_Low,
                limit?.Fuzzy_Low,
                limit?.fuzzyLow,
                limit?.FuzzyLow
              ),
              fuzzyEnabled: pick(limit?.fuzzyLimits, limit?.FuzzyLimits),
              primaryContact: normalizeContactForForm(
                pick(limit?.primaryContact, limit?.PrimaryContact)
              ),
              secondaryContact: normalizeContactForForm(
                pick(limit?.secondaryContact, limit?.SecondaryContact)
              ),
              delay: pick(limit?.secondaryContactDelay, limit?.SecondaryContactDelay),
              deviceName: pick(entry?.name, entry?.Name, entry?.description),
              rawLimit: limit,
            };

            if (!rowsByType.has(deviceType)) {
              rowsByType.set(deviceType, []);
            }
            rowsByType.get(deviceType).push(row);
          });
        });

        const collectedRows = Array.from(rowsByType.entries()).flatMap(([deviceType, rows]) =>
          [...rows]
            .sort((a, b) => String(a?.parameter ?? "").localeCompare(String(b?.parameter ?? "")))
            .map((row) => ({ ...row, deviceType }))
        );

        const mergeableEnvironmentalFields = [
          "strictUpper",
          "strictLower",
          "strictEnabled",
          "fuzzyUpper",
          "fuzzyLower",
          "fuzzyEnabled",
          "primaryContact",
          "secondaryContact",
          "delay",
        ];
        const configuredFieldCount = (row) =>
          mergeableEnvironmentalFields.reduce(
            (count, field) => (pick(row?.[field]) !== undefined ? count + 1 : count),
            0
          );
        const environmentalRowsByParameter = new Map();

        collectedRows.forEach((row) => {
          const rowKey = `${String(row?.device ?? "")
            .trim()
            .toLowerCase()}|${String(row?.parameter ?? "")
            .trim()
            .toLowerCase()}`;
          const existing = environmentalRowsByParameter.get(rowKey);
          if (!existing) {
            environmentalRowsByParameter.set(rowKey, row);
            return;
          }

          const preferred =
            configuredFieldCount(row) > configuredFieldCount(existing) ? row : existing;
          const fallback = preferred === row ? existing : row;
          const merged = { ...fallback, ...preferred };
          mergeableEnvironmentalFields.forEach((field) => {
            merged[field] = pick(preferred?.[field], fallback?.[field]);
          });
          environmentalRowsByParameter.set(rowKey, merged);
        });

        const aggregatedRows = Array.from(environmentalRowsByParameter.values());

        aggregatedRows.sort((a, b) => {
          const typeCompare = String(a.deviceType ?? "").localeCompare(String(b.deviceType ?? ""));
          if (typeCompare !== 0) return typeCompare;
          return String(a.parameter ?? "").localeCompare(String(b.parameter ?? ""));
        });

        if (!cancelled) {
          const refreshedRows = {
            environmental: aggregatedRows,
            performance: performanceRows,
            activities: activityRows,
          };
          setNotificationRows(refreshedRows);
          writeNotificationTableCache(facilityId, refreshedRows);
          setNotificationLoading(false);
        }
      } catch (error) {
        if (!cancelled) {
          if (!hasCachedRows) {
            setNotificationRows({
              environmental: [],
              performance: performanceRows,
              activities: activityRows,
            });
            setNotificationError(error);
          }
          setNotificationLoading(false);
        }
      }
    };

    loadNotifications();

    return () => {
      cancelled = true;
    };
  }, [openDialog, facilityId, notificationReload]);

  useEffect(() => {
    if (facilityId == null || typeof window === "undefined") return undefined;

    const enabledFrequencies = notificationRows.environmental
      .filter(
        (row) =>
          toNotificationBoolean(row?.strictEnabled) || toNotificationBoolean(row?.fuzzyEnabled)
      )
      .map((row) => toNumberOrNull(row?.delay))
      .filter((value) => Number.isInteger(value) && value >= 1);

    if (enabledFrequencies.length === 0) return undefined;

    const intervalMinutes = Math.min(...enabledFrequencies);
    const intervalId = window.setInterval(() => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      const triggerUrl = environmentalAlertsTriggerUrl(facilityId);
      if (!triggerUrl) return;

      axios
        .post(triggerUrl, null, {
          headers: { "Content-Type": "application/json" },
          omnioOffline: { skipAuthHeader: true },
          withCredentials: false,
        })
        .catch((error) => {
          console.error("Failed to run scheduled Environmental alert evaluation", error);
        });
    }, intervalMinutes * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [facilityId, notificationRows.environmental]);

  useEffect(() => {
    if (facilityId == null || typeof window === "undefined") return undefined;

    const scheduledGroups = [
      {
        kind: "performance",
        rows: notificationRows.performance,
        isEnabled: (row) => toNotificationBoolean(row?.enabled),
        getUrl: kpiAlertsTriggerUrl,
      },
      {
        kind: "activity",
        rows: notificationRows.activities,
        isEnabled: (row) =>
          toNotificationBoolean(row?.completed) ||
          toNotificationBoolean(row?.conditionsNotMet) ||
          toNotificationBoolean(row?.notCompleted),
        getUrl: activityAlertsTriggerUrl,
      },
    ];
    const timeoutIds = new Set();
    let cancelled = false;

    const millisecondsUntilUtcPlusTwoTime = (secondsAfterMidnight) => {
      const utcPlusTwoOffset = 2 * 60 * 60 * 1000;
      const shiftedNow = new Date(Date.now() + utcPlusTwoOffset);
      const target = new Date(shiftedNow);
      const hours = Math.floor(secondsAfterMidnight / 3600);
      const minutes = Math.floor((secondsAfterMidnight % 3600) / 60);
      target.setUTCHours(hours, minutes, 0, 0);
      if (target.getTime() <= shiftedNow.getTime()) target.setUTCDate(target.getUTCDate() + 1);
      return target.getTime() - shiftedNow.getTime();
    };

    const scheduleDailyTrigger = (group, secondsAfterMidnight) => {
      const timeoutId = window.setTimeout(async () => {
        timeoutIds.delete(timeoutId);
        if (cancelled) return;
        if (typeof navigator === "undefined" || navigator.onLine !== false) {
          const triggerUrl = group.getUrl(facilityId);
          if (triggerUrl) {
            try {
              await axios.post(triggerUrl, null, {
                headers: { "Content-Type": "application/json" },
                omnioOffline: { skipAuthHeader: true },
                withCredentials: false,
              });
            } catch (error) {
              console.error(`Failed to run scheduled ${group.kind} alert evaluation`, error);
            }
          }
        }
        if (!cancelled) scheduleDailyTrigger(group, secondsAfterMidnight);
      }, millisecondsUntilUtcPlusTwoTime(secondsAfterMidnight));
      timeoutIds.add(timeoutId);
    };

    scheduledGroups.forEach((group) => {
      const uniqueTimes = new Set(
        group.rows
          .filter(group.isEnabled)
          .map((row) => toNumberOrNull(row?.delay))
          .filter((seconds) => Number.isInteger(seconds) && seconds >= 0 && seconds < 24 * 60 * 60)
      );
      uniqueTimes.forEach((seconds) => scheduleDailyTrigger(group, seconds));
    });

    return () => {
      cancelled = true;
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
      timeoutIds.clear();
    };
  }, [facilityId, notificationRows.activities, notificationRows.performance]);

  const openNotificationEdit = (row) => {
    const backendId =
      pick(
        row?.backendId,
        row?.id,
        row?.deviceDataID,
        row?.DeviceDataID,
        row?.rawLimit?.deviceDataID,
        row?.rawLimit?.DeviceDataID,
        row?.raw?.notificationId,
        row?.raw?.notificationID,
        row?.raw?.broilerNotificationId,
        row?.raw?.BroilerNotificationId,
        row?.raw?.broilerNotificationID,
        row?.raw?.BroilerNotificationID,
        row?.raw?.kpiId,
        row?.raw?.kpiID,
        row?.raw?.id,
        row?.raw?.ID,
        row?.raw?.Id
      ) ?? null;
    const fallbackDisplayId = (() => {
      const rawValue = pick(
        row?.deviceDataID,
        row?.DeviceDataID,
        row?.rawLimit?.deviceDataID,
        row?.rawLimit?.DeviceDataID,
        row?.id,
        row?.raw?.id,
        row?.raw?.ID,
        row?.raw?.notificationId,
        row?.raw?.notificationID,
        row?.raw?.Id
      );
      if (rawValue == null) return "";
      const str = String(rawValue).trim();
      return str.length === 0 ? "" : str;
    })();
    const displayId = row?.displayId ?? (backendId != null ? `${backendId}` : fallbackDisplayId);
    const effectiveId = backendId ?? displayId ?? fallbackDisplayId;
    if (!effectiveId) return;
    const kind =
      row?.kind ??
      (row?.kpi
        ? "performance"
        : row?.activity != null || row?.act != null
        ? "activities"
        : "environmental");

    const broilerIdValue = pick(
      row?.broilerId,
      row?.raw?.broilerId,
      row?.raw?.BroilerId,
      row?.raw?.broilerID,
      row?.raw?.BroilerID,
      facilityId
    );

    if (kind === "performance") {
      const deviationValue = (() => {
        const val = pick(row?.deviationP, row?.deviation, row?.deviationp);
        if (val == null || val === "") return "";
        return `${val}`;
      })();

      setNotificationForm(
        createNotificationForm({
          id: effectiveId,
          backendId,
          displayId,
          kind,
          broilerId: broilerIdValue ?? facilityId ?? "",
          deviceId: row?.device ?? "",
          deviceType: row?.deviceType ?? "",
          parameter: row?.parameter ?? row?.kpi ?? "",
          kpi: row?.kpi ?? row?.parameter ?? "",
          deviationP: deviationValue,
          enabled: toNotificationBoolean(row?.enabled ?? row?.strictEnabled ?? row?.fuzzyEnabled),
          strictEnabled: toNotificationBoolean(row?.enabled ?? row?.strictEnabled),
          fuzzyEnabled: false,
          primaryContact: normalizeContactForForm(row?.primaryContact),
          secondaryContact: normalizeContactForForm(row?.secondaryContact),
          delay: notificationSecondsToTime(row?.delay),
        })
      );
    } else if (kind === "activities") {
      const activityName = pick(
        row?.activity,
        row?.act,
        row?.Activity,
        row?.raw?.activity,
        row?.raw?.Activity,
        row?.parameter,
        row?.device,
        row?.name,
        row?.raw?.name
      );
      const completedValue = toNotificationBoolean(
        pick(row?.completed, row?.raw?.completed, row?.Completed, row?.raw?.Completed)
      );
      const conditionsValue = toNotificationBoolean(
        pick(
          row?.conditionsNotMet,
          row?.raw?.conditionsNotMet,
          row?.completedConditionsNotMet,
          row?.raw?.completedConditionsNotMet
        )
      );
      const notCompletedValue = toNotificationBoolean(
        pick(
          row?.notCompleted,
          row?.raw?.notCompleted,
          row?.NotCompleted,
          row?.raw?.NotCompleted,
          row?.incomplete,
          row?.raw?.incomplete,
          row?.Incomplete,
          row?.raw?.Incomplete,
          row?.pending,
          row?.raw?.pending,
          row?.Pending,
          row?.raw?.Pending
        )
      );

      setNotificationForm(
        createNotificationForm({
          id: effectiveId,
          backendId,
          displayId,
          kind,
          activity: activityName || "",
          parameter: activityName || "",
          broilerId: broilerIdValue ?? facilityId ?? "",
          completed: completedValue,
          conditionsNotMet: conditionsValue,
          notCompleted: notCompletedValue,
          primaryContact: normalizeContactForForm(row?.primaryContact),
          secondaryContact: normalizeContactForForm(row?.secondaryContact),
          delay: notificationSecondsToTime(row?.delay),
        })
      );
    } else {
      setNotificationForm(
        createNotificationForm({
          id: effectiveId,
          backendId,
          displayId,
          kind: "environmental",
          broilerId: broilerIdValue ?? facilityId ?? "",
          deviceId: row?.device ?? "",
          deviceType: row?.deviceType ?? "",
          parameter: row?.parameter ?? "",
          strictUpper:
            row?.strictUpper == null || row?.strictUpper === "" ? "" : `${row.strictUpper}`,
          strictLower:
            row?.strictLower == null || row?.strictLower === "" ? "" : `${row.strictLower}`,
          strictEnabled: toNotificationBoolean(row?.strictEnabled),
          fuzzyUpper: row?.fuzzyUpper == null || row?.fuzzyUpper === "" ? "" : `${row.fuzzyUpper}`,
          fuzzyLower: row?.fuzzyLower == null || row?.fuzzyLower === "" ? "" : `${row.fuzzyLower}`,
          fuzzyEnabled: toNotificationBoolean(row?.fuzzyEnabled),
          enabled: toNotificationBoolean(row?.strictEnabled),
          primaryContact: normalizeContactForForm(row?.primaryContact),
          secondaryContact: normalizeContactForForm(row?.secondaryContact),
          delay: row?.delay == null || row?.delay === "" ? "" : `${row.delay}`,
        })
      );
    }

    setNotificationEditOpen(true);
  };

  const closeNotificationEdit = () => {
    setNotificationEditOpen(false);
    setNotificationForm(createNotificationForm());
  };

  const handleNotificationFieldChange = (field) => (event) => {
    const value = event?.target?.value ?? "";
    setNotificationForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNotificationBooleanChange = (field) => (event) => {
    const checked = event?.target?.checked ?? false;
    setNotificationForm((prev) => ({ ...prev, [field]: checked }));
  };

  const submitNotificationForm = async () => {
    if (notificationForm.id == null) {
      closeNotificationEdit();
      return;
    }
    setNotificationSaving(true);
    try {
      if (notificationForm.kind === "performance") {
        const rawId = notificationForm.backendId ?? notificationForm.id;
        const idValue = toNumberOrNull(rawId) ?? rawId;
        const primaryContactValue = normalizeContactForPayload(notificationForm.primaryContact);
        const secondaryContactValue = normalizeContactForPayload(notificationForm.secondaryContact);
        const notificationTime = notificationTimeToSeconds(notificationForm.delay);
        if (notificationForm.enabled && notificationTime == null) {
          throw new Error("Invalid notification time");
        }
        const payload = {
          id: idValue,
          kpi: notificationForm.kpi,
          deviationP: toNumberOrNull(notificationForm.deviationP),
          enabled: !!notificationForm.enabled,
          primaryContact: primaryContactValue,
          secondaryContact: secondaryContactValue,
          delay: notificationTime,
        };
        const broilerIdValue =
          toNumberOrNull(notificationForm.broilerId) ??
          toNumberOrNull(facilityId) ??
          notificationForm.broilerId ??
          facilityId;
        if (broilerIdValue != null && `${broilerIdValue}`.trim().length > 0) {
          payload.broilerId = broilerIdValue;
        }
        const facilityTarget =
          (facilityId != null && `${facilityId}`.trim().length > 0
            ? facilityId
            : notificationForm.broilerId) ?? null;
        if (facilityTarget == null || `${facilityTarget}`.trim().length === 0) {
          throw new Error("Missing facility ID for KPI notification update");
        }
        await axios.post(updateKpiNotificationsUrl(facilityTarget), payload, {
          headers: { "Content-Type": "application/json" },
          omnioOffline: { skipAuthHeader: true },
        });
      } else if (notificationForm.kind === "activities") {
        const rawId = notificationForm.backendId ?? notificationForm.id;
        const idValue = toNumberOrNull(rawId) ?? rawId;
        const primaryContactValue = normalizeContactForPayload(notificationForm.primaryContact);
        const secondaryContactValue = normalizeContactForPayload(notificationForm.secondaryContact);
        const activityValue = (notificationForm.activity ?? "").trim();
        const actPayload =
          activityValue.length > 0 ? activityValue : notificationForm.activity ?? "";
        const notificationTime = notificationTimeToSeconds(notificationForm.delay);
        if (
          (notificationForm.completed ||
            notificationForm.conditionsNotMet ||
            notificationForm.notCompleted) &&
          notificationTime == null
        ) {
          throw new Error("Invalid notification time");
        }
        const payload = {
          id: idValue,
          act: actPayload,
          completed: !!notificationForm.completed,
          conditionsNotMet: !!notificationForm.conditionsNotMet,
          notCompleted: !!notificationForm.notCompleted,
          primaryContact: primaryContactValue,
          secondaryContact: secondaryContactValue,
          delay: notificationTime,
        };
        const broilerCandidate =
          notificationForm.broilerId != null && `${notificationForm.broilerId}`.trim().length > 0
            ? notificationForm.broilerId
            : facilityId;
        const broilerCandidateStr = broilerCandidate == null ? "" : `${broilerCandidate}`.trim();
        if (broilerCandidateStr.length > 0) {
          const broilerCandidateNum = toNumberOrNull(broilerCandidateStr);
          payload.broilerID = broilerCandidateNum ?? broilerCandidateStr;
        }
        await axios.post(updateActNotificationsUrl(), payload, {
          headers: { "Content-Type": "application/json" },
          omnioOffline: { skipAuthHeader: true },
        });
      } else {
        const primaryContactValue = normalizeContactForPayload(notificationForm.primaryContact);
        const secondaryContactValue = normalizeContactForPayload(notificationForm.secondaryContact);
        const rawDeviceDataId = notificationForm.backendId ?? notificationForm.id;
        const deviceDataId = toNumberOrNull(rawDeviceDataId);
        const frequencyMinutes = toNumberOrNull(notificationForm.delay);
        if (deviceDataId == null) {
          throw new Error("Missing device data ID for Environmental notification update");
        }
        if (
          (notificationForm.strictEnabled || notificationForm.fuzzyEnabled) &&
          (!Number.isInteger(frequencyMinutes) || frequencyMinutes < 1)
        ) {
          throw new Error("Invalid Environmental notification frequency");
        }
        const payload = {
          deviceDataID: deviceDataId,
          highLimit: toNumberOrNull(notificationForm.strictUpper),
          lowLimit: toNumberOrNull(notificationForm.strictLower),
          thresholdEnabled: !!notificationForm.strictEnabled,
          fuzzyHigh: toNumberOrNull(notificationForm.fuzzyUpper),
          fuzzyLow: toNumberOrNull(notificationForm.fuzzyLower),
          primaryContact: primaryContactValue,
          secondaryContact: secondaryContactValue,
          secondaryContactDelay: frequencyMinutes,
          fuzzyLimits: !!notificationForm.fuzzyEnabled,
        };
        const response = await axios.post(updateDeviceUrl(deviceDataId), payload, {
          headers: { "Content-Type": "application/json" },
          omnioOffline: { skipAuthHeader: true },
        });
        if (response.data === false) {
          throw new Error("Environmental notification update was rejected");
        }

        const triggerUrl = environmentalAlertsTriggerUrl(facilityId);
        if (triggerUrl) {
          try {
            await axios.post(triggerUrl, null, {
              headers: { "Content-Type": "application/json" },
              omnioOffline: { skipAuthHeader: true },
              withCredentials: false,
            });
          } catch (triggerError) {
            console.error(
              "Environmental notification was saved, but alert evaluation could not be triggered",
              triggerError
            );
          }
        }
      }
      setNotificationSaving(false);
      setNotificationEditOpen(false);
      setNotificationForm(createNotificationForm());
      setSnackMessage("Notification updated successfully");
      setSnackOpen(true);
      setNotificationReload((n) => n + 1);
    } catch (error) {
      setNotificationSaving(false);
      const message =
        error?.message === "Missing facility ID for KPI notification update"
          ? "Select a facility before updating KPI notifications."
          : error?.message === "Missing device data ID for Environmental notification update"
          ? "This Environmental notification does not have a valid device data ID."
          : error?.message === "Invalid Environmental notification frequency"
          ? "Enter a whole-number frequency of at least 1 minute."
          : error?.message === "Invalid notification time"
          ? "Select a valid notification time (UTC+2)."
          : "Failed to update notification";
      setSnackMessage(message);
      setSnackOpen(true);
    }
  };

  const toIsoDateTimeFromInput = (val) => {
    if (!val) return "";
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(val)) return `${val}:00`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(val)) return val;
    const d = new Date(val);
    if (!isNaN(d.getTime())) return d.toISOString();
    return val;
  };

  const readCachedCollection = (url) => {
    if (typeof window === "undefined") return null;
    try {
      const key = `OmnioCache:get:${url}?`;
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const data = parsed?.data;
      if (Array.isArray(data)) return data;
      if (Array.isArray(parsed)) return parsed;
      return null;
    } catch (error) {
      console.warn("Unable to read cached collection for", url, error);
      return null;
    }
  };

  const writeCachedCollection = (url, data) => {
    if (typeof window === "undefined") return;
    try {
      const key = `OmnioCache:get:${url}?`;
      window.localStorage.setItem(key, JSON.stringify({ data }));
    } catch (error) {
      console.warn("Unable to write cached collection for", url, error);
    }
  };

  const getConsumptionBinCacheKey = (targetFacilityId, targetCycleId) =>
    `OmnioConsumptionBinCache:${targetFacilityId ?? "na"}:${targetCycleId ?? "na"}`;

  const readConsumptionBinCache = (targetFacilityId, targetCycleId) => {
    if (typeof window === "undefined") return { byId: {}, bySignature: {} };
    try {
      const key = getConsumptionBinCacheKey(targetFacilityId, targetCycleId);
      const raw = window.localStorage.getItem(key);
      if (!raw) return { byId: {}, bySignature: {} };
      const parsed = JSON.parse(raw);
      return {
        byId: parsed?.byId && typeof parsed.byId === "object" ? parsed.byId : {},
        bySignature:
          parsed?.bySignature && typeof parsed.bySignature === "object" ? parsed.bySignature : {},
      };
    } catch (error) {
      console.warn("Unable to read consumption bin cache", error);
      return { byId: {}, bySignature: {} };
    }
  };

  const writeConsumptionBinCache = (targetFacilityId, targetCycleId, next) => {
    if (typeof window === "undefined") return;
    try {
      const key = getConsumptionBinCacheKey(targetFacilityId, targetCycleId);
      window.localStorage.setItem(key, JSON.stringify(next));
    } catch (error) {
      console.warn("Unable to write consumption bin cache", error);
    }
  };

  const updateConsumptionBinCache = (targetFacilityId, targetCycleId, entry) => {
    if (!entry || (entry.id == null && !entry.signature)) return;
    const cache = readConsumptionBinCache(targetFacilityId, targetCycleId);
    const payload = {
      bin: normalizeBinSelection(entry.bin ?? entry.binSelection),
      bin1Bal: entry.bin1Bal ?? entry.bin1_bal ?? entry.bin_1_bal ?? null,
      bin2Bal: entry.bin2Bal ?? entry.bin2_bal ?? entry.bin_2_bal ?? null,
      feedpaperBal:
        entry.feedpaperBal ??
        entry.feedpaper_bal ??
        entry.feed_paper_bal ??
        entry.FeedpaperBal ??
        null,
    };
    if (entry.id != null) {
      cache.byId[`${entry.id}`] = payload;
    }
    if (entry.signature) {
      cache.bySignature[entry.signature] = payload;
    }
    writeConsumptionBinCache(targetFacilityId, targetCycleId, cache);
  };

  const OFFLINE_NS_MORTALITIES = "Mortalities";
  const OFFLINE_NS_PLACEMENT = "PlacementWeights";
  const OFFLINE_NS_CYCLE_WEIGHTS = "CycleWeights";
  const OFFLINE_NS_CONSUMPTION = "Consumption";
  const OFFLINE_NS_CATCH = "CatchWeights";
  const OFFLINE_QUEUE_STORAGE_KEY = "OmnioOfflineQueue";

  const readOfflineQueue = () => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(OFFLINE_QUEUE_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn("Unable to read offline queue", error);
      return [];
    }
  };

  const writeOfflineQueue = (queue) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(OFFLINE_QUEUE_STORAGE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.warn("Unable to update offline queue", error);
    }
  };

  const mergeOfflineRecords = (cachedRows, offlineRecords) => {
    const offlineArray = Array.isArray(offlineRecords) ? offlineRecords : [];
    const offlineRows = offlineArray
      .filter((entry) => !entry?.__deleted)
      .map((entry) => ({ ...entry, __isOffline: true }));
    const deletedIds = new Set(
      offlineArray.filter((entry) => entry?.__deleted).map((entry) => `${entry.id}`)
    );
    const offlineIds = new Set(offlineRows.map((entry) => `${entry.id}`));
    const filteredCached = (Array.isArray(cachedRows) ? cachedRows : []).filter(
      (entry) => !offlineIds.has(`${entry.id}`) && !deletedIds.has(`${entry.id}`)
    );
    return [...offlineRows, ...filteredCached];
  };

  const mapPlacementRows = (data, targetFacilityId) => {
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.value)
      ? data.value
      : [];
    return list
      .map((row) => ({
        id: pick(row?.id, row?.ID),
        broilerId: pick(row?.broilerId, row?.BroilerID, row?.facilityId, row?.FacilityID),
        totalBirdWeight: pick(row?.totalBirdWeight, row?.TotalBirdWeight, row?.total_weight),
        averageBox: pick(row?.averageBox, row?.avgBoxWeight, row?.average_box_weight),
        noOfBirdsPerBox: pick(row?.noOfBirdsPerBox, row?.birds, row?.total_birds),
        date: pick(row?.date, row?.Date, row?.dateTime, row?.DateTime),
      }))
      .filter((entry) => `${entry.broilerId}` === `${targetFacilityId}`);
  };

  const mapCycleWeightRows = (data, targetFacilityId, targetCycleId) => {
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.value)
      ? data.value
      : [];
    return list
      .map((row) => ({
        id: pick(row?.id, row?.ID),
        broilerId: pick(row?.broilerID, row?.BroilerID, row?.facilityId, row?.FacilityID),
        cycleId: pick(row?.cycleId, row?.cycleID, row?.CycleId, row?.CycleID),
        weightBack: row?.weightBack ?? null,
        weightBackR: row?.weightBackR ?? null,
        weightCenter: row?.weightCenter ?? null,
        weightCenterR: row?.weightCenterR ?? null,
        weightFront: row?.weightFront ?? null,
        weightFrontR: row?.weightFrontR ?? null,
        sampleSize: row?.sampleSize ?? null,
        weightAverage: pick(
          row?.weightAverage,
          row?.WeightAverage,
          row?.weight_average,
          row?.averageWeight,
          row?.avgWeight
        ),
        dateTime: pick(row?.dateTime, row?.date, row?.DateTime, row?.Date),
      }))
      .filter(
        (entry) =>
          `${entry.broilerId}` === `${targetFacilityId}` &&
          (targetCycleId == null || `${entry.cycleId}` === `${targetCycleId}`)
      );
  };

  const mapConsumptionRows = (data, targetFacilityId, targetCycleId, type = "feed") => {
    const binCache = readConsumptionBinCache(targetFacilityId, targetCycleId);
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.value)
      ? data.value
      : [];
    return list
      .map((row) => {
        const id = pick(row?.id, row?.ID);
        const feedKg = pick(
          row?.feedKg,
          row?.FeedKg,
          row?.feedKG,
          row?.feed_kg,
          row?.feed,
          row?.Feed
        );
        const dateTime = pick(row?.dateTime, row?.DateTime, row?.date, row?.Date);
        const signature = `${dateTime ?? ""}|${feedKg ?? ""}`;
        const cached = id != null ? binCache.byId?.[`${id}`] : binCache.bySignature?.[signature];
        const bin = normalizeBinSelection(
          pick(row?.bin, row?.Bin, row?.binSelection, row?.BinSelection) ??
            getBinSelectionFromFlags(row) ??
            cached?.bin
        );
        const bin1Bal = pick(
          row?.bin1Bal,
          row?.bin1_bal,
          row?.bin_1_bal,
          row?.Bin1Bal,
          row?.Bin_1_Bal
        );
        const bin2Bal = pick(
          row?.bin2Bal,
          row?.bin2_bal,
          row?.bin_2_bal,
          row?.Bin2Bal,
          row?.Bin_2_Bal
        );
        const feedpaperBal = pick(
          row?.feedpaperBal,
          row?.feedpaper_bal,
          row?.feed_paper_bal,
          row?.FeedpaperBal,
          row?.FeedPaperBal,
          row?.Feed_Paper_Bal
        );
        const stage = pick(
          row?.stage,
          row?.Stage,
          row?.stageName,
          row?.StageName,
          row?.stageId,
          row?.StageId,
          row?.StageID
        );
        const feedName = pick(
          row?.feedName,
          row?.FeedName,
          row?.feedname,
          row?.Feedname,
          row?.feedId,
          row?.FeedId,
          row?.feed_id
        );
        const feedSpendId = pick(
          row?.feedSpendId,
          row?.FeedSpendId,
          row?.feedSpendID,
          row?.FeedSpendID,
          row?.feed_spend_id
        );
        return {
          id,
          broilerId: pick(
            row?.broilerID,
            row?.broilerId,
            row?.BroilerID,
            row?.facilityId,
            row?.facilityID,
            row?.FacilityID
          ),
          farmId: pick(row?.farmId, row?.farmID, row?.FarmId, row?.FarmID),
          cycleId: pick(row?.cycleId, row?.cycleID, row?.CycleId, row?.CycleID),
          stage: stage != null ? `${stage}` : "",
          feedName: feedName != null ? `${feedName}` : "",
          feedSpendId,
          feedKg,
          waterSource: pick(
            row?.waterSource,
            row?.WaterSource,
            row?.sourcePercent,
            row?.SourcePercent,
            row?.sourcePercentage,
            row?.SourcePercentage,
            row?.source,
            row?.Source
          ),
          waterReading: pick(row?.waterReading, row?.WaterReading, row?.reading, row?.Reading),
          waterL: pick(
            row?.waterConsumption,
            row?.WaterConsumption,
            row?.waterL,
            row?.WaterL,
            row?.water_l,
            row?.water,
            row?.Water
          ),
          bin,
          bin1Bal: bin1Bal ?? cached?.bin1Bal ?? null,
          bin2Bal: bin2Bal ?? cached?.bin2Bal ?? null,
          feedpaperBal: feedpaperBal ?? cached?.feedpaperBal ?? null,
          dateTime,
          consType: type,
        };
      })
      .filter((entry) => {
        if (entry.broilerId != null && `${entry.broilerId}` !== `${targetFacilityId}`) return false;
        if (targetCycleId == null) return true;
        if (entry.cycleId == null) return true;
        return `${entry.cycleId}` === `${targetCycleId}`;
      });
  };

  const getConsumptionType = (entry, fallback = "feed") => {
    if (!entry) return fallback;
    if (entry.consType === "water" || entry.consType === "feed") return entry.consType;
    const feedCandidates = [
      entry?.feedKg,
      entry?.FeedKg,
      entry?.feed,
      entry?.Feed,
      entry?.feed_kg,
      entry?.feedKG,
    ];
    if (
      feedCandidates.some((value) => value !== undefined && value !== null && `${value}` !== "")
    ) {
      return "feed";
    }
    const waterCandidates = [
      entry?.waterReading,
      entry?.WaterReading,
      entry?.waterL,
      entry?.WaterL,
      entry?.water,
      entry?.Water,
      entry?.waterConsumption,
      entry?.WaterConsumption,
    ];
    if (
      waterCandidates.some((value) => value !== undefined && value !== null && `${value}` !== "")
    ) {
      return "water";
    }
    return fallback;
  };

  const normalizeConsumptionRows = (rows, fallback = "feed") => {
    if (!Array.isArray(rows)) return [];
    return rows.map((entry) => {
      if (!entry) return entry;
      const type = getConsumptionType(entry, fallback);
      return entry.consType === type ? entry : { ...entry, consType: type };
    });
  };

  const applyConsumptionBinMemory = (rows, existingRows) => {
    if (!Array.isArray(rows) || !Array.isArray(existingRows)) return rows;
    const existingById = new Map(
      existingRows
        .filter((entry) => entry && entry.id != null)
        .map((entry) => [`${entry.id}`, entry])
    );
    return rows.map((row) => {
      if (!row || getConsumptionType(row) === "water") return row;
      const hasBin = row.bin != null && `${row.bin}` !== "";
      const hasBalances = row.bin1Bal != null || row.bin2Bal != null || row.feedpaperBal != null;
      if (hasBin && hasBalances) return row;
      let match = null;
      if (row.id != null) {
        match = existingById.get(`${row.id}`);
      }
      if (!match) {
        match = existingRows.find(
          (entry) =>
            entry &&
            getConsumptionType(entry) !== "water" &&
            `${entry.feedKg ?? ""}` === `${row.feedKg ?? ""}` &&
            `${entry.dateTime ?? ""}` === `${row.dateTime ?? ""}`
        );
      }
      if (!match) return row;
      return {
        ...row,
        bin: hasBin ? row.bin : normalizeBinSelection(match.bin ?? match.binSelection),
        bin1Bal: row.bin1Bal ?? match.bin1Bal,
        bin2Bal: row.bin2Bal ?? match.bin2Bal,
        feedpaperBal: row.feedpaperBal ?? match.feedpaperBal,
      };
    });
  };

  const currentBin1Balance = useMemo(() => {
    const fallbackSetupTotal = (Array.isArray(setupFeedRows) ? setupFeedRows : []).reduce(
      (sum, row) => sum + (toNumberOrNull(row?.bin1) ?? 0),
      0
    );
    return toNumberOrNull(feedBalance.bin1) ?? fallbackSetupTotal;
  }, [feedBalance.bin1, setupFeedRows]);

  const currentBin1Consumption = useMemo(
    () => toNumberOrNull(feedBalance.bin1Consumption),
    [feedBalance.bin1Consumption]
  );

  const currentFeedpaperBalance = useMemo(() => {
    const setupTotal = (Array.isArray(setupFeedRows) ? setupFeedRows : []).reduce(
      (sum, row) => sum + (toNumberOrNull(row?.feedpaper) ?? 0),
      0
    );
    const consumedTotal = (Array.isArray(consRows) ? consRows : []).reduce((sum, row) => {
      if (!row || getConsumptionType(row) === "water") return sum;
      if (normalizeBinSelection(row?.bin ?? row?.binSelection) !== "feedpaper") return sum;
      return sum + (toNumberOrNull(row?.feedKg) ?? 0);
    }, 0);
    return setupTotal - consumedTotal;
  }, [setupFeedRows, consRows]);

  const currentBin2Balance = useMemo(() => {
    const fallbackSetupTotal = (Array.isArray(setupFeedRows) ? setupFeedRows : []).reduce(
      (sum, row) => sum + (toNumberOrNull(row?.bin2) ?? 0),
      0
    );
    return toNumberOrNull(feedBalance.bin2) ?? fallbackSetupTotal;
  }, [feedBalance.bin2, setupFeedRows]);

  const currentBin2Consumption = useMemo(
    () => toNumberOrNull(feedBalance.bin2Consumption),
    [feedBalance.bin2Consumption]
  );

  const currentBin1Total = useMemo(
    () =>
      toNumberOrNull(feedBalance.bin1Total) ??
      (toNumberOrNull(currentBin1Consumption) ?? 0) + (toNumberOrNull(currentBin1Balance) ?? 0),
    [feedBalance.bin1Total, currentBin1Consumption, currentBin1Balance]
  );

  const currentBin2Total = useMemo(
    () =>
      toNumberOrNull(feedBalance.bin2Total) ??
      (toNumberOrNull(currentBin2Consumption) ?? 0) + (toNumberOrNull(currentBin2Balance) ?? 0),
    [feedBalance.bin2Total, currentBin2Consumption, currentBin2Balance]
  );

  const loadFeedBalance = useCallback(async () => {
    if (facilityId == null || cycleId == null) {
      const emptyBalance = {
        bin1: null,
        bin2: null,
        bin1Consumption: null,
        bin2Consumption: null,
        bin1Total: null,
        bin2Total: null,
      };
      setFeedBalance(emptyBalance);
      return emptyBalance;
    }

    const url = getFeedBalanceUrl(facilityId, cycleId);
    const cached = normalizeFeedBalance(readCachedCollection(url));
    if (
      cached.bin1 != null ||
      cached.bin2 != null ||
      cached.bin1Total != null ||
      cached.bin2Total != null
    ) {
      setFeedBalance(cached);
    }

    try {
      const response = await axios.get(url);
      writeCachedCollection(url, response?.data);
      const normalized = normalizeFeedBalance(response?.data);
      setFeedBalance(normalized);
      return normalized;
    } catch (error) {
      if (
        cached.bin1 != null ||
        cached.bin2 != null ||
        cached.bin1Total != null ||
        cached.bin2Total != null
      ) {
        return cached;
      }
      console.error("Failed to load feed balance", error);
      const emptyBalance = {
        bin1: null,
        bin2: null,
        bin1Consumption: null,
        bin2Consumption: null,
        bin1Total: null,
        bin2Total: null,
      };
      setFeedBalance(emptyBalance);
      return emptyBalance;
    }
  }, [facilityId, cycleId]);

  const loadFeedBins = useCallback(async () => {
    if (facilityId == null || cycleId == null) return null;
    try {
      const response = await axios.get(getFeedBinsUrl(facilityId, cycleId));
      const normalized = normalizeFeedBins(response?.data);
      setFeedBins(normalized);
      return normalized;
    } catch (error) {
      console.error("Failed to load feed bins", error);
      return null;
    }
  }, [facilityId, cycleId]);

  const loadSetupFeedRows = useCallback(async () => {
    if (facilityId == null || cycleId == null) return [];
    const url = feedScheduleUrl(facilityId, cycleId);
    try {
      const response = await axios.get(url);
      writeCachedCollection(url, response?.data);
      const normalized = mapSetupFeedRows(response?.data, facilityId, cycleId);
      setSetupFeedRows(normalized);
      return normalized;
    } catch (error) {
      const cached = mapSetupFeedRows(readCachedCollection(url), facilityId, cycleId);
      if (cached.length > 0) {
        setSetupFeedRows(cached);
        return cached;
      }
      console.error("Failed to load setup feed rows", error);
      setSetupFeedRows([]);
      return [];
    }
  }, [facilityId, cycleId]);

  const applyFeedDictionary = useCallback((dictData) => {
    const dictSets = normalizeFeedDictionary(dictData);
    setFeedStageOptions(dictSets.stage.list);
    setFeedStageMap(dictSets.stage.map);
    setFeedNameOptions(dictSets.feedName.list);
    setFeedNameMap(dictSets.feedName.map);
    return dictSets;
  }, []);

  const loadFeedDictionary = useCallback(async () => {
    if (farmId == null) {
      setFeedStageOptions([]);
      setFeedStageMap({});
      setFeedNameOptions([]);
      setFeedNameMap({});
      return null;
    }

    const url = broilerDataByFarmUrl(farmId);
    const cached = readCachedCollection(url) ?? [];
    if (cached.length > 0) applyFeedDictionary(cached);

    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    if (offline) return cached.length > 0 ? normalizeFeedDictionary(cached) : null;

    try {
      const response = await axios.get(url);
      writeCachedCollection(url, response?.data);
      return applyFeedDictionary(response?.data);
    } catch {
      return cached.length > 0 ? normalizeFeedDictionary(cached) : null;
    }
  }, [applyFeedDictionary, farmId]);

  const fetchConsumptionRowsFromServer = useCallback(async () => {
    if (facilityId == null || cycleId == null) return [];
    const canLoadUtilities = farmId != null && `${farmId}` !== "";
    const utilitiesEndpoint = canLoadUtilities ? utilitiesUrl(farmId, facilityId, cycleId) : null;
    const [feedResult, waterResult, utilitiesResult] = await Promise.allSettled([
      axios.get(waterFeedDataUrl(facilityId, cycleId)),
      axios.get(waterDataUrl(facilityId, cycleId)),
      utilitiesEndpoint ? axios.get(utilitiesEndpoint) : Promise.resolve({ data: [] }),
    ]);

    const feedRows =
      feedResult.status === "fulfilled"
        ? mapConsumptionRows(feedResult.value.data, facilityId, cycleId, "feed")
        : [];
    if (feedResult.status === "fulfilled") {
      writeCachedCollection(waterFeedDataUrl(facilityId, cycleId), feedResult.value.data);
    }
    if (feedResult.status === "rejected") {
      console.error("Failed to load feed consumption data", feedResult.reason);
    }

    let waterRows =
      waterResult.status === "fulfilled"
        ? mapConsumptionRows(waterResult.value.data, facilityId, cycleId, "water")
        : [];
    if (waterResult.status === "fulfilled") {
      writeCachedCollection(waterDataUrl(facilityId, cycleId), waterResult.value.data);
    }
    if (waterResult.status === "rejected") {
      console.error("Failed to load water consumption data", waterResult.reason);
    }

    const utilitiesRows =
      utilitiesResult.status === "fulfilled" && Array.isArray(utilitiesResult.value.data)
        ? utilitiesResult.value.data
        : [];
    if (utilitiesResult.status === "fulfilled" && utilitiesEndpoint) {
      writeCachedCollection(utilitiesEndpoint, utilitiesResult.value.data);
    }
    if (utilitiesResult.status === "rejected") {
      console.error("Failed to load utilities data", utilitiesResult.reason);
    }
    const utilitiesByDate = new Map(
      utilitiesRows.map((row) => [
        `${pick(row?.date, row?.Date, row?.dateTime, row?.DateTime) ?? ""}`.slice(0, 10),
        row,
      ])
    );
    waterRows = waterRows.map((row) => {
      const dateKey = toLocalDateTimeInput(row?.dateTime).slice(0, 10);
      const utility = utilitiesByDate.get(dateKey);
      if (!utility) return row;
      return {
        ...row,
        utilityId: pick(utility?.id, utility?.ID, utility?.Id),
        waterSource: pick(utility?.water, utility?.Water),
      };
    });

    if (
      feedRows.length === 0 &&
      waterRows.length === 0 &&
      feedResult.status === "rejected" &&
      waterResult.status === "rejected"
    ) {
      throw feedResult.reason || waterResult.reason || new Error("Failed to load consumption data");
    }

    const combinedRows = [...feedRows, ...waterRows];
    return combineServerRowsWithOffline(OFFLINE_NS_CONSUMPTION, combinedRows, matchConsumptionRow);
  }, [facilityId, cycleId, farmId]);

  const refreshConsumptionFromServer = useCallback(async () => {
    const mergedRows = await fetchConsumptionRowsFromServer();
    setConsRows((prev) => normalizeConsumptionRows(applyConsumptionBinMemory(mergedRows, prev)));
  }, [fetchConsumptionRowsFromServer]);

  const refreshAfterFeedReadingSave = useCallback(async () => {
    await Promise.allSettled([refreshConsumptionFromServer(), loadFeedBins(), loadSetupFeedRows()]);
    await loadFeedBalance();
  }, [refreshConsumptionFromServer, loadFeedBalance, loadFeedBins, loadSetupFeedRows]);

  const mapMortalitiesToRows = (data, mortEvents, cullEvents) => {
    const mortEventMap = new Map(
      mortEvents.map((e) => [String(e?.id ?? e?.ID ?? ""), e?.eventName ?? e?.name ?? ""])
    );
    const cullEventMap = new Map(
      cullEvents.map((e) => [String(e?.id ?? e?.ID ?? ""), e?.eventName ?? e?.name ?? ""])
    );

    return data
      .map((row) => {
        const facilityRef = pick(row?.briolerID, row?.broilerID, row?.broilerId, row?.facilityID);
        const mortalities = pick(row?.fatalities, row?.mortalities, row?.deaths);
        const culls = pick(row?.culls, row?.Culls);
        const date = pick(row?.dateTime, row?.DateTime, row?.date, row?.Date);
        const id = pick(row?.id, row?.ID);

        const mortEventId = pick(
          row?.eventMortalitiesID,
          row?.eventMortalityID,
          row?.mortEventId,
          row?.mortalitiesEventId
        );
        const cullEventId = pick(row?.eventCullsID, row?.cullEventId, row?.cullsEventId);

        const mortDesc = mortEventId != null ? mortEventMap.get(String(mortEventId)) || "" : "";
        const cullDesc = cullEventId != null ? cullEventMap.get(String(cullEventId)) || "" : "";

        return {
          id,
          facilityRef,
          mortalities,
          mortDesc,
          eventMortalitiesID: mortEventId ?? "",
          culls,
          cullDesc,
          eventCullsID: cullEventId ?? "",
          date,
        };
      })
      .filter((entry) => `${entry.facilityRef}` === `${facilityId}`);
  };

  const refreshMortalities = useCallback(async () => {
    if (facilityId == null || cycleId == null) return;
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    if (offline) {
      const cachedMortalities = readCachedCollection(mortalitiesUrl(facilityId, cycleId));
      const cachedMortEvents = readCachedCollection(mortEventsUrl());
      const cachedCullEvents = readCachedCollection(cullEventsUrl());

      const mortEvents = Array.isArray(cachedMortEvents) ? cachedMortEvents : [];
      const cullEvents = Array.isArray(cachedCullEvents) ? cachedCullEvents : [];
      const cachedRows = Array.isArray(cachedMortalities)
        ? mapMortalitiesToRows(cachedMortalities, mortEvents, cullEvents)
        : [];
      const optimisticStoredRaw = readOfflineRecords(OFFLINE_NS_MORTALITIES, facilityId, cycleId);
      const optimisticRows = optimisticStoredRaw
        .filter((row) => !row?.__deleted)
        .map((row) => ({
          ...row,
          __isOffline: true,
        }));
      const deletedIds = new Set(
        optimisticStoredRaw.filter((row) => row?.__deleted).map((row) => `${row.id}`)
      );
      const optimisticIds = new Set(optimisticRows.map((row) => `${row.id}`));
      const filteredCached = cachedRows.filter(
        (row) => !optimisticIds.has(`${row.id}`) && !deletedIds.has(`${row.id}`)
      );
      const combined = [...optimisticRows, ...filteredCached];

      setMortRows(combined);
      return;
    }
    try {
      const [mortRes, mortEventsRes, cullEventsRes] = await Promise.all([
        axios.get(mortalitiesUrl(facilityId, cycleId)),
        axios.get(mortEventsUrl()),
        axios.get(cullEventsUrl()),
      ]);

      const data = Array.isArray(mortRes.data) ? mortRes.data : [];
      const mortEvents = Array.isArray(mortEventsRes.data) ? mortEventsRes.data : [];
      const cullEvents = Array.isArray(cullEventsRes.data) ? cullEventsRes.data : [];
      writeCachedCollection(mortalitiesUrl(facilityId, cycleId), mortRes.data);
      writeCachedCollection(mortEventsUrl(), mortEventsRes.data);
      writeCachedCollection(cullEventsUrl(), cullEventsRes.data);

      const rows = mapMortalitiesToRows(data, mortEvents, cullEvents);
      const merged = combineServerRowsWithOffline(OFFLINE_NS_MORTALITIES, rows, matchMortalityRow);
      setMortRows(merged);
    } catch (error) {
      console.warn("Unable to refresh mortalities", error);
    }
  }, [facilityId, cycleId]);

  useEffect(() => {
    if (openDialog !== "mortalities") return;
    refreshMortalities();
  }, [openDialog, refreshMortalities]);
  useEffect(() => {
    if (typeof window === "undefined") return () => {};

    const handleOfflineSync = () => {
      setOfflineSyncTick((tick) => tick + 1);
      if (openDialog === "mortalities") {
        refreshMortalities();
      }
    };

    window.addEventListener("omnio:offline-sync-complete", handleOfflineSync);
    return () => window.removeEventListener("omnio:offline-sync-complete", handleOfflineSync);
  }, [openDialog, refreshMortalities]);

  useEffect(() => {
    if (
      openDialog === "consumption" &&
      (consActiveView === "feed" || consActiveView === "readings")
    ) {
      loadFeedBalance();
      loadFeedBins();
      loadSetupFeedRows();
    }
  }, [openDialog, consActiveView, loadFeedBalance, loadFeedBins, loadSetupFeedRows]);

  // Load placement weights when Weights dialog open and tab is Placement
  useEffect(() => {
    const loadPlacement = async () => {
      if (
        openDialog !== "weights" ||
        weightTab !== "placement" ||
        facilityId == null ||
        cycleId == null
      )
        return;
      const hydrateFromCache = () => {
        const cached = readCachedCollection(placementWeightsUrl(facilityId, cycleId));
        const cachedRows = mapPlacementRows(cached, facilityId);
        const offlineRows = readOfflineRecords(OFFLINE_NS_PLACEMENT, facilityId, cycleId);
        setPlacementRows(mergeOfflineRecords(cachedRows, offlineRows));
      };

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        hydrateFromCache();
        return;
      }

      try {
        const res = await axios.get(placementWeightsUrl(facilityId, cycleId));
        writeCachedCollection(placementWeightsUrl(facilityId, cycleId), res.data);
        const rows = mapPlacementRows(res.data, facilityId);
        const merged = combineServerRowsWithOffline(OFFLINE_NS_PLACEMENT, rows, matchPlacementRow);
        setPlacementRows(merged);
      } catch (error) {
        console.error("Failed to load placement weights", error);
        hydrateFromCache();
      }
    };
    loadPlacement();
  }, [openDialog, weightTab, facilityId, cycleId, offlineSyncTick]);

  const submitCatchForm = async () => {
    if (facilityId == null || cycleId == null) {
      setSnackMessage("Please select a facility and cycle before saving catch records");
      setSnackOpen(true);
      return;
    }

    const existingIdValue = catchForm.id;
    const isTempId = existingIdValue != null && isTempIdentifier(existingIdValue);
    const optimisticId = getOptimisticId(existingIdValue);
    const resolvedExistingId = isTempId
      ? null
      : existingIdValue != null
      ? normalizePersistedId(existingIdValue)
      : null;
    const isNewRecord = resolvedExistingId == null && !isTempId;
    let effectiveDateInput = catchForm.date;
    if (isNewRecord || !effectiveDateInput) {
      effectiveDateInput = nowLocalDateTimeInput();
    }
    const payload = {
      noofbirds: catchForm.noofbirds === "" ? 0 : Number(catchForm.noofbirds),
      date: toIsoDateTimeFromInput(effectiveDateInput),
      broilerId: Number(facilityId),
      cycleId: cycleId == null ? null : Number(cycleId),
      totalweight: catchForm.totalweight === "" ? 0 : Number(catchForm.totalweight),
    };
    if (resolvedExistingId != null) {
      payload.id = resolvedExistingId;
      payload.ID = resolvedExistingId;
      payload.Id = resolvedExistingId;
    }
    const isPendingCreate = existingIdValue != null && isTempIdentifier(existingIdValue);
    if (isPendingCreate) {
      const targetId = applyOptimisticCatchUpsert(payload, optimisticId ?? null);
      handleQueuedOfflineRequest(OFFLINE_NS_CATCH, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_CATCH, targetId, payload);
      closeCatchEdit();
      setSnackMessage("Catch record saved locally. It will sync when you're online.");
      setSnackOpen(true);
      return;
    }

    let response;
    try {
      response = await axios.post(updateCatchDataUrl(), payload, {
        headers: { "Content-Type": "application/json" },
        omnioOffline: { skipAuthHeader: true },
      });
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        const targetId = applyOptimisticCatchUpsert(payload, optimisticId ?? null);
        handleQueuedOfflineRequest(OFFLINE_NS_CATCH, targetId);
        updateQueuePayloadForRecord(OFFLINE_NS_CATCH, targetId, payload);
        closeCatchEdit();
        setSnackMessage("Catch record saved locally. It will sync when you're online.");
        setSnackOpen(true);
        return;
      }
      console.error("Failed to save catch record", error);
      setSnackMessage("Failed to save catch record");
      setSnackOpen(true);
      return;
    }

    if (wasQueuedOffline(response)) {
      const targetId = applyOptimisticCatchUpsert(payload, optimisticId ?? null);
      handleQueuedOfflineRequest(OFFLINE_NS_CATCH, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_CATCH, targetId, payload);
      closeCatchEdit();
      setSnackMessage("Catch record saved locally. It will sync when you're online.");
      setSnackOpen(true);
      return;
    }

    try {
      const res = await axios.get(catchDataUrl(facilityId), {
        omnioOffline: { skipAuthHeader: true },
      });
      writeCachedCollection(catchDataUrl(facilityId), res.data);
      applyCatchRows(res.data);
    } catch (refreshError) {
      console.error("Failed to refresh catch data", refreshError);
    }

    closeCatchEdit();
    setSnackMessage("Catch saved successfully");
    setSnackOpen(true);
  };

  const performCatchDelete = async () => {
    const row = catchDeleteTarget;
    if (!row?.id) {
      setCatchDeleteOpen(false);
      setCatchDeleteTarget(null);
      return;
    }

    const idString = `${row.id}`;
    if (isTempIdentifier(idString) || row?.__pendingCreate) {
      applyOptimisticCatchDelete(row);
      setSnackMessage("Catch record removed locally.");
      setSnackOpen(true);
      setCatchDeleteOpen(false);
      setCatchDeleteTarget(null);
      return;
    }

    let response;
    try {
      response = await axios.post(
        removeCatchUrl(),
        { id: row.id },
        {
          headers: { "Content-Type": "application/json" },
          omnioOffline: { skipAuthHeader: true },
        }
      );
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        applyOptimisticCatchDelete(row);
        setSnackMessage("Catch record removal queued. It will sync when you're online.");
        setSnackOpen(true);
      } else {
        console.error("Failed to delete catch record", error);
        setSnackMessage("Failed to delete catch record");
        setSnackOpen(true);
      }
      setCatchDeleteOpen(false);
      setCatchDeleteTarget(null);
      return;
    }

    if (wasQueuedOffline(response)) {
      applyOptimisticCatchDelete(row);
      setSnackMessage("Catch record removal queued. It will sync when you're online.");
      setSnackOpen(true);
      setCatchDeleteOpen(false);
      setCatchDeleteTarget(null);
      return;
    }

    try {
      const res = await axios.get(catchDataUrl(facilityId), {
        omnioOffline: { skipAuthHeader: true },
      });
      writeCachedCollection(catchDataUrl(facilityId), res.data);
      applyCatchRows(res.data);
    } catch (refreshError) {
      console.error("Failed to refresh catch data", refreshError);
    }

    setSnackMessage("Catch record deleted successfully");
    setSnackOpen(true);
    setCatchDeleteOpen(false);
    setCatchDeleteTarget(null);
  };

  // Load catch weights
  useEffect(() => {
    const loadCatch = async () => {
      if (
        openDialog !== "weights" ||
        weightTab !== "catch" ||
        facilityId == null ||
        cycleId == null
      )
        return;
      const hydrateFromCache = () => {
        const cached = readCachedCollection(catchDataUrl(facilityId));
        applyCatchRows(cached);
      };

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        hydrateFromCache();
        return;
      }

      try {
        const res = await axios.get(catchDataUrl(facilityId), {
          omnioOffline: { skipAuthHeader: true },
        });
        writeCachedCollection(catchDataUrl(facilityId), res.data);
        applyCatchRows(res.data);
      } catch (error) {
        console.error("Failed to load catch data", error);
        hydrateFromCache();
      }
    };
    loadCatch();
  }, [openDialog, weightTab, facilityId, cycleId, offlineSyncTick]);

  // Load during-cycle weights
  useEffect(() => {
    const loadCycle = async () => {
      if (
        openDialog !== "weights" ||
        weightTab !== "cycle" ||
        facilityId == null ||
        cycleId == null
      )
        return;
      const hydrateFromCache = () => {
        const cached = readCachedCollection(broilerWeightsUrl(facilityId, cycleId));
        const cachedRows = mapCycleWeightRows(cached, facilityId, cycleId);
        const offlineRows = readOfflineRecords(OFFLINE_NS_CYCLE_WEIGHTS, facilityId, cycleId);
        setCycleRows(mergeOfflineRecords(cachedRows, offlineRows));
      };

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        hydrateFromCache();
        return;
      }

      try {
        const res = await axios.get(broilerWeightsUrl(facilityId, cycleId));
        writeCachedCollection(broilerWeightsUrl(facilityId, cycleId), res.data);
        const rows = mapCycleWeightRows(res.data, facilityId, cycleId);
        const merged = combineServerRowsWithOffline(
          OFFLINE_NS_CYCLE_WEIGHTS,
          rows,
          matchCycleWeightRow
        );
        setCycleRows(merged);
      } catch (error) {
        console.error("Failed to load cycle weights", error);
        hydrateFromCache();
      }
    };
    loadCycle();
  }, [openDialog, weightTab, facilityId, cycleId, offlineSyncTick]); // Load description dropdown options when mortalities dialog opens
  useEffect(() => {
    const loadOptions = async () => {
      if (openDialog !== "mortalities") return;
      try {
        const [mortRes, cullRes] = await Promise.all([
          axios.get(mortDescriptionsUrl()),
          axios.get(cullDescriptionsUrl()),
        ]);
        writeCachedCollection(mortDescriptionsUrl(), mortRes.data);
        writeCachedCollection(cullDescriptionsUrl(), cullRes.data);
        const mort = Array.isArray(mortRes.data) ? mortRes.data : [];
        const cull = Array.isArray(cullRes.data) ? cullRes.data : [];
        setMortOptions(mort);
        setCullOptions(cull);
      } catch (e) {
        const cachedMort = readCachedCollection(mortDescriptionsUrl());
        const cachedCull = readCachedCollection(cullDescriptionsUrl());
        setMortOptions(Array.isArray(cachedMort) ? cachedMort : []);
        setCullOptions(Array.isArray(cachedCull) ? cachedCull : []);
      }
    };
    loadOptions();
  }, [openDialog]);

  // Load Consumption rows when dialog opens and selection is set
  useEffect(() => {
    if (openDialog !== "consumption") return;
    if (facilityId == null || cycleId == null) {
      setConsRows([]);
      return;
    }

    const hydrateFromCache = () => {
      const cachedFeed = mapConsumptionRows(
        readCachedCollection(waterFeedDataUrl(facilityId, cycleId)),
        facilityId,
        cycleId,
        "feed"
      );
      let cachedWater = mapConsumptionRows(
        readCachedCollection(waterDataUrl(facilityId, cycleId)),
        facilityId,
        cycleId,
        "water"
      );
      if (farmId != null && `${farmId}` !== "") {
        const cachedUtilities = readCachedCollection(utilitiesUrl(farmId, facilityId, cycleId));
        const utilitiesByDate = new Map(
          (Array.isArray(cachedUtilities) ? cachedUtilities : []).map((row) => [
            `${pick(row?.date, row?.Date, row?.dateTime, row?.DateTime) ?? ""}`.slice(0, 10),
            row,
          ])
        );
        cachedWater = cachedWater.map((row) => {
          const utility = utilitiesByDate.get(toLocalDateTimeInput(row?.dateTime).slice(0, 10));
          return utility
            ? {
                ...row,
                utilityId: pick(utility?.id, utility?.ID, utility?.Id),
                waterSource: pick(utility?.water, utility?.Water),
              }
            : row;
        });
      }
      const cachedRows = [...cachedFeed, ...cachedWater];
      const offlineRowsRaw = readOfflineRecords(OFFLINE_NS_CONSUMPTION, facilityId, cycleId);
      const offlineRows = normalizeConsumptionRows(offlineRowsRaw);
      const mergedRows = mergeOfflineRecords(cachedRows, offlineRows);
      setConsRows((prev) => normalizeConsumptionRows(applyConsumptionBinMemory(mergedRows, prev)));
    };

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      hydrateFromCache();
      return;
    }

    let active = true;
    (async () => {
      try {
        const mergedRows = await fetchConsumptionRowsFromServer();
        if (!active) return;
        setConsRows((prev) =>
          normalizeConsumptionRows(applyConsumptionBinMemory(mergedRows, prev))
        );
      } catch (error) {
        console.error("Failed to load consumption data", error);
        hydrateFromCache();
      }
    })();

    return () => {
      active = false;
    };
  }, [openDialog, facilityId, cycleId, offlineSyncTick, fetchConsumptionRowsFromServer]);

  useEffect(() => {
    if (openDialog === "consumption") {
      setConsActiveView("readings");
      setConsPage(1);
      loadFeedDictionary();
    }
  }, [loadFeedDictionary, openDialog]);

  useEffect(() => {
    if (openDialog === "mortalities") {
      setMortActiveView("mortalities");
      setOrderBy("date");
      setOrder("desc");
      setPage(1);
    }
  }, [openDialog]);

  // Consumption: sorting helpers
  const findSetupFeedMatch = (rows, targetDateTime, stageValue, feedNameValue) => {
    const targetTime = getDateTimestamp(targetDateTime);
    const stageKey = stageValue != null && `${stageValue}` !== "" ? `${stageValue}` : null;
    const feedNameKey =
      feedNameValue != null && `${feedNameValue}` !== "" ? `${feedNameValue}` : null;
    const candidates = (Array.isArray(rows) ? rows : []).filter((entry) => {
      if (stageKey && `${entry?.stage ?? ""}` !== stageKey) return false;
      if (feedNameKey && `${entry?.feedName ?? ""}` !== feedNameKey) return false;
      return true;
    });
    if (targetTime == null) return candidates[0] ?? null;

    const matchesRange = candidates.filter((entry) => {
      const start = entry?.startTimestamp;
      const end = entry?.endTimestamp;
      if (start != null && targetTime < start) return false;
      if (end != null && targetTime > end) return false;
      return start != null || end != null;
    });
    const matchesBefore = candidates.filter(
      (entry) => entry?.startTimestamp != null && entry.startTimestamp <= targetTime
    );
    const [match] = sortRowsByDateAscending(
      matchesRange.length > 0 ? matchesRange : matchesBefore,
      (entry) => entry?.startDate ?? entry?.dateTime
    ).reverse();
    return match ?? candidates[0] ?? null;
  };

  const getConsumptionFeedMetadata = (row, useLabels = true) => {
    const directStage = row?.stage != null && `${row.stage}`.trim() ? `${row.stage}` : "";
    const directFeedName =
      row?.feedName != null && `${row.feedName}`.trim() ? `${row.feedName}` : "";
    if (directStage || directFeedName) {
      const setupMatch = findSetupFeedMatch(
        setupFeedRows,
        row?.dateTime,
        directStage,
        directFeedName
      );
      return {
        stage: useLabels ? feedStageMap[directStage] || directStage : directStage,
        feedName: useLabels ? feedNameMap[directFeedName] || directFeedName : directFeedName,
        supplier: row?.supplier != null ? `${row.supplier}` : setupMatch?.supplier ?? "",
      };
    }

    const targetTime = getDateTimestamp(row?.dateTime);
    const candidates = Array.isArray(setupFeedRows) ? setupFeedRows : [];
    if (targetTime == null || candidates.length === 0) return { stage: "", feedName: "" };

    const match = findSetupFeedMatch(candidates, row?.dateTime);

    return {
      stage: useLabels ? feedStageMap[match?.stage] || match?.stage || "" : match?.stage ?? "",
      feedName: useLabels
        ? feedNameMap[match?.feedName] || match?.feedName || ""
        : match?.feedName ?? "",
      supplier: match?.supplier ?? "",
    };
  };

  const getConsSortValue = (row, key) => {
    switch (key) {
      case "date": {
        const raw = row?.dateTime ?? row?.date ?? "";
        const full = Date.parse(raw);
        if (!Number.isNaN(full)) return full;
        const d = toDateOnly(raw);
        const t = Date.parse(d || "");
        return Number.isNaN(t) ? 0 : t;
      }
      case "feedKg":
        return Number(row.feedKg ?? 0);
      case "stage":
        return `${getConsumptionFeedMetadata(row).stage ?? ""}`.toLowerCase();
      case "feedName":
        return `${getConsumptionFeedMetadata(row).feedName ?? ""}`.toLowerCase();
      case "waterL":
        return Number(row.waterL ?? 0);
      case "waterReading":
        return Number(row.waterReading ?? 0);
      default:
        return 0;
    }
  };
  const consFilteredRows = (() => {
    const rows = Array.isArray(consRows)
      ? consRows.filter((row) => {
          const type = getConsumptionType(row);
          return consActiveView === "water" ? type === "water" : type !== "water";
        })
      : [];
    rows.sort((a, b) => {
      const va = getConsSortValue(a, consOrderBy);
      const vb = getConsSortValue(b, consOrderBy);
      if (va < vb) return consOrder === "asc" ? -1 : 1;
      if (va > vb) return consOrder === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  })();

  const consPageStart = (consPage - 1) * consPageSize;
  const consPageEnd = consPageStart + consPageSize;
  const consPageRows = consFilteredRows.slice(consPageStart, consPageEnd);
  const consTotalPages = Math.max(1, Math.ceil(consFilteredRows.length / consPageSize));

  const handleConsRequestSort = (property) => () => {
    const isAsc = consOrderBy === property && consOrder === "asc";
    setConsOrder(isAsc ? "desc" : "asc");
    setConsOrderBy(property);
    setConsPage(1);
  };

  const handleConsViewChange = (view) => {
    if (view === consActiveView) return;
    setConsActiveView(view);
    setConsPage(1);
  };

  const openConsAdd = () => {
    const mode = consActiveView === "water" ? "water" : "feed";
    setConsFormMode(mode);
    setConsForm({
      id: null,
      feedKg: "",
      waterReading: "",
      waterSource: "",
      utilityId: null,
      waterL: "",
      dateTime: mode === "feed" ? nowUtcPlusTwoDateTimeInput() : nowLocalDateTimeInput(),
      bin: "bin1",
      stage: "",
      feedName: "",
      feedSpendId: null,
    });
    if (mode === "feed") {
      loadFeedBalance();
      loadFeedBins();
      loadSetupFeedRows();
      loadFeedDictionary();
    }
    setConsEditOpen(true);
  };
  const openConsEdit = (row, modeOverride) => {
    const mode = modeOverride || getConsumptionType(row);
    const feedMetadata = mode === "feed" ? getConsumptionFeedMetadata(row, false) : {};
    setConsFormMode(mode);
    setConsForm({
      id: row?.id ?? null,
      feedKg: mode === "feed" ? row?.feedKg ?? "" : "",
      waterReading: row?.waterReading ?? "",
      waterSource: row?.waterSource ?? "",
      utilityId: row?.utilityId ?? null,
      waterL: row?.waterL ?? "",
      dateTime:
        (mode === "feed"
          ? toUtcPlusTwoDateTimeInput(row?.dateTime)
          : toLocalDateTimeInput(row?.dateTime)) || "",
      bin: normalizeBinSelection(row?.bin ?? row?.binSelection),
      stage: feedMetadata.stage ?? "",
      feedName: feedMetadata.feedName ?? "",
      feedSpendId: row?.feedSpendId ?? null,
    });
    if (mode === "feed") {
      loadFeedBalance();
      loadFeedBins();
      loadSetupFeedRows();
      loadFeedDictionary();
    }
    setConsEditOpen(true);
  };
  const closeConsEdit = () => setConsEditOpen(false);
  const submitConsForm = async () => {
    if (facilityId == null || cycleId == null) {
      setSnackMessage("Please select a facility and cycle before saving consumption");
      setSnackOpen(true);
      return;
    }

    const mode = consFormMode === "water" ? "water" : "feed";
    const existingIdValue = consForm.id;
    const isTempId = existingIdValue != null && isTempIdentifier(existingIdValue);
    const optimisticId = getOptimisticId(existingIdValue);
    const resolvedExistingId = isTempId
      ? null
      : existingIdValue != null
      ? normalizePersistedId(existingIdValue)
      : null;
    const isNewRecord = resolvedExistingId == null && !isTempId;
    let effectiveDateInput = consForm.dateTime;
    if (isNewRecord || !effectiveDateInput) {
      effectiveDateInput = nowLocalDateTimeInput();
    }

    const feedValue =
      mode === "feed" ? (consForm.feedKg === "" ? 0 : Number(consForm.feedKg)) : null;
    const waterReadingValue = consForm.waterReading === "" ? null : Number(consForm.waterReading);
    const waterSourceValue =
      mode === "water" && consForm.waterSource !== "" ? Number(consForm.waterSource) : null;
    const waterValue = consForm.waterL === "" ? 0 : Number(consForm.waterL);
    const binSelection = normalizeBinSelection(consForm.bin);
    const stageValue =
      mode === "feed" && consForm.stage !== "" && consForm.stage != null
        ? toNumberOrNull(consForm.stage) ?? consForm.stage
        : null;
    const feedNameValue =
      mode === "feed" && consForm.feedName !== "" && consForm.feedName != null
        ? toNumberOrNull(consForm.feedName) ?? consForm.feedName
        : null;
    let binTotals = feedBins;
    let latestFeedBalance = feedBalance;
    let currentSetupFeedRows = setupFeedRows;
    if (mode === "feed") {
      const loadedFeedBalance = await loadFeedBalance();
      if (loadedFeedBalance) {
        latestFeedBalance = loadedFeedBalance;
      }
      const latestBins = await loadFeedBins();
      if (latestBins) {
        binTotals = latestBins;
      }
      const latestSetupRows = await loadSetupFeedRows();
      if (Array.isArray(latestSetupRows) && latestSetupRows.length > 0) {
        currentSetupFeedRows = latestSetupRows;
      }
    }
    const effectiveDateIso = toIsoDateTimeFromInput(effectiveDateInput);
    const availableBins =
      mode === "feed"
        ? computeAvailableFeedBinsAtDate(currentSetupFeedRows, consRows, effectiveDateIso, {
            excludeIds: [existingIdValue, resolvedExistingId, optimisticId],
            fallbackBins: binTotals,
          })
        : {};
    if (mode === "feed") {
      const selectedBalance =
        binSelection === "bin2"
          ? toNumberOrNull(latestFeedBalance.bin2)
          : toNumberOrNull(latestFeedBalance.bin1);
      if (selectedBalance != null && feedValue > selectedBalance) {
        const selectedLabel = binSelection === "bin2" ? "Bin B" : "Bin A";
        setSnackMessage(`Insufficient feed remaining in ${selectedLabel}, please add more`);
        setSnackOpen(true);
        return;
      }
    }
    const { bin1Bal, bin2Bal, feedpaperBal } =
      mode === "feed"
        ? computeFeedBalances(
            {
              ...availableBins,
              bin1: toNumberOrNull(latestFeedBalance.bin1) ?? availableBins.bin1,
              bin2: toNumberOrNull(latestFeedBalance.bin2) ?? availableBins.bin2,
            },
            binSelection,
            feedValue
          )
        : {};

    const optimisticPayload =
      mode === "water"
        ? {
            broilerID: Number(facilityId),
            cycleId: cycleId == null ? null : Number(cycleId),
            waterSource: waterSourceValue,
            waterReading: waterReadingValue,
            waterConsumption: waterValue,
            dateTime: effectiveDateIso,
          }
        : {
            broilerID: Number(facilityId),
            farmId: toNumberOrNull(farmId) ?? farmId,
            feed: feedValue,
            dateTime: effectiveDateIso,
            cycleId: cycleId == null ? null : Number(cycleId),
            stage: stageValue,
            feedName: feedNameValue,
            feedSpendId: consForm.feedSpendId ?? null,
            bin: binSelection,
            bin1Bal,
            bin2Bal,
            feedpaperBal,
          };

    const requestPayload =
      mode === "water"
        ? {
            broilerID: optimisticPayload.broilerID,
            cycleId: optimisticPayload.cycleId,
            source: optimisticPayload.waterSource,
            sourcePercent: optimisticPayload.waterSource,
            waterReading: optimisticPayload.waterReading,
            dateTime: optimisticPayload.dateTime,
          }
        : {
            ...(resolvedExistingId != null ? { id: resolvedExistingId } : {}),
            broilerID: optimisticPayload.broilerID,
            farmId: optimisticPayload.farmId,
            feed: optimisticPayload.feed,
            dateTime: optimisticPayload.dateTime,
            cycleId: optimisticPayload.cycleId,
            stage: optimisticPayload.stage,
            feedName: optimisticPayload.feedName,
            feedSpendId: optimisticPayload.feedSpendId,
            bin_1: optimisticPayload.bin === "bin1",
            bin_2: optimisticPayload.bin === "bin2",
          };

    if (mode === "feed") {
      const signature = `${optimisticPayload.dateTime ?? ""}|${optimisticPayload.feed ?? ""}`;
      updateConsumptionBinCache(facilityId, cycleId, {
        id: resolvedExistingId ?? optimisticId,
        signature,
        bin: binSelection,
        bin1Bal,
        bin2Bal,
        feedpaperBal,
      });
    }

    if (resolvedExistingId != null) {
      Object.assign(optimisticPayload, { id: resolvedExistingId });
    }

    const isPendingCreate = existingIdValue != null && isTempIdentifier(existingIdValue);
    if (isPendingCreate) {
      const targetId = applyOptimisticConsumptionUpsert(
        optimisticPayload,
        optimisticId ?? null,
        mode
      );
      handleQueuedOfflineRequest(OFFLINE_NS_CONSUMPTION, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_CONSUMPTION, targetId, requestPayload);
      setConsEditOpen(false);
      setSnackMessage("Consumption saved locally. It will sync when you're online.");
      setSnackOpen(true);
      return;
    }

    const targetUrl = mode === "water" ? updateWaterDataUrl() : updateFeedWaterUrl();

    try {
      console.debug("Posting consumption", targetUrl, requestPayload);
    } catch {}

    let response;
    try {
      response = await axios.post(targetUrl, requestPayload, {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      });
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        const targetId = applyOptimisticConsumptionUpsert(
          optimisticPayload,
          optimisticId ?? null,
          mode
        );
        handleQueuedOfflineRequest(OFFLINE_NS_CONSUMPTION, targetId);
        updateQueuePayloadForRecord(OFFLINE_NS_CONSUMPTION, targetId, requestPayload);
        setConsEditOpen(false);
        setSnackMessage("Consumption saved locally. It will sync when you're online.");
        setSnackOpen(true);
        return;
      }
      console.error("Failed to save consumption", error);
      setSnackMessage("Failed to save consumption");
      setSnackOpen(true);
      return;
    }

    if (wasQueuedOffline(response)) {
      const targetId = applyOptimisticConsumptionUpsert(
        optimisticPayload,
        optimisticId ?? null,
        mode
      );
      handleQueuedOfflineRequest(OFFLINE_NS_CONSUMPTION, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_CONSUMPTION, targetId, requestPayload);
      setConsEditOpen(false);
      setSnackMessage("Consumption saved locally. It will sync when you're online.");
      setSnackOpen(true);
      return;
    }

    let utilitiesUpdateFailed = false;
    if (mode === "water") {
      const utilityRecord = {
        ...(consForm.utilityId != null && `${consForm.utilityId}` !== ""
          ? { id: toNumberOrNull(consForm.utilityId) ?? consForm.utilityId }
          : {}),
        date: nowLocalDateTimeInput().slice(0, 10),
        farm_id: toNumberOrNull(farmId) ?? farmId,
        facility_id: toNumberOrNull(facilityId) ?? facilityId,
        cycle_id: toNumberOrNull(cycleId) ?? cycleId,
        water: waterSourceValue,
        gas: null,
        electricity_mains: null,
        electricity_solar: null,
      };
      try {
        await axios.post(updateUtilitiesUrl(), [utilityRecord], {
          headers: { "Content-Type": "application/json", Accept: "application/json" },
        });
      } catch (utilitiesError) {
        utilitiesUpdateFailed = true;
        console.error("Failed to update water utility source", utilitiesError);
      }
    }

    if (mode === "feed") {
      const responseId = pick(response?.data?.id, response?.data?.ID);
      if (responseId != null) {
        updateConsumptionBinCache(facilityId, cycleId, {
          id: responseId,
          signature: `${optimisticPayload.dateTime ?? ""}|${optimisticPayload.feed ?? ""}`,
          bin: binSelection,
          bin1Bal,
          bin2Bal,
          feedpaperBal,
        });
      }
    }

    try {
      await refreshConsumptionFromServer();
      if (mode === "feed") {
        await loadFeedBalance();
      }
    } catch (e) {
      console.error("Failed to refresh consumption after save", e);
    }

    setConsEditOpen(false);
    setSnackMessage(
      utilitiesUpdateFailed
        ? "Water saved, but its utility source could not be updated"
        : "Consumption saved successfully"
    );
    setSnackOpen(true);
  };
  const openConsDelete = (row) => {
    setConsDeleteTarget(row);
    setConsDeleteOpen(true);
  };
  const performConsDelete = async () => {
    const row = consDeleteTarget;
    if (!row || row.id == null) {
      setConsDeleteOpen(false);
      setConsDeleteTarget(null);
      return;
    }
    const idString = `${row.id}`;
    if (isTempIdentifier(idString) || row?.__pendingCreate) {
      applyOptimisticConsumptionDelete(row);
      setSnackMessage("Consumption removed locally.");
      setSnackOpen(true);
      setConsDeleteOpen(false);
      setConsDeleteTarget(null);
      return;
    }

    let response;
    try {
      const isFeedDelete = getConsumptionType(row) !== "water";
      const deleteUrl = isFeedDelete ? removeFeedWaterUrl() : removeWaterDataUrl();
      response = await axios.post(
        deleteUrl,
        { id: row.id },
        { headers: { "Content-Type": "application/json", Accept: "application/json" } }
      );
      const feedSpendId = pick(row?.feedSpendId, row?.FeedSpendId, row?.feed_spend_id);
      if (isFeedDelete && feedSpendId != null && `${feedSpendId}` !== "") {
        await axios.post(
          removeFeedSpendUrl(),
          { id: toNumberOrNull(feedSpendId) ?? feedSpendId },
          { headers: { "Content-Type": "application/json", Accept: "application/json" } }
        );
      }
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        applyOptimisticConsumptionDelete(row);
        setSnackMessage("Consumption removal queued. It will sync when you're online.");
        setSnackOpen(true);
      } else {
        console.error("Failed to delete consumption", error);
        setSnackMessage("Failed to delete consumption");
        setSnackOpen(true);
      }
      setConsDeleteOpen(false);
      setConsDeleteTarget(null);
      return;
    }

    if (wasQueuedOffline(response)) {
      applyOptimisticConsumptionDelete(row);
      setSnackMessage("Consumption removal queued. It will sync when you're online.");
      setSnackOpen(true);
      setConsDeleteOpen(false);
      setConsDeleteTarget(null);
      return;
    }

    try {
      await refreshConsumptionFromServer();
      if (getConsumptionType(row) !== "water") {
        await loadFeedBalance();
      }
    } catch (refreshError) {
      console.error("Failed to refresh consumption after delete", refreshError);
      setConsRows((prev) =>
        Array.isArray(prev) ? prev.filter((entry) => `${entry.id}` !== idString) : []
      );
    }

    setSnackMessage("Consumption deleted successfully");
    setSnackOpen(true);
    setConsDeleteOpen(false);
    setConsDeleteTarget(null);
  };

  const openAddDialog = () => {
    setForm({
      id: null,
      fatalities: "",
      culls: "",
      dateTime: nowLocalDateTimeInput(),
      eventMortalitiesID: "",
      eventCullsID: "",
    });
    setMortalityImageFile(null);
    if (mortalityUploadInputRef.current) mortalityUploadInputRef.current.value = "";
    if (mortalityCameraInputRef.current) mortalityCameraInputRef.current.value = "";
    setEditOpen(true);
  };

  const hasMortalityOrCullRecordToday = (rows, view = mortActiveView) => {
    const today = toDateOnly(nowLocalDateTimeInput());
    const list = Array.isArray(rows)
      ? rows
      : Array.isArray(rows?.data)
      ? rows.data
      : Array.isArray(rows?.value)
      ? rows.value
      : [];
    return list.some((row) => {
      const rowDate = toDateOnly(pick(row?.dateTime, row?.DateTime, row?.date, row?.Date));
      if (rowDate !== today) return false;
      const value =
        view === "mortalities"
          ? pick(row?.fatalities, row?.mortalities)
          : pick(row?.culls, row?.Culls);
      return value !== undefined && value !== null && `${value}`.trim() !== "";
    });
  };

  const openAdd = async () => {
    const addView = mortActiveView;
    let hasRecordToday = hasMortalityOrCullRecordToday(mortRows, addView);

    if (facilityId != null && cycleId != null && navigator?.onLine !== false) {
      try {
        const response = await axios.get(mortalitiesUrl(facilityId, cycleId));
        writeCachedCollection(mortalitiesUrl(facilityId, cycleId), response.data);
        hasRecordToday = hasMortalityOrCullRecordToday(response.data, addView);
      } catch (error) {
        console.warn("Unable to check existing mortalities before adding", error);
      }
    }

    if (hasRecordToday) {
      setDuplicateAddView(addView);
      setDuplicateAddOpen(true);
      return;
    }

    openAddDialog();
  };

  const openEdit = (row) => {
    setForm({
      id: row.id ?? null,
      fatalities: row.mortalities ?? "",
      culls: row.culls ?? "",
      dateTime: toLocalDateTimeInput(row.date) || "",
      eventMortalitiesID: row.eventMortalitiesID ?? "",
      eventCullsID: row.eventCullsID ?? "",
    });
    setMortalityImageFile(null);
    if (mortalityUploadInputRef.current) mortalityUploadInputRef.current.value = "";
    if (mortalityCameraInputRef.current) mortalityCameraInputRef.current.value = "";
    setEditOpen(true);
  };

  const viewMortalityImage = (row) => {
    const imageUrl =
      row?.imageUrl ||
      row?.ImageUrl ||
      row?.imageURL ||
      row?.url ||
      row?.photoUrl ||
      row?.photoURL ||
      "";
    if (imageUrl) {
      if (typeof window !== "undefined") {
        window.open(String(imageUrl), "_blank", "noopener,noreferrer");
      }
      return;
    }
    const blobName = resolveMortalityImageBlobName(row);
    if (!blobName) {
      setSnackMessage("No image available for this record");
      setSnackOpen(true);
      return;
    }
    if (typeof window !== "undefined") {
      const readUrl = `${IMAGE_BLOB_READ_BASE_URL}/${encodeURIComponent(
        blobName
      )}${IMAGE_BLOB_READ_SAS_TOKEN}`;
      window.open(readUrl, "_blank", "noopener,noreferrer");
    }
  };

  const hasMortalityImage = (row) => {
    const directUrl =
      row?.imageUrl ||
      row?.ImageUrl ||
      row?.imageURL ||
      row?.url ||
      row?.photoUrl ||
      row?.photoURL ||
      "";
    if (directUrl) return true;
    return Boolean(resolveMortalityImageBlobName(row));
  };

  const closeEdit = () => {
    setEditOpen(false);
    setMortalityImageFile(null);
    if (mortalityUploadInputRef.current) mortalityUploadInputRef.current.value = "";
    if (mortalityCameraInputRef.current) mortalityCameraInputRef.current.value = "";
  };

  const openMortalityUpload = () => {
    mortalityUploadInputRef.current?.click();
  };

  const openMortalityCamera = () => {
    mortalityCameraInputRef.current?.click();
  };

  const handleMortalityImageChange = (event) => {
    const nextFile = event?.target?.files?.[0] ?? null;
    if (nextFile) {
      const fileName = String(nextFile.name || "").toLowerCase();
      const isAllowed = ALLOWED_IMAGE_EXTENSIONS.some((ext) => fileName.endsWith(ext));
      if (!isAllowed) {
        setSnackMessage("Please select a .png, .jpg or .jpeg image");
        setSnackOpen(true);
        if (event?.target) event.target.value = "";
        setMortalityImageFile(null);
        return;
      }
    }
    setMortalityImageFile(nextFile);
  };

  const uploadMortalityImage = async ({ savedAtIso, mortalityDescription, cullDescription }) => {
    if (!mortalityImageFile) return { attempted: false, succeeded: false, blobName: "" };
    const broilerId = Number(facilityId);
    const cycle = Number(cycleId);
    const description =
      mortActiveView === "mortalities"
        ? String(mortalityDescription || "").trim()
        : String(cullDescription || "").trim();
    if (!Number.isFinite(broilerId) || !Number.isFinite(cycle) || !description) {
      return { attempted: true, succeeded: false, blobName: "" };
    }
    const sanitizeToken = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    const safeDescription = sanitizeToken(description) || "none";
    const timestampToken = (savedAtIso || new Date().toISOString())
      .replace(/\.\d{3}Z$/, "Z")
      .replace(/[-:]/g, "")
      .replace(/\.\d+/g, "");
    const typeSegment =
      mortActiveView === "mortalities" ? `mortality-${safeDescription}` : `cull-${safeDescription}`;
    const generatedFileName = `${broilerId}-${cycle}-${typeSegment}-${timestampToken}.jpg`;
    const putUrl = IMAGE_BLOB_SAS_TEMPLATE.replace("<image.png>", generatedFileName);
    try {
      const response = await fetch(putUrl, {
        method: "PUT",
        mode: "cors",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": "image/jpeg",
        },
        body: mortalityImageFile,
      });
      if (!response.ok) {
        throw new Error(`Blob upload failed with status ${response.status}`);
      }
      return { attempted: true, succeeded: true, blobName: generatedFileName };
    } catch (error) {
      console.error("Failed to upload mortality image", error);
      return { attempted: true, succeeded: false, blobName: "" };
    }
  };

  const toIsoDateTime = (dateStr) => {
    // Here dateStr is expected to be from a datetime-local input
    if (!dateStr) return "";
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dateStr)) return `${dateStr}:00`;
    return dateStr;
  };

  const makeTempId = () => `temp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const isTempIdentifier = (value) => {
    if (value === undefined || value === null) return false;
    const str = String(value);
    return str.startsWith("temp-") || str.startsWith("tmp-");
  };

  const normalizePersistedId = (value) => {
    if (value === undefined || value === null) return null;
    const numeric = Number(value);
    return Number.isNaN(numeric) ? value : numeric;
  };

  const getOptimisticId = (rawId) => {
    if (rawId === undefined || rawId === null) return null;
    return isTempIdentifier(rawId) ? rawId : normalizePersistedId(rawId);
  };

  const normalizeForComparison = (value) => {
    if (value === undefined || value === null || value === "") return null;
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;
      if (/^[+-]?\d+(\.\d+)?$/.test(trimmed)) {
        const num = Number(trimmed);
        if (!Number.isNaN(num)) return num;
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(trimmed) || trimmed.includes("T")) {
        const parsed = Date.parse(trimmed);
        if (!Number.isNaN(parsed)) return new Date(parsed).toISOString();
      }
      return trimmed;
    }
    return value;
  };

  const valuesEqual = (a, b) => {
    const na = normalizeForComparison(a);
    const nb = normalizeForComparison(b);
    if (na === nb) return true;
    if (na == null && nb == null) return true;
    return false;
  };

  const createOfflineMatcher = (fields) => (entry, row) => {
    if (!entry || !row) return false;
    const idMatches =
      entry.id != null &&
      !isTempIdentifier(entry.id) &&
      row.id != null &&
      `${entry.id}` === `${row.id}`;
    const fieldsMatch = fields.every((field) => valuesEqual(entry[field], row[field]));
    if (idMatches) return fieldsMatch;
    return fieldsMatch;
  };

  const matchMortalityRow = createOfflineMatcher([
    "facilityRef",
    "mortalities",
    "culls",
    "eventMortalitiesID",
    "eventCullsID",
    "date",
  ]);

  const matchPlacementRow = createOfflineMatcher([
    "broilerId",
    "cycleId",
    "totalBirdWeight",
    "averageBox",
    "noOfBirdsPerBox",
    "date",
  ]);

  const matchCycleWeightRow = createOfflineMatcher([
    "broilerId",
    "cycleId",
    "weightBack",
    "weightBackR",
    "weightCenter",
    "weightCenterR",
    "weightFront",
    "weightFrontR",
    "sampleSize",
    "weightAverage",
    "dateTime",
  ]);

  const matchCatchRow = createOfflineMatcher([
    "broilerId",
    "cycleId",
    "totalweight",
    "noofbirds",
    "date",
  ]);

  const matchConsumptionRow = (entry, row) => {
    if (!entry || !row) return false;
    const entryType = getConsumptionType(entry);
    const rowType = getConsumptionType(row);
    if (entryType !== rowType) return false;
    const idMatches =
      entry.id != null &&
      !isTempIdentifier(entry.id) &&
      row.id != null &&
      `${entry.id}` === `${row.id}`;
    const fieldsMatch =
      valuesEqual(entry.broilerId, row.broilerId) &&
      valuesEqual(entry.cycleId, row.cycleId) &&
      valuesEqual(entry.feedKg, row.feedKg) &&
      valuesEqual(entry.waterSource, row.waterSource) &&
      valuesEqual(entry.waterReading, row.waterReading) &&
      valuesEqual(entry.waterL, row.waterL) &&
      valuesEqual(entry.bin, row.bin) &&
      valuesEqual(entry.bin1Bal, row.bin1Bal) &&
      valuesEqual(entry.bin2Bal, row.bin2Bal) &&
      valuesEqual(entry.feedpaperBal, row.feedpaperBal) &&
      valuesEqual(entry.dateTime, row.dateTime);
    if (idMatches) return fieldsMatch;
    return fieldsMatch;
  };

  const updateOfflineRecordQueueId = (namespace, targetId, queueId) => {
    if (!queueId || targetId == null) return;
    const records = readOfflineRecords(namespace, facilityId, cycleId);
    if (!Array.isArray(records) || records.length === 0) return;
    const next = records.map((entry) => {
      if (!entry || `${entry.id}` !== `${targetId}`) return entry;
      return { ...entry, __queueId: queueId };
    });
    replaceOfflineRecords(namespace, facilityId, cycleId, next);
  };

  const parseQueuePayload = (entry) => {
    if (!entry?.data) return null;
    if (entry.data.type === "json") {
      try {
        return entry.data.body ? JSON.parse(entry.data.body) : null;
      } catch (error) {
        console.warn("Unable to parse queued payload", error);
        return null;
      }
    }
    if (entry.data.type === "string") {
      const raw = entry.data.body;
      if (raw == null) return null;
      try {
        return JSON.parse(raw);
      } catch (error) {
        return { __raw: raw };
      }
    }
    return null;
  };

  const queuePayloadMatchesRecord = (namespace, payload, record) => {
    if (!payload || typeof payload !== "object" || !record) return false;
    switch (namespace) {
      case OFFLINE_NS_MORTALITIES: {
        const hasFatalitiesPayload = Object.prototype.hasOwnProperty.call(payload, "fatalities");
        const hasCullsPayload = Object.prototype.hasOwnProperty.call(payload, "culls");
        return (
          valuesEqual(payload.briolerID, record.facilityRef) &&
          valuesEqual(payload.cycleId, cycleId == null ? null : Number(cycleId)) &&
          valuesEqual(payload.dateTime, record.date) &&
          (!hasFatalitiesPayload ||
            (valuesEqual(payload.fatalities, record.mortalities) &&
              valuesEqual(
                payload.eventMortalitiesID ?? payload.eventMortalityID,
                record.eventMortalitiesID
              ))) &&
          (!hasCullsPayload ||
            (valuesEqual(payload.culls, record.culls) &&
              valuesEqual(payload.eventCullsID, record.eventCullsID)))
        );
      }
      case OFFLINE_NS_PLACEMENT:
        return (
          valuesEqual(payload.broilerId, record.broilerId) &&
          valuesEqual(payload.cycleId, record.cycleId) &&
          valuesEqual(payload.totalBirdWeight, record.totalBirdWeight) &&
          valuesEqual(payload.averageBox, record.averageBox) &&
          valuesEqual(payload.noOfBirdsPerBox, record.noOfBirdsPerBox) &&
          valuesEqual(payload.date, record.date)
        );
      case OFFLINE_NS_CYCLE_WEIGHTS:
        return (
          valuesEqual(payload.broilerID, record.broilerId) &&
          valuesEqual(payload.cycleId, record.cycleId) &&
          valuesEqual(payload.weightBack, record.weightBack) &&
          valuesEqual(payload.weightBackR, record.weightBackR) &&
          valuesEqual(payload.weightCenter, record.weightCenter) &&
          valuesEqual(payload.weightCenterR, record.weightCenterR) &&
          valuesEqual(payload.weightFront, record.weightFront) &&
          valuesEqual(payload.weightFrontR, record.weightFrontR) &&
          valuesEqual(payload.sampleSize, record.sampleSize) &&
          valuesEqual(payload.weightAverage, record.weightAverage) &&
          valuesEqual(payload.dateTime, record.dateTime)
        );
      case OFFLINE_NS_CATCH:
        return (
          valuesEqual(payload.broilerId, record.broilerId) &&
          valuesEqual(payload.cycleId, record.cycleId) &&
          valuesEqual(payload.noofbirds, record.noofbirds) &&
          valuesEqual(payload.totalweight, record.totalweight) &&
          valuesEqual(payload.date, record.date)
        );
      case OFFLINE_NS_CONSUMPTION: {
        const payloadType = payload.feed !== undefined && payload.feed !== null ? "feed" : "water";
        const recordType = getConsumptionType(record);
        if (payloadType !== recordType) return false;
        const payloadFeed = payload.feed ?? payload.feedKg ?? null;
        const payloadSource =
          payload.waterSource ?? payload.sourcePercent ?? payload.source ?? null;
        const payloadReading = payload.waterReading ?? payload.reading ?? null;
        const payloadWater = payload.waterConsumption ?? payload.waterL ?? null;
        const payloadBin = payload.bin ?? payload.binSelection ?? getBinSelectionFromFlags(payload);
        return (
          valuesEqual(payload.broilerID, record.broilerId) &&
          valuesEqual(payload.cycleId, record.cycleId) &&
          valuesEqual(payloadFeed, record.feedKg) &&
          valuesEqual(payloadSource, record.waterSource) &&
          valuesEqual(payloadReading, record.waterReading) &&
          valuesEqual(payloadWater, record.waterL) &&
          valuesEqual(payloadBin, record.bin) &&
          valuesEqual(payload.bin1Bal ?? payload.bin1_bal ?? payload.bin_1_bal, record.bin1Bal) &&
          valuesEqual(payload.bin2Bal ?? payload.bin2_bal ?? payload.bin_2_bal, record.bin2Bal) &&
          valuesEqual(
            payload.feedpaperBal ?? payload.feedpaper_bal ?? payload.feed_paper_bal,
            record.feedpaperBal
          ) &&
          valuesEqual(payload.dateTime, record.dateTime)
        );
      }
      default:
        return false;
    }
  };

  const updateQueuePayloadForRecord = (namespace, targetId, payload) => {
    if (targetId == null) return;
    const queue = readOfflineQueue();
    if (!queue.length) return;
    const records = readOfflineRecords(namespace, facilityId, cycleId);
    if (!Array.isArray(records) || records.length === 0) return;
    const record = records.find((entry) => entry && `${entry.id}` === `${targetId}`);
    if (!record?.__queueId) return;
    const queueIndex = queue.findIndex((entry) => entry && `${entry.id}` === `${record.__queueId}`);
    if (queueIndex < 0) return;

    const entry = queue[queueIndex];
    if (!entry) return;
    const updatedEntry = {
      ...entry,
      timestamp: Date.now(),
      data: {
        type: "json",
        body: JSON.stringify(payload ?? null),
      },
    };
    const updatedQueue = [...queue];
    updatedQueue[queueIndex] = updatedEntry;
    writeOfflineQueue(updatedQueue);
  };

  const handleQueuedOfflineRequest = (namespace, targetId) => {
    if (targetId == null) return;
    const queue = readOfflineQueue();
    if (!queue.length) return;

    const records = readOfflineRecords(namespace, facilityId, cycleId);
    if (!Array.isArray(records) || records.length === 0) return;
    const recordIndex = records.findIndex((entry) => entry && `${entry.id}` === `${targetId}`);
    if (recordIndex < 0) return;
    const record = records[recordIndex] || {};
    const existingQueueId = record.__queueId ?? null;

    const existingQueueIndex =
      existingQueueId != null
        ? queue.findIndex((entry) => entry && `${entry.id}` === `${existingQueueId}`)
        : -1;

    let matchedIndex = -1;
    for (let idx = queue.length - 1; idx >= 0; idx -= 1) {
      const payload = parseQueuePayload(queue[idx]);
      if (queuePayloadMatchesRecord(namespace, payload, record)) {
        matchedIndex = idx;
        break;
      }
    }

    if (matchedIndex < 0) {
      matchedIndex = existingQueueIndex;
    }
    if (matchedIndex < 0 && queue.length) {
      matchedIndex = queue.length - 1;
    }

    if (matchedIndex < 0) {
      if (existingQueueId != null) {
        const nextRecords = [...records];
        nextRecords[recordIndex] = { ...record, __queueId: null };
        replaceOfflineRecords(namespace, facilityId, cycleId, nextRecords);
      }
      return;
    }

    const matchedEntry = queue[matchedIndex];
    if (!matchedEntry || matchedEntry.id == null) return;

    let updatedQueue = [...queue];
    let nextQueueId = existingQueueId;

    if (existingQueueIndex >= 0) {
      const existingEntry = updatedQueue[existingQueueIndex];
      if (!existingEntry) return;
      const mergedEntry = {
        ...existingEntry,
        url: matchedEntry.url ?? existingEntry.url,
        baseURL: matchedEntry.baseURL ?? existingEntry.baseURL,
        method: matchedEntry.method ?? existingEntry.method,
        headers: matchedEntry.headers ?? existingEntry.headers,
        params: matchedEntry.params ?? existingEntry.params,
        data: matchedEntry.data ?? existingEntry.data,
        timestamp: matchedEntry.timestamp ?? existingEntry.timestamp,
      };
      updatedQueue[existingQueueIndex] = mergedEntry;
      if (matchedIndex !== existingQueueIndex) {
        updatedQueue.splice(matchedIndex, 1);
        if (matchedIndex < existingQueueIndex) {
          // existing entry shifts left by one after removal
          // no action needed as we already updated slot before removal
        }
      }
    } else {
      nextQueueId = matchedEntry.id;
      if (nextQueueId == null) return;
    }

    writeOfflineQueue(updatedQueue);

    const nextRecords = [...records];
    nextRecords[recordIndex] = { ...record, __queueId: nextQueueId };
    replaceOfflineRecords(namespace, facilityId, cycleId, nextRecords);
  };

  const combineServerRowsWithOffline = (namespace, serverRows, matcher) => {
    const baseRows = Array.isArray(serverRows) ? serverRows : [];
    const storedOffline = readOfflineRecords(namespace, facilityId, cycleId);
    let offlineList = Array.isArray(storedOffline) ? storedOffline.filter(Boolean) : [];
    if (namespace === OFFLINE_NS_CONSUMPTION && offlineList.length) {
      offlineList = normalizeConsumptionRows(offlineList);
    }
    if (!offlineList.length) return baseRows;

    const queueEntries = readOfflineQueue();
    const queueIds = new Set(
      Array.isArray(queueEntries) ? queueEntries.filter(Boolean).map((entry) => `${entry.id}`) : []
    );

    const filteredOffline = offlineList.filter((entry) => {
      if (!entry?.__queueId) return true;
      return queueIds.has(`${entry.__queueId}`);
    });

    if (filteredOffline.length !== offlineList.length) {
      offlineList = filteredOffline;
      if (offlineList.length) {
        replaceOfflineRecords(namespace, facilityId, cycleId, offlineList);
      } else {
        clearOfflineRecords(namespace, facilityId, cycleId);
        return baseRows;
      }
    }

    const pending = offlineList.filter((entry) => {
      if (!entry) return false;
      if (entry.__deleted) {
        return baseRows.some((row) => row && `${row.id}` === `${entry.id}`);
      }
      const hasPersistedId = entry.id != null && !isTempIdentifier(entry.id);
      if (hasPersistedId) {
        const matchedById = baseRows.some((row) => row && `${row.id}` === `${entry.id}`);
        if (matchedById) {
          return false;
        }
      }
      return !baseRows.some((row) => matcher(entry, row));
    });

    if (pending.length === 0) {
      clearOfflineRecords(namespace, facilityId, cycleId);
      return baseRows;
    }

    if (pending.length !== offlineList.length) {
      replaceOfflineRecords(namespace, facilityId, cycleId, pending);
    }

    return mergeOfflineRecords(baseRows, pending);
  };

  const resolveOptionLabel = (options, id, fallbackRows, fallbackLabelKey, fallbackIdKey) => {
    if (id == null || id === "") return "";
    const normalized = `${id}`;
    const optionMatch =
      Array.isArray(options) &&
      options.find((opt) =>
        [opt?.id, opt?.ID, opt?.value, opt?.Id].some(
          (candidate) => candidate != null && `${candidate}` === normalized
        )
      );
    if (optionMatch) {
      return (
        optionMatch?.eventName ??
        optionMatch?.name ??
        optionMatch?.description ??
        optionMatch?.label ??
        optionMatch?.EventName ??
        optionMatch?.Description ??
        ""
      );
    }
    const fallbackMatch =
      Array.isArray(fallbackRows) &&
      fallbackRows.find(
        (row) => row && row[fallbackIdKey] != null && `${row[fallbackIdKey]}` === normalized
      );
    if (fallbackMatch) {
      return fallbackMatch[fallbackLabelKey] ?? "";
    }
    return "";
  };

  const applyOptimisticMortalityUpsert = (payload, existingId) => {
    const targetId = existingId != null ? existingId : makeTempId();
    let persistedRowData = null;
    setMortRows((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const existingEntry = next.find((row) => `${row.id}` === `${targetId}`);
      const existingQueueId = existingEntry?.__queueId;
      const hasMortEvent = Object.prototype.hasOwnProperty.call(payload, "eventMortalitiesID");
      const hasCullEvent = Object.prototype.hasOwnProperty.call(payload, "eventCullsID");
      const normalizedMortId =
        hasMortEvent && payload.eventMortalitiesID != null
          ? `${payload.eventMortalitiesID}`
          : hasMortEvent
          ? ""
          : existingEntry?.eventMortalitiesID ?? "";
      const normalizedCullId =
        hasCullEvent && payload.eventCullsID != null
          ? `${payload.eventCullsID}`
          : hasCullEvent
          ? ""
          : existingEntry?.eventCullsID ?? "";
      const mortDesc = resolveOptionLabel(
        mortOptions,
        normalizedMortId,
        next,
        "mortDesc",
        "eventMortalitiesID"
      );
      const cullDesc = resolveOptionLabel(
        cullOptions,
        normalizedCullId,
        next,
        "cullDesc",
        "eventCullsID"
      );

      const rowData = {
        id: targetId,
        facilityRef: payload.briolerID,
        mortalities: Object.prototype.hasOwnProperty.call(payload, "fatalities")
          ? payload.fatalities
          : existingEntry?.mortalities ?? "",
        mortDesc,
        eventMortalitiesID: normalizedMortId,
        culls: Object.prototype.hasOwnProperty.call(payload, "culls")
          ? payload.culls
          : existingEntry?.culls ?? "",
        cullDesc,
        eventCullsID: normalizedCullId,
        date: payload.dateTime,
        __isOffline: true,
        __pendingCreate: existingId == null,
        __pendingUpdate: existingId != null,
        __queueId: existingQueueId,
      };
      persistedRowData = rowData;

      const existingIndex = next.findIndex((row) => `${row.id}` === `${targetId}`);
      if (existingIndex >= 0) {
        next[existingIndex] = { ...next[existingIndex], ...rowData };
      } else {
        next.unshift(rowData);
      }
      return next;
    });
    if (persistedRowData) {
      upsertOfflineRecord(OFFLINE_NS_MORTALITIES, facilityId, cycleId, persistedRowData);
    }
    return targetId;
  };

  const applyOptimisticMortalityDelete = (row) => {
    if (!row) return;
    const idString = `${row.id}`;
    setMortRows((prev) =>
      Array.isArray(prev) ? prev.filter((entry) => `${entry.id}` !== idString) : []
    );
    if (idString.startsWith("temp-")) {
      removeOfflineRecord(OFFLINE_NS_MORTALITIES, facilityId, cycleId, row.id);
    } else {
      upsertOfflineRecord(OFFLINE_NS_MORTALITIES, facilityId, cycleId, {
        id: row.id,
        __deleted: true,
        __isOffline: true,
      });
    }
  };

  const buildPlacementRowFromPayload = (payload, targetId) => ({
    id: targetId,
    broilerId: Number(facilityId),
    cycleId: payload.cycleId ?? null,
    totalBirdWeight: payload.totalBirdWeight,
    averageBox: payload.averageBox,
    noOfBirdsPerBox: payload.noOfBirdsPerBox,
    date: payload.date,
  });

  const applyOptimisticPlacementUpsert = (payload, existingId) => {
    const targetId = existingId != null ? existingId : makeTempId();
    let persistedRowData = null;
    setPlacementRows((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const index = next.findIndex((row) => `${row.id}` === `${targetId}`);
      const existingQueueId = index >= 0 ? next[index]?.__queueId : undefined;
      const rowData = {
        ...buildPlacementRowFromPayload(payload, targetId),
        __isOffline: true,
        __pendingCreate: existingId == null || isTempIdentifier(existingId),
        __pendingUpdate: existingId != null && !isTempIdentifier(existingId),
        __queueId: existingQueueId,
      };
      persistedRowData = rowData;
      if (index >= 0) {
        next[index] = { ...next[index], ...rowData };
      } else {
        next.unshift(rowData);
      }
      return next;
    });
    if (persistedRowData) {
      upsertOfflineRecord(OFFLINE_NS_PLACEMENT, facilityId, cycleId, persistedRowData);
    }
    return targetId;
  };

  const applyOptimisticPlacementDelete = (row) => {
    if (!row) return;
    const idString = `${row.id}`;
    setPlacementRows((prev) =>
      Array.isArray(prev) ? prev.filter((entry) => `${entry.id}` !== idString) : []
    );
    if (isTempIdentifier(idString) || row?.__pendingCreate) {
      removeOfflineRecord(OFFLINE_NS_PLACEMENT, facilityId, cycleId, row.id);
    } else {
      upsertOfflineRecord(OFFLINE_NS_PLACEMENT, facilityId, cycleId, {
        id: row.id,
        __deleted: true,
        __isOffline: true,
      });
    }
  };

  const buildConsumptionRowFromPayload = (payload, targetId, consType = "feed") => ({
    id: targetId,
    broilerId: Number(facilityId),
    cycleId: cycleId == null ? null : Number(cycleId),
    stage: pick(payload.stage, payload.stageId, payload.stageID, payload.StageId, payload.StageID),
    feedName: pick(
      payload.feedName,
      payload.feedname,
      payload.feedId,
      payload.FeedName,
      payload.Feedname,
      payload.FeedId
    ),
    feedSpendId: pick(payload.feedSpendId, payload.FeedSpendId, payload.feed_spend_id),
    feedKg: payload.feed,
    waterSource: payload.waterSource ?? payload.sourcePercent ?? payload.source ?? null,
    waterReading: payload.waterReading ?? payload.reading ?? null,
    waterL: payload.waterConsumption,
    bin: normalizeBinSelection(payload.bin ?? payload.binSelection),
    bin1Bal: payload.bin1Bal ?? payload.bin1_bal ?? payload.bin_1_bal,
    bin2Bal: payload.bin2Bal ?? payload.bin2_bal ?? payload.bin_2_bal,
    feedpaperBal: payload.feedpaperBal ?? payload.feedpaper_bal ?? payload.feed_paper_bal,
    dateTime: payload.dateTime,
    consType,
  });

  const applyOptimisticConsumptionUpsert = (payload, existingId, consType = "feed") => {
    const targetId = existingId != null ? existingId : makeTempId();
    let persistedRowData = null;
    setConsRows((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const index = next.findIndex((row) => `${row.id}` === `${targetId}`);
      const existingQueueId = index >= 0 ? next[index]?.__queueId : undefined;
      const rowData = {
        ...buildConsumptionRowFromPayload(payload, targetId, consType),
        __isOffline: true,
        __pendingCreate: existingId == null || isTempIdentifier(existingId),
        __pendingUpdate: existingId != null && !isTempIdentifier(existingId),
        __queueId: existingQueueId,
      };
      persistedRowData = rowData;
      if (index >= 0) {
        next[index] = { ...next[index], ...rowData };
      } else {
        next.unshift(rowData);
      }
      return next;
    });
    if (persistedRowData) {
      upsertOfflineRecord(OFFLINE_NS_CONSUMPTION, facilityId, cycleId, persistedRowData);
    }
    return targetId;
  };

  const applyOptimisticConsumptionDelete = (row) => {
    if (!row) return;
    const idString = `${row.id}`;
    setConsRows((prev) =>
      Array.isArray(prev) ? prev.filter((entry) => `${entry.id}` !== idString) : []
    );
    if (isTempIdentifier(idString) || row?.__pendingCreate) {
      removeOfflineRecord(OFFLINE_NS_CONSUMPTION, facilityId, cycleId, row.id);
    } else {
      upsertOfflineRecord(OFFLINE_NS_CONSUMPTION, facilityId, cycleId, {
        id: row.id,
        consType: getConsumptionType(row),
        __deleted: true,
        __isOffline: true,
      });
    }
  };

  const buildCycleRowFromPayload = (payload, targetId) => ({
    id: targetId,
    broilerId: Number(facilityId),
    cycleId: cycleId == null ? null : Number(cycleId),
    weightBack: payload.weightBack,
    weightBackR: payload.weightBackR,
    weightCenter: payload.weightCenter,
    weightCenterR: payload.weightCenterR,
    weightFront: payload.weightFront,
    weightFrontR: payload.weightFrontR,
    sampleSize: payload.sampleSize,
    weightAverage: payload.weightAverage,
    dateTime: payload.dateTime,
  });

  const applyOptimisticCycleUpsert = (payload, existingId) => {
    const targetId = existingId != null ? existingId : makeTempId();
    let persistedRowData = null;
    setCycleRows((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const index = next.findIndex((row) => `${row.id}` === `${targetId}`);
      const existingQueueId = index >= 0 ? next[index]?.__queueId : undefined;
      const rowData = {
        ...buildCycleRowFromPayload(payload, targetId),
        __isOffline: true,
        __pendingCreate: existingId == null || isTempIdentifier(existingId),
        __pendingUpdate: existingId != null && !isTempIdentifier(existingId),
        __queueId: existingQueueId,
      };
      persistedRowData = rowData;
      if (index >= 0) {
        next[index] = { ...next[index], ...rowData };
      } else {
        next.unshift(rowData);
      }
      return next;
    });
    if (persistedRowData) {
      upsertOfflineRecord(OFFLINE_NS_CYCLE_WEIGHTS, facilityId, cycleId, persistedRowData);
    }
    return targetId;
  };

  const applyOptimisticCycleDelete = (row) => {
    if (!row) return;
    const idString = `${row.id}`;
    setCycleRows((prev) =>
      Array.isArray(prev) ? prev.filter((entry) => `${entry.id}` !== idString) : []
    );
    if (isTempIdentifier(idString) || row?.__pendingCreate) {
      removeOfflineRecord(OFFLINE_NS_CYCLE_WEIGHTS, facilityId, cycleId, row.id);
    } else {
      upsertOfflineRecord(OFFLINE_NS_CYCLE_WEIGHTS, facilityId, cycleId, {
        id: row.id,
        __deleted: true,
        __isOffline: true,
      });
    }
  };

  const buildCatchRowFromPayload = (payload, targetId) => ({
    id: targetId,
    broilerId: Number(facilityId),
    cycleId: cycleId == null ? null : Number(cycleId),
    totalweight: payload.totalweight,
    noofbirds: payload.noofbirds,
    date: payload.date,
  });

  const applyOptimisticCatchUpsert = (payload, existingId) => {
    const targetId = existingId != null ? existingId : makeTempId();
    let persistedRowData = null;
    setCatchRows((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const index = next.findIndex((row) => `${row.id}` === `${targetId}`);
      const existingQueueId = index >= 0 ? next[index]?.__queueId : undefined;
      const rowData = {
        ...buildCatchRowFromPayload(payload, targetId),
        __isOffline: true,
        __pendingCreate: existingId == null || isTempIdentifier(existingId),
        __pendingUpdate: existingId != null && !isTempIdentifier(existingId),
        __queueId: existingQueueId,
      };
      persistedRowData = rowData;
      if (index >= 0) {
        next[index] = { ...next[index], ...rowData };
      } else {
        next.unshift(rowData);
      }
      return next;
    });
    if (persistedRowData) {
      upsertOfflineRecord(OFFLINE_NS_CATCH, facilityId, cycleId, persistedRowData);
    }
    return targetId;
  };

  const applyOptimisticCatchDelete = (row) => {
    if (!row) return;
    const idString = `${row.id}`;
    setCatchRows((prev) =>
      Array.isArray(prev) ? prev.filter((entry) => `${entry.id}` !== idString) : []
    );
    if (isTempIdentifier(idString) || row?.__pendingCreate) {
      removeOfflineRecord(OFFLINE_NS_CATCH, facilityId, cycleId, row.id);
    } else {
      upsertOfflineRecord(OFFLINE_NS_CATCH, facilityId, cycleId, {
        id: row.id,
        __deleted: true,
        __isOffline: true,
      });
    }
  };

  const wasQueuedOffline = (response, error) => {
    if (navigator?.onLine === false) return true;
    if (response?.status === 202) return true;
    if (response?.data?.offline || response?.data?.queued) return true;
    if (error) {
      const status = error?.response?.status;
      if (!status && (error?.code === "ERR_NETWORK" || error?.message?.includes("Network"))) {
        return true;
      }
    }
    return false;
  };

  const submitForm = async () => {
    const existingIdValue = form.id;
    const isTempId = existingIdValue != null && isTempIdentifier(existingIdValue);
    const normalizedId = isTempId ? null : normalizePersistedId(existingIdValue);
    const isNewRecord = normalizedId == null && !isTempId;
    const optimisticId = getOptimisticId(existingIdValue);
    let effectiveDateInput = form.dateTime;
    if (isNewRecord || !effectiveDateInput) {
      effectiveDateInput = nowLocalDateTimeInput();
    }
    const payload = {
      ...(normalizedId != null ? { id: normalizedId } : {}),
      briolerID: Number(facilityId),
      cycleId: cycleId == null ? null : Number(cycleId),
      dateTime: toIsoDateTime(effectiveDateInput),
    };
    if (mortActiveView === "mortalities") {
      payload.fatalities = form.fatalities === "" ? 0 : Number(form.fatalities);
      payload.eventMortalitiesID =
        form.eventMortalitiesID === "" ? null : Number(form.eventMortalitiesID);
    } else {
      payload.culls = form.culls === "" ? 0 : Number(form.culls);
      payload.eventCullsID = form.eventCullsID === "" ? null : Number(form.eventCullsID);
    }
    const mortalityDescription = getOptionLabelById(mortOptions, form.eventMortalitiesID);
    const cullDescription = getOptionLabelById(cullOptions, form.eventCullsID);
    const isPendingCreate = existingIdValue != null && isTempIdentifier(existingIdValue);
    if (isPendingCreate) {
      const targetId = applyOptimisticMortalityUpsert(payload, optimisticId ?? null);
      handleQueuedOfflineRequest(OFFLINE_NS_MORTALITIES, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_MORTALITIES, targetId, payload);
      setEditOpen(false);
      setMortalityImageFile(null);
      if (mortalityUploadInputRef.current) mortalityUploadInputRef.current.value = "";
      if (mortalityCameraInputRef.current) mortalityCameraInputRef.current.value = "";
      setSnackMessage(
        mortalityImageFile
          ? "Recording saved locally. Image upload requires an online save."
          : "Recording saved locally. It will sync when you're online."
      );
      setSnackOpen(true);
      return;
    }

    let response;
    try {
      response = await axios.post(updateMortalitiesUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        const targetId = applyOptimisticMortalityUpsert(payload, optimisticId ?? null);
        handleQueuedOfflineRequest(OFFLINE_NS_MORTALITIES, targetId);
        updateQueuePayloadForRecord(OFFLINE_NS_MORTALITIES, targetId, payload);
        setEditOpen(false);
        setMortalityImageFile(null);
        if (mortalityUploadInputRef.current) mortalityUploadInputRef.current.value = "";
        if (mortalityCameraInputRef.current) mortalityCameraInputRef.current.value = "";
        setSnackMessage(
          mortalityImageFile
            ? "Recording saved locally. Image upload requires an online save."
            : "Recording saved locally. It will sync when you're online."
        );
        setSnackOpen(true);
        return;
      }
      console.error("Failed to save mortalities record", error);
      setSnackMessage("Failed to save recording");
      setSnackOpen(true);
      return;
    }

    if (wasQueuedOffline(response)) {
      const targetId = applyOptimisticMortalityUpsert(payload, optimisticId ?? null);
      handleQueuedOfflineRequest(OFFLINE_NS_MORTALITIES, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_MORTALITIES, targetId, payload);
      setEditOpen(false);
      setMortalityImageFile(null);
      if (mortalityUploadInputRef.current) mortalityUploadInputRef.current.value = "";
      if (mortalityCameraInputRef.current) mortalityCameraInputRef.current.value = "";
      setSnackMessage(
        mortalityImageFile
          ? "Recording saved locally. Image upload requires an online save."
          : "Recording saved locally. It will sync when you're online."
      );
      setSnackOpen(true);
      return;
    }

    const savedAtIso = new Date().toISOString();
    const uploadResult = await uploadMortalityImage({
      savedAtIso,
      mortalityDescription,
      cullDescription,
    });
    const persistedRecordId = pick(
      response?.data?.id,
      response?.data?.ID,
      response?.data?.Id,
      payload?.id,
      payload?.ID,
      payload?.Id,
      existingIdValue
    );
    if (uploadResult.succeeded && uploadResult.blobName && persistedRecordId != null) {
      rememberMortalityImageBlobName(persistedRecordId, uploadResult.blobName);
    }

    setEditOpen(false);
    setSnackMessage(
      uploadResult.attempted && !uploadResult.succeeded
        ? "Recording saved. Image upload failed (check browser CORS/SAS settings)."
        : uploadResult.succeeded
        ? "Recording and image saved successfully"
        : "Recording saved successfully"
    );
    setSnackOpen(true);
    setMortalityImageFile(null);
    if (mortalityUploadInputRef.current) mortalityUploadInputRef.current.value = "";
    if (mortalityCameraInputRef.current) mortalityCameraInputRef.current.value = "";
    await refreshMortalities();
  };
  // Sorting helpers
  const getSortValue = (row, key) => {
    switch (key) {
      case "date": {
        const d = toDateOnly(row.date);
        const t = Date.parse(d || "");
        return isNaN(t) ? 0 : t;
      }
      case "mortalities":
        return Number(row.mortalities ?? 0);
      case "culls":
        return Number(row.culls ?? 0);
      case "mortDesc":
        return String(row.mortDesc || "").toLowerCase();
      case "cullDesc":
        return String(row.cullDesc || "").toLowerCase();
      default:
        return 0;
    }
  };

  const hasMortalityData = (row) => {
    const mortVal = Number(row?.mortalities ?? 0);
    const hasCount = Number.isFinite(mortVal) && mortVal > 0;
    const hasDesc = String(row?.mortDesc ?? "").trim().length > 0;
    return hasCount || hasDesc;
  };

  const hasCullData = (row) => {
    const cullVal = Number(row?.culls ?? 0);
    const hasCount = Number.isFinite(cullVal) && cullVal > 0;
    const hasDesc = String(row?.cullDesc ?? "").trim().length > 0;
    return hasCount || hasDesc;
  };

  const sortedRows = (() => {
    const rows = Array.isArray(mortRows)
      ? mortRows.filter((row) =>
          mortActiveView === "mortalities" ? hasMortalityData(row) : hasCullData(row)
        )
      : [];
    rows.sort((a, b) => {
      const va = getSortValue(a, orderBy);
      const vb = getSortValue(b, orderBy);
      if (va < vb) return order === "asc" ? -1 : 1;
      if (va > vb) return order === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  })();

  const handleRequestSort = (property) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(1); // reset to first page when sorting changes
  };

  const handleMortViewChange = (view) => {
    if (view === mortActiveView) return;
    setMortActiveView(view);
    setOrderBy("date");
    setOrder("desc");
    setPage(1);
  };

  const performDelete = async (row) => {
    if (!row?.id) return;
    const idString = `${row.id}`;
    if (isTempIdentifier(idString) || row?.__pendingCreate) {
      applyOptimisticMortalityDelete(row);
      setSnackMessage("Pending record removed locally.");
      setSnackOpen(true);
      return;
    }

    let response;
    try {
      response = await axios.post(
        removeMortalitiesUrl(),
        { id: row.id },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        applyOptimisticMortalityDelete(row);
        setSnackMessage("Recording removal queued. It will sync when you're online.");
        setSnackOpen(true);
      } else {
        console.error("Failed to delete mortalities record", error);
        setSnackMessage("Failed to delete recording");
        setSnackOpen(true);
      }
      return;
    }

    if (wasQueuedOffline(response)) {
      applyOptimisticMortalityDelete(row);
      setSnackMessage("Recording removal queued. It will sync when you're online.");
      setSnackOpen(true);
      return;
    }

    await refreshMortalities();
    setSnackMessage("Recording deleted successfully");
    setSnackOpen(true);
  };
  // Placement weights: add/edit handlers
  const openPlacementAdd = () => {
    setPlacementForm({
      id: null,
      totalBirdWeight: "",
      averageBox: "",
      noOfBirdsPerBox: "",
      date: nowLocalDateTimeInput(),
      broilerId: facilityId != null ? String(facilityId) : null,
      cycleId: cycleId != null ? String(cycleId) : null,
    });
    setPlacementEditOpen(true);
  };

  const openPlacementEdit = (row) => {
    setPlacementForm({
      id: row?.id ?? null,
      totalBirdWeight: row?.totalBirdWeight ?? "",
      averageBox: row?.averageBox ?? "",
      noOfBirdsPerBox: row?.noOfBirdsPerBox ?? "",
      date: toLocalDateTimeInput(row?.date) || "",
    });
    setPlacementEditOpen(true);
  };

  const closePlacementEdit = () => setPlacementEditOpen(false);

  const openPlacementDelete = (row) => {
    setPlacementDeleteTarget(row);
    setPlacementDeleteOpen(true);
  };

  const performPlacementDelete = async () => {
    const row = placementDeleteTarget;
    if (!row?.id) {
      setPlacementDeleteOpen(false);
      setPlacementDeleteTarget(null);
      return;
    }

    const idString = `${row.id}`;
    if (isTempIdentifier(idString) || row?.__pendingCreate) {
      applyOptimisticPlacementDelete(row);
      setSnackMessage("Placement removed locally.");
      setSnackOpen(true);
      setPlacementDeleteOpen(false);
      setPlacementDeleteTarget(null);
      return;
    }

    let response;
    try {
      response = await axios.post(
        removePlacementWeightUrl(),
        { id: row.id },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        applyOptimisticPlacementDelete(row);
        setSnackMessage("Placement removal queued. It will sync when you're online.");
        setSnackOpen(true);
      } else {
        console.error("Failed to delete placement record", error);
        setSnackMessage("Failed to delete placement record");
        setSnackOpen(true);
      }
      setPlacementDeleteOpen(false);
      setPlacementDeleteTarget(null);
      return;
    }

    if (wasQueuedOffline(response)) {
      applyOptimisticPlacementDelete(row);
      setSnackMessage("Placement removal queued. It will sync when you're online.");
      setSnackOpen(true);
      setPlacementDeleteOpen(false);
      setPlacementDeleteTarget(null);
      return;
    }

    try {
      if (cycleId != null) {
        const res = await axios.get(placementWeightsUrl(facilityId, cycleId));
        const rows = mapPlacementRows(res.data, facilityId);
        const merged = combineServerRowsWithOffline(OFFLINE_NS_PLACEMENT, rows, matchPlacementRow);
        setPlacementRows(merged);
      } else {
        setPlacementRows([]);
      }
    } catch (refreshError) {
      console.error("Failed to refresh placement weights", refreshError);
    }

    setSnackMessage("Placement deleted successfully");
    setSnackOpen(true);
    setPlacementDeleteOpen(false);
    setPlacementDeleteTarget(null);
  };

  // During-cycle weights: add/edit handlers
  const openCycleAdd = () => {
    setCycleForm({
      id: null,
      weightBack: "",
      weightBackR: "",
      weightCenter: "",
      weightCenterR: "",
      weightFront: "",
      weightFrontR: "",
      sampleSize: "",
      weightAverage: "",
      dateTime: nowLocalDateTimeInput(),
    });
    setCycleEditOpen(true);
  };

  const openCycleEdit = (row) => {
    setCycleForm({
      id: row?.id ?? null,
      weightBack: row?.weightBack ?? "",
      weightBackR: row?.weightBackR ?? "",
      weightCenter: row?.weightCenter ?? "",
      weightCenterR: row?.weightCenterR ?? "",
      weightFront: row?.weightFront ?? "",
      weightFrontR: row?.weightFrontR ?? "",
      sampleSize: row?.sampleSize ?? "",
      // weightAverage may not be present in list; keep empty to allow manual input
      weightAverage: row?.weightAverage ?? "",
      dateTime: toLocalDateTimeInput(row?.dateTime) || "",
    });
    setCycleEditOpen(true);
  };

  const closeCycleEdit = () => setCycleEditOpen(false);

  const handleCycleDateSort = () => {
    setCycleDateOrder((order) => (order === "asc" ? "desc" : "asc"));
  };

  const sortedCycleRows = useMemo(() => {
    const direction = cycleDateOrder === "asc" ? 1 : -1;
    return [...cycleRows].sort((a, b) => {
      const aTime = getDateTimestamp(a?.dateTime);
      const bTime = getDateTimestamp(b?.dateTime);
      const aHasDate = Number.isFinite(aTime);
      const bHasDate = Number.isFinite(bTime);

      if (!aHasDate && !bHasDate) return 0;
      if (!aHasDate) return 1;
      if (!bHasDate) return -1;
      return (aTime - bTime) * direction;
    });
  }, [cycleRows, cycleDateOrder]);

  const submitCycleForm = async () => {
    const existingIdValue = cycleForm.id;
    const isTempId = existingIdValue != null && isTempIdentifier(existingIdValue);
    const optimisticId = getOptimisticId(existingIdValue);
    const resolvedExistingId = isTempId
      ? null
      : existingIdValue != null
      ? normalizePersistedId(existingIdValue)
      : null;
    const isNewRecord = resolvedExistingId == null && !isTempId;
    let effectiveDateInput = cycleForm.dateTime;
    if (isNewRecord || !effectiveDateInput) {
      effectiveDateInput = nowLocalDateTimeInput();
    }
    const payload = {
      broilerID: Number(facilityId),
      cycleId: cycleId == null ? null : Number(cycleId),
      weightBack: cycleForm.weightBack === "" ? null : Number(cycleForm.weightBack),
      weightBackR: cycleForm.weightBackR === "" ? null : Number(cycleForm.weightBackR),
      weightCenter: cycleForm.weightCenter === "" ? null : Number(cycleForm.weightCenter),
      weightCenterR: cycleForm.weightCenterR === "" ? null : Number(cycleForm.weightCenterR),
      weightFront: cycleForm.weightFront === "" ? null : Number(cycleForm.weightFront),
      weightFrontR: cycleForm.weightFrontR === "" ? null : Number(cycleForm.weightFrontR),
      sampleSize: cycleForm.sampleSize === "" ? null : Number(cycleForm.sampleSize),
      weightAverage: cycleForm.weightAverage === "" ? null : Number(cycleForm.weightAverage),
      dateTime: toIsoDateTimeFromInput(effectiveDateInput),
    };
    if (resolvedExistingId != null) {
      payload.id = resolvedExistingId;
      payload.ID = resolvedExistingId;
      payload.Id = resolvedExistingId;
    }
    const isPendingCreate = existingIdValue != null && isTempIdentifier(existingIdValue);
    if (isPendingCreate) {
      const targetId = applyOptimisticCycleUpsert(payload, optimisticId ?? null);
      handleQueuedOfflineRequest(OFFLINE_NS_CYCLE_WEIGHTS, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_CYCLE_WEIGHTS, targetId, payload);
      setCycleEditOpen(false);
      setSnackMessage("Weights saved locally. They will sync when you're online.");
      setSnackOpen(true);
      return;
    }

    let response;
    try {
      response = await axios.post(updateBroilerWeightsUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        const targetId = applyOptimisticCycleUpsert(payload, optimisticId ?? null);
        handleQueuedOfflineRequest(OFFLINE_NS_CYCLE_WEIGHTS, targetId);
        updateQueuePayloadForRecord(OFFLINE_NS_CYCLE_WEIGHTS, targetId, payload);
        setCycleEditOpen(false);
        setSnackMessage("Weights saved locally. They will sync when you're online.");
        setSnackOpen(true);
        return;
      }
      console.error("Failed to save weights", error);
      setSnackMessage("Failed to save weights");
      setSnackOpen(true);
      return;
    }

    if (wasQueuedOffline(response)) {
      const targetId = applyOptimisticCycleUpsert(payload, optimisticId ?? null);
      handleQueuedOfflineRequest(OFFLINE_NS_CYCLE_WEIGHTS, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_CYCLE_WEIGHTS, targetId, payload);
      setCycleEditOpen(false);
      setSnackMessage("Weights saved locally. They will sync when you're online.");
      setSnackOpen(true);
      return;
    }

    try {
      if (cycleId != null) {
        const res = await axios.get(broilerWeightsUrl(facilityId, cycleId));
        const rows = mapCycleWeightRows(res.data, facilityId, cycleId);
        const merged = combineServerRowsWithOffline(
          OFFLINE_NS_CYCLE_WEIGHTS,
          rows,
          matchCycleWeightRow
        );
        setCycleRows(merged);
      } else {
        setCycleRows([]);
      }
    } catch (refreshError) {
      console.error("Failed to refresh weights", refreshError);
    }

    setCycleEditOpen(false);
    setSnackMessage("Weights saved successfully");
    setSnackOpen(true);
  };

  const openCycleDelete = (row) => {
    setCycleDeleteTarget(row);
    setCycleDeleteOpen(true);
  };

  const performCycleDelete = async () => {
    const row = cycleDeleteTarget;
    if (!row?.id) {
      setCycleDeleteOpen(false);
      setCycleDeleteTarget(null);
      return;
    }
    const idString = `${row.id}`;
    if (isTempIdentifier(idString) || row?.__pendingCreate) {
      applyOptimisticCycleDelete(row);
      setSnackMessage("Weight record removed locally.");
      setSnackOpen(true);
      setCycleDeleteOpen(false);
      setCycleDeleteTarget(null);
      return;
    }

    let response;
    try {
      response = await axios.post(
        removeBroilerWeightUrl(),
        { id: row.id },
        { headers: { "Content-Type": "application/json" } }
      );
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        applyOptimisticCycleDelete(row);
        setSnackMessage("Weight removal queued. It will sync when you're online.");
        setSnackOpen(true);
      } else {
        console.error("Failed to delete weight record", error);
        setSnackMessage("Failed to delete weight record");
        setSnackOpen(true);
      }
      setCycleDeleteOpen(false);
      setCycleDeleteTarget(null);
      return;
    }

    if (wasQueuedOffline(response)) {
      applyOptimisticCycleDelete(row);
      setSnackMessage("Weight removal queued. It will sync when you're online.");
      setSnackOpen(true);
      setCycleDeleteOpen(false);
      setCycleDeleteTarget(null);
      return;
    }

    try {
      if (cycleId != null) {
        const res = await axios.get(broilerWeightsUrl(facilityId, cycleId));
        const rows = mapCycleWeightRows(res.data, facilityId, cycleId);
        const merged = combineServerRowsWithOffline(
          OFFLINE_NS_CYCLE_WEIGHTS,
          rows,
          matchCycleWeightRow
        );
        setCycleRows(merged);
      } else {
        setCycleRows([]);
      }
    } catch (refreshError) {
      console.error("Failed to refresh weights", refreshError);
    }

    setSnackMessage("Weight record deleted successfully");
    setSnackOpen(true);
    setCycleDeleteOpen(false);
    setCycleDeleteTarget(null);
  };
  const submitPlacementForm = async () => {
    const existingIdValue = placementForm.id;
    const isTempId = existingIdValue != null && isTempIdentifier(existingIdValue);
    const optimisticId = getOptimisticId(existingIdValue);
    const resolvedExistingId = isTempId
      ? null
      : existingIdValue != null
      ? normalizePersistedId(existingIdValue)
      : null;
    const isNewRecord = resolvedExistingId == null && !isTempId;
    let effectiveDateInput = placementForm.date;
    if (isNewRecord || !effectiveDateInput) {
      effectiveDateInput = nowLocalDateTimeInput();
    }
    const payload = {
      totalBirdWeight:
        placementForm.totalBirdWeight === "" ? 0 : Number(placementForm.totalBirdWeight),
      averageBox: placementForm.averageBox === "" ? 0 : Number(placementForm.averageBox),
      noOfBirdsPerBox:
        placementForm.noOfBirdsPerBox === "" ? 0 : Number(placementForm.noOfBirdsPerBox),
      date: toIsoDateTimeFromInput(effectiveDateInput),
      broilerId: Number(facilityId),
      cycleId: cycleId == null ? null : Number(cycleId),
    };
    if (resolvedExistingId != null) {
      payload.id = resolvedExistingId;
      payload.ID = resolvedExistingId;
      payload.Id = resolvedExistingId;
    }
    const isPendingCreate = existingIdValue != null && isTempIdentifier(existingIdValue);
    if (isPendingCreate) {
      const targetId = applyOptimisticPlacementUpsert(payload, optimisticId ?? null);
      handleQueuedOfflineRequest(OFFLINE_NS_PLACEMENT, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_PLACEMENT, targetId, payload);
      setPlacementEditOpen(false);
      setSnackMessage("Placement saved locally. It will sync when you're online.");
      setSnackOpen(true);
      return;
    }

    let response;
    try {
      response = await axios.post(updateWeightUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        const targetId = applyOptimisticPlacementUpsert(payload, optimisticId ?? null);
        handleQueuedOfflineRequest(OFFLINE_NS_PLACEMENT, targetId);
        updateQueuePayloadForRecord(OFFLINE_NS_PLACEMENT, targetId, payload);
        setPlacementEditOpen(false);
        setSnackMessage("Placement saved locally. It will sync when you're online.");
        setSnackOpen(true);
        return;
      }
      console.error("Failed to save placement record", error);
      setSnackMessage("Failed to save placement record");
      setSnackOpen(true);
      return;
    }

    if (wasQueuedOffline(response)) {
      const targetId = applyOptimisticPlacementUpsert(payload, optimisticId ?? null);
      handleQueuedOfflineRequest(OFFLINE_NS_PLACEMENT, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_PLACEMENT, targetId, payload);
      setPlacementEditOpen(false);
      setSnackMessage("Placement saved locally. It will sync when you're online.");
      setSnackOpen(true);
      return;
    }

    try {
      if (cycleId != null) {
        const res = await axios.get(placementWeightsUrl(facilityId, cycleId));
        const rows = mapPlacementRows(res.data, facilityId);
        const merged = combineServerRowsWithOffline(OFFLINE_NS_PLACEMENT, rows, matchPlacementRow);
        setPlacementRows(merged);
      } else {
        setPlacementRows([]);
      }
    } catch (refreshError) {
      console.error("Failed to refresh placement weights", refreshError);
    }

    setPlacementEditOpen(false);
    setSnackMessage("Placement saved successfully");
    setSnackOpen(true);
  };
  const requestDelete = (row) => {
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  return (
    <>
      <Button
        fullWidth
        variant="outlined"
        sx={{ color: "grey.600" }}
        onClick={() => setOpenDialog("mortalities")}
      >
        Mortalities
      </Button>

      <Button
        fullWidth
        variant="outlined"
        sx={{ mt: 2, color: "grey.600" }}
        onClick={() => setOpenDialog("weights")}
      >
        Weights
      </Button>

      <Button
        fullWidth
        variant="outlined"
        sx={{ mt: 2, color: "grey.600" }}
        onClick={() => setOpenDialog("consumption")}
      >
        Consumption
      </Button>

      <Button
        fullWidth
        variant="outlined"
        sx={{ mt: 2, color: "grey.600" }}
        onClick={() => setOpenDialog("notifications")}
      >
        Notifications
      </Button>

      {/* Dialogs */}
      <Dialog
        open={openDialog === "mortalities"}
        onClose={() => setOpenDialog(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { minHeight: "70vh" } }}
      >
        <DialogTitle sx={{ position: "relative", pr: 6 }}>
          Mortalities and Culls
          <IconButton
            aria-label="close"
            size="small"
            title="Close"
            onClick={() => setOpenDialog(null)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ position: "relative", pb: 2, px: { xs: 1, sm: 2 }, overflowX: "hidden" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 2,
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", gap: 1.5 }}>
              {[
                { label: "Mortalities", value: "mortalities" },
                { label: "Culls", value: "culls" },
              ].map((option) => (
                <Button
                  key={option.value}
                  variant={mortActiveView === option.value ? "contained" : "outlined"}
                  onClick={() => handleMortViewChange(option.value)}
                  sx={{
                    minWidth: 112,
                    color: mortActiveView === option.value ? "#fff" : "grey.800",
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Box>
            {(() => {
              const start = (page - 1) * pageSize;
              const end = start + pageSize;
              const pageRows = sortedRows.slice(start, end);
              const isMortalityView = mortActiveView === "mortalities";
              return (
                <TableContainer
                  component={Box}
                  sx={{
                    boxShadow: "none",
                    width: "fit-content",
                    maxWidth: "100%",
                    mx: "auto",
                    overflowX: "auto",
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      tableLayout: "fixed",
                      width: "17.25cm",
                      minWidth: "17.25cm",
                      borderCollapse: "collapse",
                      "& th, & td": {
                        border: "1px solid",
                        borderColor: "grey.300",
                        fontSize: "0.9rem",
                        lineHeight: 1.2,
                        p: 0.5,
                        textAlign: "center",
                        color: "text.primary",
                      },
                    }}
                  >
                    <colgroup>
                      <col style={{ width: "4.35cm" }} />
                      <col style={{ width: "6.05cm" }} />
                      <col style={{ width: "4.4cm" }} />
                      <col style={{ width: "2.45cm" }} />
                    </colgroup>
                    <TableBody>
                      <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={orderBy === (isMortalityView ? "mortalities" : "culls")}
                            direction={
                              orderBy === (isMortalityView ? "mortalities" : "culls")
                                ? order
                                : "asc"
                            }
                            onClick={handleRequestSort(isMortalityView ? "mortalities" : "culls")}
                          >
                            {isMortalityView ? "Mortalities" : "Culls"}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={orderBy === (isMortalityView ? "mortDesc" : "cullDesc")}
                            direction={
                              orderBy === (isMortalityView ? "mortDesc" : "cullDesc")
                                ? order
                                : "asc"
                            }
                            onClick={handleRequestSort(isMortalityView ? "mortDesc" : "cullDesc")}
                          >
                            {isMortalityView ? "Mortality Description" : "Cull Description"}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={orderBy === "date"}
                            direction={orderBy === "date" ? order : "asc"}
                            onClick={handleRequestSort("date")}
                          >
                            Date
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <IconButton
                            size="small"
                            aria-label="add"
                            onClick={openAdd}
                            sx={{
                              width: 28,
                              height: 28,
                              bgcolor: "grey.100",
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "grey.800",
                              "&:hover": { bgcolor: "grey.200" },
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {pageRows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ py: 2, color: "text.secondary" }}
                          >
                            No data
                          </TableCell>
                        </TableRow>
                      ) : (
                        pageRows.map((r, idx) => (
                          <TableRow key={idx} sx={{ height: "0.64cm" }}>
                            <TableCell>
                              {isMortalityView ? r.mortalities ?? "" : r.culls ?? ""}
                            </TableCell>
                            <TableCell>
                              {isMortalityView ? r.mortDesc ?? "" : r.cullDesc ?? ""}
                            </TableCell>
                            <TableCell>{formatDisplayDateTime(r.date)}</TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  gap: 1,
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "100%",
                                }}
                              >
                                <EditIcon
                                  onClick={() => openEdit(r)}
                                  sx={{ fontSize: 30, color: "text.secondary", cursor: "pointer" }}
                                />
                                <DeleteIcon
                                  onClick={() => requestDelete(r)}
                                  sx={{ fontSize: 30, color: "text.secondary", cursor: "pointer" }}
                                />
                                <VisibilityIcon
                                  onClick={() => {
                                    if (hasMortalityImage(r)) viewMortalityImage(r);
                                  }}
                                  sx={{
                                    fontSize: 30,
                                    color: hasMortalityImage(r) ? "text.secondary" : "grey.400",
                                    cursor: hasMortalityImage(r) ? "pointer" : "not-allowed",
                                  }}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })()}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              {Array.from(
                { length: Math.max(1, Math.ceil(sortedRows.length / pageSize)) },
                (_, i) => i + 1
              ).map((n) =>
                n === page ? (
                  <Box
                    key={n}
                    sx={{
                      px: 1,
                      py: 0.25,
                      bgcolor: "grey.300",
                      color: "text.primary",
                      borderRadius: 1,
                      minWidth: 24,
                      textAlign: "center",
                      fontSize: "0.9rem",
                    }}
                  >
                    {n}
                  </Box>
                ) : (
                  <Button
                    key={n}
                    size="small"
                    variant="text"
                    onClick={() => setPage(n)}
                    sx={{ minWidth: 0, color: "text.primary", textDecoration: "underline" }}
                  >
                    {n}
                  </Button>
                )
              )}
              <Button
                size="small"
                variant="text"
                onClick={() =>
                  setPage((p) =>
                    Math.min(Math.max(1, Math.ceil(sortedRows.length / pageSize)), p + 1)
                  )
                }
                disabled={page >= Math.max(1, Math.ceil(sortedRows.length / pageSize))}
                sx={{ ml: 1 }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Duplicate mortality/cull add confirmation dialog */}
      <Dialog
        open={duplicateAddOpen}
        onClose={() => setDuplicateAddOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1.5 }}>Add Another Record</DialogTitle>
        <DialogContent dividers>
          A record already exists today, do you want to add another?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ color: "#fff" }}
            onClick={() => {
              if (duplicateAddView) setMortActiveView(duplicateAddView);
              setDuplicateAddOpen(false);
              setDuplicateAddView(null);
              openAddDialog();
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Catch Add/Edit Dialog */}
      <Dialog open={catchEditOpen} onClose={closeCatchEdit} fullWidth maxWidth="sm">
        <DialogTitle>{catchForm.id ? "Edit Catch" : "Add Catch"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Total weight (kg)"
              type="number"
              value={catchForm.totalweight}
              onChange={(e) => setCatchForm((f) => ({ ...f, totalweight: e.target.value }))}
              inputProps={{ min: 0, step: "any" }}
            />
            <TextField
              label="Total no. of birds"
              type="number"
              value={catchForm.noofbirds}
              onChange={(e) => setCatchForm((f) => ({ ...f, noofbirds: e.target.value }))}
              inputProps={{ min: 0 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCatchEdit}>Cancel</Button>
          <Button variant="contained" onClick={submitCatchForm} sx={{ color: "#fff" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Catch confirmation dialog */}
      <Dialog
        open={catchDeleteOpen}
        onClose={() => setCatchDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1.5 }}>Delete Catch</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this catch record?</DialogContent>
        <DialogActions>
          <Button onClick={() => setCatchDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" sx={{ color: "#fff" }} onClick={performCatchDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Row Dialog */}
      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <Box>
            {mortActiveView === "mortalities"
              ? form.id
                ? "Edit Mortality"
                : "Add Mortality"
              : form.id
              ? "Edit Cull"
              : "Add Cull"}
          </Box>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="text"
              size="small"
              onClick={openMortalityUpload}
              sx={{ minWidth: "auto", px: 0.5, textTransform: "none", textDecoration: "underline" }}
            >
              Upload
            </Button>
            <IconButton
              aria-label="capture mortality image"
              title="Capture image"
              size="small"
              onClick={openMortalityCamera}
            >
              <CameraAltIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <input
            ref={mortalityUploadInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,image/png,image/jpeg"
            style={{ display: "none" }}
            onChange={handleMortalityImageChange}
          />
          <input
            ref={mortalityCameraInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,image/png,image/jpeg"
            capture="environment"
            style={{ display: "none" }}
            onChange={handleMortalityImageChange}
          />
          {mortActiveView === "mortalities" ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                label="Mortalities"
                type="number"
                value={form.fatalities}
                onChange={(e) => setForm((f) => ({ ...f, fatalities: e.target.value }))}
                inputProps={{ min: 0 }}
              />
              <FormControl
                sx={{
                  "& .MuiInputBase-root": { height: "1.18cm" },
                  "& .MuiSelect-select": {
                    height: "1.15cm",
                    display: "flex",
                    alignItems: "center",
                    py: 0,
                  },
                }}
              >
                <InputLabel id="mort-desc-label">Mortality Description</InputLabel>
                <Select
                  labelId="mort-desc-label"
                  label="Mortality Description"
                  value={form.eventMortalitiesID}
                  onChange={(e) => setForm((f) => ({ ...f, eventMortalitiesID: e.target.value }))}
                >
                  {mortOptions.map((o) => (
                    <MenuItem key={o?.id ?? o?.ID} value={String(o?.id ?? o?.ID)}>
                      {o?.eventName ?? o?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                label="Culls"
                type="number"
                value={form.culls}
                onChange={(e) => setForm((f) => ({ ...f, culls: e.target.value }))}
                inputProps={{ min: 0 }}
              />
              <FormControl
                sx={{
                  "& .MuiInputBase-root": { height: "1.18cm" },
                  "& .MuiSelect-select": {
                    height: "1.15cm",
                    display: "flex",
                    alignItems: "center",
                    py: 0,
                  },
                }}
              >
                <InputLabel id="cull-desc-label">Cull Description</InputLabel>
                <Select
                  labelId="cull-desc-label"
                  label="Cull Description"
                  value={form.eventCullsID}
                  onChange={(e) => setForm((f) => ({ ...f, eventCullsID: e.target.value }))}
                >
                  {cullOptions.map((o) => (
                    <MenuItem key={o?.id ?? o?.ID} value={String(o?.id ?? o?.ID)}>
                      {o?.eventName ?? o?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {mortalityImageFile ? (
            <Typography sx={{ mt: 1, fontSize: "0.85rem", color: "text.secondary" }}>
              Selected image: {mortalityImageFile.name}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button variant="contained" onClick={submitForm} sx={{ color: "#fff" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Placement Add/Edit Dialog */}
      <Dialog open={placementEditOpen} onClose={closePlacementEdit} fullWidth maxWidth="sm">
        <DialogTitle>{placementForm.id ? "Edit Placement" : "Add Placement"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Total weight (kg)"
              type="number"
              value={placementForm.totalBirdWeight}
              onChange={(e) => setPlacementForm((f) => ({ ...f, totalBirdWeight: e.target.value }))}
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Avg. box weight (5%)"
              type="number"
              value={placementForm.averageBox}
              onChange={(e) => setPlacementForm((f) => ({ ...f, averageBox: e.target.value }))}
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Total no. of birds"
              type="number"
              value={placementForm.noOfBirdsPerBox}
              onChange={(e) => setPlacementForm((f) => ({ ...f, noOfBirdsPerBox: e.target.value }))}
              inputProps={{ min: 0 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePlacementEdit}>Cancel</Button>
          <Button variant="contained" onClick={submitPlacementForm} sx={{ color: "#fff" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Placement confirmation dialog */}
      <Dialog
        open={placementDeleteOpen}
        onClose={() => setPlacementDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1.5 }}>Delete Placement</DialogTitle>
        <DialogContent dividers>
          {" "}
          Are you sure you want to delete this placement record?{" "}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlacementDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" sx={{ color: "#fff" }} onClick={performPlacementDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Delete Recording</DialogTitle>
        <DialogContent dividers> Are you sure you want to delete this record? </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ color: "#fff" }}
            onClick={() => {
              const target = deleteTarget;
              setDeleteOpen(false);
              setDeleteTarget(null);
              if (target) performDelete(target);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save/Delete Snackbars */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={2500}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: "100%" }}>
          {snackMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDialog === "weights"}
        onClose={() => setOpenDialog(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ position: "relative", pr: 6 }}>
          Weights (kg)
          <IconButton
            aria-label="close"
            size="small"
            title="Close"
            onClick={() => setOpenDialog(null)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ position: "relative", pb: 2, px: { xs: 1, sm: 2 } }}>
          {/* Tabs */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 0.5,
                bgcolor: "grey.200",
                borderRadius: 1,
                width: { xs: "100%", md: "60%" },
              }}
            >
              {[
                { key: "placement", label: "Placement" },
                { key: "cycle", label: "During Cycle" },
                { key: "catch", label: "Catch" },
              ].map((t) => (
                <Button
                  key={t.key}
                  onClick={() => setWeightTab(t.key)}
                  variant={weightTab === t.key ? "outlined" : "text"}
                  sx={{
                    color: "text.primary",
                    bgcolor: weightTab === t.key ? "white" : "grey.200",
                    borderColor: weightTab === t.key ? "info.main" : "transparent",
                    textTransform: "none",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                  }}
                >
                  {t.label}
                </Button>
              ))}
            </Box>
          </Box>

          {weightTab === "placement" && (
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <TableContainer
                component={Box}
                sx={{ boxShadow: "none", mx: "auto", width: "fit-content" }}
              >
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "24.41cm",
                    minWidth: "24.41cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontSize: "0.9rem",
                      lineHeight: 1.2,
                      p: 0.5,
                      textAlign: "center",
                      color: "text.primary",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "5.2cm" }} />
                    <col style={{ width: "5.2cm" }} />
                    <col style={{ width: "5.2cm" }} />
                    <col style={{ width: "4.2cm" }} />
                    <col style={{ width: "1.5cm" }} />
                  </colgroup>
                  <TableBody>
                    <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Total weight</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Avg. box weight (5%)</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Total no. of birds</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={cycleDateOrder}
                          onClick={handleCycleDateSort}
                        >
                          Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <IconButton
                          size="small"
                          aria-label="add"
                          onClick={openPlacementAdd}
                          disabled={cycleId == null}
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.800",
                            "&:hover": { bgcolor: "grey.200" },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {(placementRows.length === 0 ? [] : placementRows).length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          No data
                        </TableCell>
                      </TableRow>
                    ) : (
                      placementRows.map((r, idx) => (
                        <TableRow key={idx} sx={{ height: "0.64cm" }}>
                          <TableCell>{r.totalBirdWeight ?? ""}</TableCell>
                          <TableCell>{r.averageBox ?? ""}</TableCell>
                          <TableCell>{r.noOfBirdsPerBox ?? ""}</TableCell>
                          <TableCell>{formatDisplayDateTime(r.date)}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "inline-flex",
                                gap: 1,
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              <EditIcon
                                onClick={() => openPlacementEdit(r)}
                                sx={{ fontSize: 22, color: "text.secondary", cursor: "pointer" }}
                              />
                              <DeleteIcon
                                onClick={() => openPlacementDelete(r)}
                                sx={{ fontSize: 22, color: "text.secondary", cursor: "pointer" }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {weightTab === "catch" && (
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <TableContainer
                component={Box}
                sx={{ boxShadow: "none", mx: "auto", width: "fit-content" }}
              >
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "21.77cm",
                    minWidth: "21.77cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontSize: "0.9rem",
                      lineHeight: 1.2,
                      p: 0.5,
                      textAlign: "center",
                      color: "text.primary",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "6.5cm" }} />
                    <col style={{ width: "6.5cm" }} />
                    <col style={{ width: "6.5cm" }} />
                    <col style={{ width: "1.5cm" }} />
                  </colgroup>
                  <TableBody>
                    <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Total weight</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Total no. of birds</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <IconButton
                          size="small"
                          aria-label="add"
                          onClick={openCatchAdd}
                          disabled={cycleId == null}
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.800",
                            "&:hover": { bgcolor: "grey.200" },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {catchRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          No data
                        </TableCell>
                      </TableRow>
                    ) : (
                      catchRows.map((r, idx) => (
                        <TableRow key={idx} sx={{ height: "0.64cm" }}>
                          <TableCell>{r.totalweight ?? ""}</TableCell>
                          <TableCell>{r.noofbirds ?? ""}</TableCell>
                          <TableCell>{formatDisplayDateTime(r.date)}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "inline-flex",
                                gap: 1,
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              <EditIcon
                                sx={{ fontSize: 22, color: "text.secondary", cursor: "pointer" }}
                                onClick={() => openCatchEdit(r)}
                              />
                              <DeleteIcon
                                sx={{ fontSize: 22, color: "text.secondary", cursor: "pointer" }}
                                onClick={() => {
                                  setCatchDeleteTarget(r);
                                  setCatchDeleteOpen(true);
                                }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {weightTab === "cycle" && (
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <TableContainer
                component={Box}
                sx={{ boxShadow: "none", mx: "auto", width: "fit-content" }}
              >
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "21.77cm",
                    minWidth: "21.77cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontSize: "0.9rem",
                      lineHeight: 1.2,
                      p: 0.5,
                      textAlign: "center",
                      color: "text.primary",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "2.88cm" }} />
                    <col style={{ width: "2.88cm" }} />
                    <col style={{ width: "2.88cm" }} />
                    <col style={{ width: "2.88cm" }} />
                    <col style={{ width: "2.88cm" }} />
                    <col style={{ width: "2.88cm" }} />
                    <col style={{ width: "2.64cm" }} />
                    <col style={{ width: "2.64cm" }} />
                    <col style={{ width: "3.6cm" }} />
                    <col style={{ width: "1.8cm" }} />
                  </colgroup>
                  <TableBody>
                    <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Weight Back
                        <br />
                        (L)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Weight Back
                        <br />
                        (R)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Weight Centre
                        <br />
                        (L)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Weight Centre
                        <br />
                        (R)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Weight Front
                        <br />
                        (L)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Weight Front
                        <br />
                        (R)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Sample Size</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Avg (kg)</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <IconButton
                          size="small"
                          aria-label="add"
                          onClick={openCycleAdd}
                          disabled={cycleId == null}
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.800",
                            "&:hover": { bgcolor: "grey.200" },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {sortedCycleRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          No data
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedCycleRows.map((r, idx) => (
                        <TableRow key={idx} sx={{ height: "0.64cm" }}>
                          <TableCell>{r.weightBack ?? ""}</TableCell>
                          <TableCell>{r.weightBackR ?? ""}</TableCell>
                          <TableCell>{r.weightCenter ?? ""}</TableCell>
                          <TableCell>{r.weightCenterR ?? ""}</TableCell>
                          <TableCell>{r.weightFront ?? ""}</TableCell>
                          <TableCell>{r.weightFrontR ?? ""}</TableCell>
                          <TableCell>{r.sampleSize ?? ""}</TableCell>
                          <TableCell>{r.weightAverage ?? ""}</TableCell>
                          <TableCell>{formatDisplayDateTime(r.dateTime)}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "inline-flex",
                                gap: 1,
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              <EditIcon
                                sx={{ fontSize: 22, color: "text.secondary", cursor: "pointer" }}
                                onClick={() => openCycleEdit(r)}
                              />
                              <DeleteIcon
                                sx={{ fontSize: 22, color: "text.secondary", cursor: "pointer" }}
                                onClick={() => openCycleDelete(r)}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete During-cycle weight confirmation dialog */}
      <Dialog
        open={cycleDeleteOpen}
        onClose={() => setCycleDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1.5 }}>Delete Cycle Weight</DialogTitle>
        <DialogContent dividers>
          {" "}
          Are you sure you want to delete this cycle weight record?{" "}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCycleDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" sx={{ color: "#fff" }} onClick={performCycleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* During-cycle Add/Edit Dialog */}
      <Dialog open={cycleEditOpen} onClose={closeCycleEdit} fullWidth maxWidth="sm">
        <DialogTitle>
          {cycleForm.id ? "Edit Cycle Weights" : "Add During Cycle Weights"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Weight Back (L)"
              type="number"
              value={cycleForm.weightBack}
              onChange={(e) => setCycleForm((f) => ({ ...f, weightBack: e.target.value }))}
              inputProps={{ min: 0, step: "any" }}
            />
            <TextField
              label="Weight Back (R)"
              type="number"
              value={cycleForm.weightBackR}
              onChange={(e) => setCycleForm((f) => ({ ...f, weightBackR: e.target.value }))}
              inputProps={{ min: 0, step: "any" }}
            />
            <TextField
              label="Weight Centre (L)"
              type="number"
              value={cycleForm.weightCenter}
              onChange={(e) => setCycleForm((f) => ({ ...f, weightCenter: e.target.value }))}
              inputProps={{ min: 0, step: "any" }}
            />
            <TextField
              label="Weight Centre (R)"
              type="number"
              value={cycleForm.weightCenterR}
              onChange={(e) => setCycleForm((f) => ({ ...f, weightCenterR: e.target.value }))}
              inputProps={{ min: 0, step: "any" }}
            />
            <TextField
              label="Weight Front (L)"
              type="number"
              value={cycleForm.weightFront}
              onChange={(e) => setCycleForm((f) => ({ ...f, weightFront: e.target.value }))}
              inputProps={{ min: 0, step: "any" }}
            />
            <TextField
              label="Weight Front (R)"
              type="number"
              value={cycleForm.weightFrontR}
              onChange={(e) => setCycleForm((f) => ({ ...f, weightFrontR: e.target.value }))}
              inputProps={{ min: 0, step: "any" }}
            />
            <TextField
              label="Sample Size"
              type="number"
              value={cycleForm.sampleSize}
              onChange={(e) => setCycleForm((f) => ({ ...f, sampleSize: e.target.value }))}
              inputProps={{ min: 0, step: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCycleEdit}>Cancel</Button>
          <Button variant="contained" onClick={submitCycleForm} sx={{ color: "#fff" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDialog === "consumption"}
        onClose={() => setOpenDialog(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ position: "relative", pr: 6 }}>
          Consumption
          <IconButton
            aria-label="close"
            size="small"
            title="Close"
            onClick={() => setOpenDialog(null)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ position: "relative", pb: 2, px: { xs: 1, sm: 2 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 1.5 }}>
              {[
                { label: "Feed", value: "readings" },
                { label: "Water", value: "water" },
              ].map((option) => (
                <Button
                  key={option.value}
                  variant={consActiveView === option.value ? "contained" : "outlined"}
                  onClick={() => handleConsViewChange(option.value)}
                  sx={{
                    minWidth: 112,
                    color: consActiveView === option.value ? "#fff" : "grey.800",
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Box>
            {consActiveView === "readings" ? (
              <TableContainer
                component={Box}
                sx={{
                  boxShadow: "none",
                  width: "fit-content",
                  maxWidth: "100%",
                  overflowX: "auto",
                }}
              >
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "15cm",
                    minWidth: "15cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontSize: "0.9rem",
                      lineHeight: 1.2,
                      p: 0.5,
                      textAlign: "center",
                      color: "text.primary",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "2.5cm" }} />
                    <col style={{ width: "2.5cm" }} />
                    <col style={{ width: "2.5cm" }} />
                    <col style={{ width: "2.5cm" }} />
                    <col style={{ width: "2.5cm" }} />
                    <col style={{ width: "2.5cm" }} />
                  </colgroup>
                  <TableBody>
                    <TableRow sx={{ height: "0.48cm", bgcolor: "grey.100" }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                        Total Added
                      </TableCell>
                      <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                        Consumption
                      </TableCell>
                      <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                        Balance
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Bin A</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Bin B</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Bin A</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Bin B</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Bin A</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Bin B</TableCell>
                    </TableRow>
                    <TableRow sx={{ height: "0.64cm" }}>
                      <TableCell>{formatIntValue(currentBin1Total)}</TableCell>
                      <TableCell>{formatIntValue(currentBin2Total)}</TableCell>
                      <TableCell>{formatIntValue(currentBin1Consumption)}</TableCell>
                      <TableCell>{formatIntValue(currentBin2Consumption)}</TableCell>
                      <TableCell>{formatIntValue(currentBin1Balance)}</TableCell>
                      <TableCell>{formatIntValue(currentBin2Balance)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </Box>
          {consActiveView === "readings" ? (
            <SetupCycle
              facilityId={facilityId}
              cycleId={cycleId}
              farmId={farmId}
              embeddedFeed
              onFeedSaved={refreshAfterFeedReadingSave}
            />
          ) : null}
          {consActiveView === "water"
            ? (() => {
                const pageRows = consPageRows;
                return (
                  <TableContainer
                    component={Box}
                    sx={{
                      boxShadow: "none",
                      width: "fit-content",
                      maxWidth: "100%",
                      mx: "auto",
                      overflowX: "auto",
                      mb: 2,
                    }}
                  >
                    <Table
                      size="small"
                      sx={{
                        tableLayout: "fixed",
                        width: "19.125cm",
                        minWidth: "19.125cm",
                        borderCollapse: "collapse",
                        "& th, & td": {
                          border: "1px solid",
                          borderColor: "grey.300",
                          fontSize: "0.9rem",
                          lineHeight: 1.2,
                          p: 0.5,
                          textAlign: "center",
                          color: "text.primary",
                        },
                      }}
                    >
                      <colgroup>
                        <col style={{ width: "3cm" }} />
                        <col style={{ width: "5.5cm" }} />
                        <col style={{ width: "5.5cm" }} />
                        <col style={{ width: "3.875cm" }} />
                        <col style={{ width: "1.25cm" }} />
                      </colgroup>
                      <TableBody>
                        <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                          <TableCell sx={{ fontWeight: 700 }}>Source (%)</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={consOrderBy === "waterReading"}
                              direction={consOrderBy === "waterReading" ? consOrder : "asc"}
                              onClick={handleConsRequestSort("waterReading")}
                            >
                              Reading
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={consOrderBy === "waterL"}
                              direction={consOrderBy === "waterL" ? consOrder : "asc"}
                              onClick={handleConsRequestSort("waterL")}
                            >
                              Consumption (L)
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={consOrderBy === "date"}
                              direction={consOrderBy === "date" ? consOrder : "asc"}
                              onClick={handleConsRequestSort("date")}
                            >
                              Date
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <IconButton
                              size="small"
                              aria-label="add"
                              onClick={openConsAdd}
                              sx={{
                                width: 28,
                                height: 28,
                                bgcolor: "grey.100",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "grey.800",
                                "&:hover": { bgcolor: "grey.200" },
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        {pageRows.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              align="center"
                              sx={{ py: 2, color: "text.secondary" }}
                            >
                              No data
                            </TableCell>
                          </TableRow>
                        ) : (
                          pageRows.map((r, idx) => (
                            <TableRow key={`water-${idx}`} sx={{ height: "0.64cm" }}>
                              <TableCell>{r.waterSource ?? ""}</TableCell>
                              <TableCell>{r.waterReading ?? ""}</TableCell>
                              <TableCell>{r.waterL ?? ""}</TableCell>
                              <TableCell>{formatDisplayDateTime(r.dateTime)}</TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    gap: 1,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                  }}
                                >
                                  <EditIcon
                                    sx={{
                                      fontSize: 22,
                                      color: "text.secondary",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => openConsEdit(r, "water")}
                                  />
                                  <DeleteIcon
                                    sx={{
                                      fontSize: 22,
                                      color: "text.secondary",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => openConsDelete(r)}
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()
            : null}
          {consActiveView === "feed"
            ? (() => {
                const pageRows = consPageRows;
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      gap: 1.5,
                      maxWidth: "100%",
                      mb: 2,
                    }}
                  >
                    <TableContainer
                      component={Box}
                      sx={{
                        boxShadow: "none",
                        width: "fit-content",
                        maxWidth: "100%",
                        overflowX: "auto",
                      }}
                    >
                      <Table
                        size="small"
                        sx={{
                          tableLayout: "fixed",
                          width: "17.5cm",
                          minWidth: "17.5cm",
                          borderCollapse: "collapse",
                          "& th, & td": {
                            border: "1px solid",
                            borderColor: "grey.300",
                            fontSize: "0.9rem",
                            lineHeight: 1.2,
                            p: 0.5,
                            textAlign: "center",
                            color: "text.primary",
                          },
                        }}
                      >
                        <colgroup>
                          <col style={{ width: "3cm" }} />
                          <col style={{ width: "4cm" }} />
                          <col style={{ width: "3.75cm" }} />
                          <col style={{ width: "2cm" }} />
                          <col style={{ width: "3.5cm" }} />
                          <col style={{ width: "1.25cm" }} />
                        </colgroup>
                        <TableBody>
                          <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={consOrderBy === "stage"}
                                direction={consOrderBy === "stage" ? consOrder : "asc"}
                                onClick={handleConsRequestSort("stage")}
                              >
                                Stage
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={consOrderBy === "feedName"}
                                direction={consOrderBy === "feedName" ? consOrder : "asc"}
                                onClick={handleConsRequestSort("feedName")}
                              >
                                Feed name
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={consOrderBy === "feedKg"}
                                direction={consOrderBy === "feedKg" ? consOrder : "asc"}
                                onClick={handleConsRequestSort("feedKg")}
                              >
                                Feed (kg)
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Bin</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={consOrderBy === "date"}
                                direction={consOrderBy === "date" ? consOrder : "asc"}
                                onClick={handleConsRequestSort("date")}
                              >
                                Date
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <IconButton
                                size="small"
                                aria-label="add"
                                onClick={openConsAdd}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  bgcolor: "grey.100",
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: "grey.800",
                                  "&:hover": { bgcolor: "grey.200" },
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          {pageRows.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={6}
                                align="center"
                                sx={{ py: 2, color: "text.secondary" }}
                              >
                                No data
                              </TableCell>
                            </TableRow>
                          ) : (
                            pageRows.map((r, idx) => {
                              const feedMetadata = getConsumptionFeedMetadata(r);
                              return (
                                <TableRow key={`feed-${idx}`} sx={{ height: "0.64cm" }}>
                                  <TableCell>{feedMetadata.stage || ""}</TableCell>
                                  <TableCell>{feedMetadata.feedName || ""}</TableCell>
                                  <TableCell>{r.feedKg ?? ""}</TableCell>
                                  <TableCell>
                                    {r.bin
                                      ? normalizeBinSelection(r.bin) === "bin2"
                                        ? "Bin B"
                                        : normalizeBinSelection(r.bin) === "feedpaper"
                                        ? "Feedpaper"
                                        : "Bin A"
                                      : ""}
                                  </TableCell>
                                  <TableCell>
                                    {formatDisplayDateTimeUtcPlusTwo(r.dateTime)}
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        gap: 1,
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "100%",
                                      }}
                                    >
                                      <EditIcon
                                        sx={{
                                          fontSize: 22,
                                          color: "text.secondary",
                                          cursor: "pointer",
                                        }}
                                        onClick={() => openConsEdit(r, "feed")}
                                      />
                                      <DeleteIcon
                                        sx={{
                                          fontSize: 22,
                                          color: "text.secondary",
                                          cursor: "pointer",
                                        }}
                                        onClick={() => openConsDelete(r)}
                                      />
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              })()
            : null}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            {Array.from({ length: consTotalPages }, (_, i) => i + 1).map((n) =>
              n === consPage ? (
                <Box
                  key={n}
                  sx={{
                    px: 1,
                    py: 0.25,
                    bgcolor: "grey.300",
                    color: "text.primary",
                    borderRadius: 1,
                    minWidth: 24,
                    textAlign: "center",
                    fontSize: "0.9rem",
                  }}
                >
                  {n}
                </Box>
              ) : (
                <Button
                  key={n}
                  size="small"
                  variant="text"
                  onClick={() => setConsPage(n)}
                  sx={{ minWidth: 0, color: "text.primary", textDecoration: "underline" }}
                >
                  {n}
                </Button>
              )
            )}
            <Button
              size="small"
              variant="text"
              onClick={() => setConsPage((p) => Math.min(consTotalPages, p + 1))}
              disabled={consPage >= consTotalPages}
              sx={{ ml: 1 }}
            >
              Next
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Consumption Add/Edit Dialog */}
      <Dialog open={consEditOpen} onClose={closeConsEdit} fullWidth maxWidth="sm">
        <DialogTitle>
          {consFormMode === "water"
            ? consForm.id
              ? "Edit Water"
              : "Add Water"
            : consForm.id
            ? "Edit Feed Consumption"
            : "Add Feed Consumption"}
        </DialogTitle>
        <DialogContent dividers>
          {consFormMode === "feed" ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                select
                label="Stage"
                value={consForm.stage}
                onChange={(e) => setConsForm((f) => ({ ...f, stage: e.target.value }))}
                fullWidth
                size="small"
                InputLabelProps={{ sx: { fontSize: "1rem" } }}
                SelectProps={{ displayEmpty: true }}
                sx={{
                  "& .MuiOutlinedInput-root": { minHeight: "1cm" },
                  "& .MuiSelect-select": {
                    minHeight: "1cm",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "1rem",
                  },
                }}
              >
                {feedStageOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Feed name"
                value={consForm.feedName}
                onChange={(e) => setConsForm((f) => ({ ...f, feedName: e.target.value }))}
                fullWidth
                size="small"
                InputLabelProps={{ sx: { fontSize: "1rem" } }}
                SelectProps={{ displayEmpty: true }}
                sx={{
                  "& .MuiOutlinedInput-root": { minHeight: "1cm" },
                  "& .MuiSelect-select": {
                    minHeight: "1cm",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "1rem",
                  },
                }}
              >
                {feedNameOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Feed (kg)"
                type="number"
                value={consForm.feedKg}
                onChange={(e) => setConsForm((f) => ({ ...f, feedKg: e.target.value }))}
                inputProps={{ min: 0, step: "any" }}
                fullWidth
              />
              <TextField
                select
                label="Bin"
                value={consForm.bin}
                onChange={(e) => setConsForm((f) => ({ ...f, bin: e.target.value }))}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": { minHeight: "45px" },
                  "& .MuiSelect-select": {
                    minHeight: "45px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                <MenuItem value="bin1">Bin A</MenuItem>
                <MenuItem value="bin2">Bin B</MenuItem>
              </TextField>
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}>
              <TextField
                label="Reading"
                type="number"
                value={consForm.waterReading}
                onChange={(e) => setConsForm((f) => ({ ...f, waterReading: e.target.value }))}
                inputProps={{ min: 0, step: "any" }}
                fullWidth
              />
              <TextField
                label="Source (%)"
                type="number"
                value={consForm.waterSource}
                onChange={(e) => setConsForm((f) => ({ ...f, waterSource: e.target.value }))}
                inputProps={{ min: 0, max: 100, step: "any" }}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConsEdit}>Cancel</Button>
          <Button variant="contained" onClick={submitConsForm} sx={{ color: "#fff" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Consumption Delete Dialog */}
      <Dialog
        open={consDeleteOpen}
        onClose={() => setConsDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1.5 }}>Delete Consumption</DialogTitle>
        <DialogContent dividers>
          Are you sure you want to delete this consumption record?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" sx={{ color: "#fff" }} onClick={performConsDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={notificationEditOpen}
        onClose={notificationSaving ? undefined : closeNotificationEdit}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Notification</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.3,
                bgcolor: "grey.100",
                borderRadius: 1,
                px: 1.25,
                py: 1,
              }}
            >
              {notificationForm.kind === "performance" ? (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "text.primary", lineHeight: 1.1 }}
                  >
                    KPI
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", lineHeight: 1.1, fontWeight: 400 }}
                  >
                    {notificationForm.kpi || "Unknown KPI"}
                  </Typography>
                </>
              ) : notificationForm.kind === "activities" ? (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "text.primary", lineHeight: 1.1 }}
                  >
                    Activity
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", lineHeight: 1.1, fontWeight: 400 }}
                  >
                    {notificationForm.activity || "Unknown activity"}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "text.primary", lineHeight: 1.1 }}
                  >
                    {notificationForm.deviceId || "Unknown device"}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", lineHeight: 1.1, fontWeight: 400 }}
                  >
                    {notificationForm.parameter || "-"}
                  </Typography>
                </>
              )}
            </Box>

            {notificationForm.kind === "performance" && (
              <>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Deviation %"
                    type="number"
                    value={notificationForm.deviationP}
                    onChange={handleNotificationFieldChange("deviationP")}
                    inputProps={{ step: 1 }}
                    disabled={notificationSaving}
                    sx={{ width: { xs: "100%", sm: "50%" } }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!notificationForm.enabled}
                        onChange={handleNotificationBooleanChange("enabled")}
                        disabled={notificationSaving}
                      />
                    }
                    label="Enabled"
                  />
                </Box>
              </>
            )}

            {notificationForm.kind === "environmental" && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Strict Upper"
                    type="number"
                    value={notificationForm.strictUpper}
                    onChange={handleNotificationFieldChange("strictUpper")}
                    inputProps={{ step: "any" }}
                    disabled={notificationSaving}
                  />
                  <TextField
                    label="Strict Lower"
                    type="number"
                    value={notificationForm.strictLower}
                    onChange={handleNotificationFieldChange("strictLower")}
                    inputProps={{ step: "any" }}
                    disabled={notificationSaving}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    columnGap: 1.5,
                    rowGap: 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!notificationForm.strictEnabled}
                        onChange={handleNotificationBooleanChange("strictEnabled")}
                        disabled={notificationSaving}
                      />
                    }
                    label="Strict Enabled"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!notificationForm.fuzzyEnabled}
                        onChange={handleNotificationBooleanChange("fuzzyEnabled")}
                        disabled={notificationSaving}
                      />
                    }
                    label="Fuzzy Enabled"
                  />
                </Box>
              </>
            )}

            {notificationForm.kind === "activities" && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    columnGap: 1.5,
                    rowGap: 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!notificationForm.completed}
                        onChange={handleNotificationBooleanChange("completed")}
                        disabled={notificationSaving}
                      />
                    }
                    label="Completed"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!notificationForm.conditionsNotMet}
                        onChange={handleNotificationBooleanChange("conditionsNotMet")}
                        disabled={notificationSaving}
                      />
                    }
                    label="Completed: Conditions not met"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!notificationForm.notCompleted}
                        onChange={handleNotificationBooleanChange("notCompleted")}
                        disabled={notificationSaving}
                      />
                    }
                    label="Not completed"
                  />
                </Box>
              </>
            )}

            <TextField
              label={notificationForm.kind === "environmental" ? "Freq (min)" : "Time (UTC+2)"}
              type={notificationForm.kind === "environmental" ? "number" : "time"}
              value={notificationForm.delay}
              onChange={handleNotificationFieldChange("delay")}
              inputProps={
                notificationForm.kind === "environmental" ? { step: 1, min: 1 } : { step: 60 }
              }
              disabled={notificationSaving}
              helperText={
                notificationForm.kind === "environmental"
                  ? "Environmental Logic App evaluation interval"
                  : "Daily trigger time in UTC+2"
              }
              sx={{ width: { xs: "100%", sm: "24%" } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNotificationEdit} disabled={notificationSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ color: "#fff" }}
            onClick={submitNotificationForm}
            disabled={notificationSaving}
          >
            {notificationSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDialog === "notifications"}
        onClose={() => setOpenDialog(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { minHeight: "calc(70vh + 1cm)" } }}
      >
        <DialogTitle sx={{ position: "relative", pr: 6 }}>
          Notifications
          <IconButton
            aria-label="close"
            size="small"
            title="Close"
            onClick={() => setOpenDialog(null)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ position: "relative", pb: 2, px: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 0.5,
                bgcolor: "grey.200",
                borderRadius: 1,
                width: { xs: "100%", md: "60%" },
              }}
            >
              {notificationTabs.map((t) => (
                <Button
                  key={t.key}
                  onClick={() => setNotificationTab(t.key)}
                  variant={notificationTab === t.key ? "outlined" : "text"}
                  sx={{
                    color: "text.primary",
                    bgcolor: notificationTab === t.key ? "white" : "grey.200",
                    borderColor: notificationTab === t.key ? "info.main" : "transparent",
                    textTransform: "none",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                  }}
                >
                  {t.label}
                </Button>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            {notificationLoading ? (
              <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                Loading notifications...
              </Box>
            ) : notificationError ? (
              <Box sx={{ py: 6, textAlign: "center", color: "error.main" }}>
                Unable to load notifications.
              </Box>
            ) : (
              renderNotificationsTable(
                notificationRows[notificationTab] ?? [],
                notificationTabs.find((t) => t.key === notificationTab)
              )
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

CaptureRecords.propTypes = {
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cycleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  farmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CaptureRecords;
